import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { generateChatMessage, getAvailableModels } from '../services/openRouterService';
import SearchableSelect from './SearchableSelect';
import './ScriptEditor.css';

// Import pptxgenjs for PowerPoint generation
let PptxGenJS;
try {
  PptxGenJS = require('pptxgenjs');
} catch (e) {
  console.warn('pptxgenjs not available:', e);
}

// Minimal Markdown → HTML converter for inserting AI content in editable form
// Handles common patterns: headings, bold/italic, inline/code blocks, links, lists, quotes
const markdownToHtml = (markdown) => {
  if (!markdown) return '';

  const escapeHtml = (str) => String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Handle fenced code blocks first
  let html = markdown.replace(/```([\s\S]*?)```/g, (m, code) => {
    return `<pre><code>${escapeHtml(code.trim())}</code></pre>`;
  });

  // Headings
  html = html.replace(/^######\s?(.*)$/gm, '<h6>$1</h6>')
             .replace(/^#####\s?(.*)$/gm, '<h5>$1</h5>')
             .replace(/^####\s?(.*)$/gm, '<h4>$1</h4>')
             .replace(/^###\s?(.*)$/gm, '<h3>$1</h3>')
             .replace(/^##\s?(.*)$/gm, '<h2>$1</h2>')
             .replace(/^#\s?(.*)$/gm, '<h1>$1</h1>');

  // Blockquotes
  html = html.replace(/^>\s?(.*)$/gm, '<blockquote>$1</blockquote>');

  // Horizontal rules
  html = html.replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '<hr>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold and italic (process bold first)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
             .replace(/__(.+?)__/g, '<strong>$1</strong>')
             .replace(/\*(.+?)\*/g, '<em>$1</em>')
             .replace(/_(.+?)_/g, '<em>$1</em>');

  // Images ![alt](url) - must be processed before links
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
    // Escape the alt text and URL for HTML
    const escapedAlt = escapeHtml(alt || 'Image');
    const escapedUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return `<img src="${escapedUrl}" alt="${escapedAlt}" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
  });

  // Also handle plain image URLs (http/https URLs ending in image extensions)
  html = html.replace(/(https?:\/\/[^\s<>"]+\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s<>"]*)?)/gi, (match, url) => {
    // Only convert if not already inside an img tag or link
    if (!match.includes('<img') && !match.includes('href=')) {
      const escapedUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      return `<img src="${escapedUrl}" alt="Image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
    }
    return match;
  });

  // Handle base64 image data URLs
  html = html.replace(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+)/g, (match, dataUrl) => {
    const escapedUrl = dataUrl.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return `<img src="${escapedUrl}" alt="Generated Image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
  });

  // Links [text](url) - process after images to avoid conflicts
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Lists: build line-by-line to group consecutive items
  const lines = html.split(/\r?\n/);
  const out = [];
  let inUl = false;
  let inOl = false;
  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*[-*+]\s+/.test(line)) {
      if (!inUl) { closeLists(); out.push('<ul>'); inUl = true; }
      out.push(`<li>${line.replace(/^\s*[-*+]\s+/, '')}</li>`);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      if (!inOl) { closeLists(); out.push('<ol>'); inOl = true; }
      out.push(`<li>${line.replace(/^\s*\d+\.\s+/, '')}</li>`);
      continue;
    }
    // Non-list line
    closeLists();
    out.push(line);
  }
  closeLists();
  html = out.join('\n');

  // Convert remaining blank-line separated paragraphs
  html = html
    .split(/\n{2,}/)
    .map(block => {
      if (/^\s*<(h\d|ul|ol|pre|blockquote|hr)/i.test(block.trim())) return block; // already block element
      return `<p>${block.replace(/\n/g, '<br>')}</p>`;
    })
    .join('\n');

  return html;
};

// PDF Export functionality
const exportToPDF = async (content, filename = 'document') => {
  try {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    
    // Create HTML content with proper styling
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 40px;
              color: #333;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #2c3e50;
              margin-top: 20px;
              margin-bottom: 10px;
            }
            h1 { font-size: 24px; }
            h2 { font-size: 20px; }
            h3 { font-size: 18px; }
            h4 { font-size: 16px; }
            h5 { font-size: 14px; }
            h6 { font-size: 12px; }
            p { margin-bottom: 10px; }
            blockquote {
              border-left: 4px solid #3498db;
              padding-left: 15px;
              margin: 15px 0;
              font-style: italic;
              color: #555;
            }
            code {
              background-color: #f8f9fa;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
            }
            pre {
              background-color: #f8f9fa;
              padding: 15px;
              border-radius: 5px;
              overflow-x: auto;
              margin: 15px 0;
            }
            ul, ol {
              margin: 10px 0;
              padding-left: 20px;
            }
            li { margin-bottom: 5px; }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 15px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            img {
              max-width: 100%;
              height: auto;
              margin: 10px 0;
            }
            hr {
              border: none;
              border-top: 1px solid #ddd;
              margin: 20px 0;
            }
            @media print {
              body { margin: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="margin-bottom: 20px; padding: 10px; background-color: #f8f9fa; border-radius: 5px;">
            <strong>Generated by AI Content Assistant</strong><br>
            <small>Date: ${new Date().toLocaleDateString()}</small>
          </div>
          ${content}
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
    
    return { success: true, message: 'PDF export initiated' };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, error: error.message };
  }
};

// WordPress integration functionality
const generateWordPressContent = (content, options = {}) => {
  const {
    title = 'Generated Content',
    author = 'AI Content Assistant',
    category = 'General',
    tags = [],
    featuredImage = '',
    seoDescription = '',
    publishStatus = 'draft'
  } = options;
  
  // Convert content to WordPress-compatible format
  let wpContent = content;
  
  // Add WordPress-specific formatting
  if (featuredImage) {
    wpContent = `<!-- wp:image {"url":"${featuredImage}","id":1,"sizeSlug":"large"} -->
<figure class="wp-block-image size-large"><img src="${featuredImage}" alt="${title}"/></figure>
<!-- /wp:image -->

${wpContent}`;
  }
  
  // Create WordPress export object
  const wpExport = {
    title: title,
    content: wpContent,
    excerpt: seoDescription || content.substring(0, 160) + '...',
    author: author,
    categories: [category],
    tags: tags,
    status: publishStatus,
    date: new Date().toISOString(),
    featuredImage: featuredImage,
    seo: {
      title: title,
      description: seoDescription,
      keywords: tags.join(', ')
    }
  };
  
  return wpExport;
};

const ScriptEditor = forwardRef(({ onClose, showAIChatOnMount = false }, ref) => {
  const [content, setContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [autosave, setAutosave] = useState(true);

  // Expose insertGeneratedContent method to parent components
  useImperativeHandle(ref, () => ({
    insertGeneratedContent: (generatedContent) => {
      // Convert Markdown/plain text to HTML for rich editing
      const htmlContent = markdownToHtml(generatedContent);
      
      // Append to existing content instead of replacing
      setContent(prevContent => {
        // Add separator if there's existing content
        const separator = prevContent ? '<br><br>---<br><br>' : '';
        return prevContent + separator + htmlContent;
      });
      
      console.log('Content appended to Content Editor');
    }
  }));

  const [showSourceCode, setShowSourceCode] = useState(false);
  const [contentMode, setContentMode] = useState('html'); // 'html' or 'text'
  const [showVisualAids, setShowVisualAids] = useState(true);
  const [showInvisibleChars, setShowInvisibleChars] = useState(false);
  const [showBlocks, setShowBlocks] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    fontSize: '3',
    fontName: 'Arial'
  });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [isPasting, setIsPasting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isUpdatingContent, setIsUpdatingContent] = useState(false);
  
  // AI Chat Modal State
  const [showChatModal, setShowChatModal] = useState(showAIChatOnMount);
  const [chatHistory, setChatHistory] = useState([]); // Array of conversations
  const [currentConversationId, setCurrentConversationId] = useState(null); // ID of active conversation
  const [chatMessages, setChatMessages] = useState([]); // Messages in current conversation
  const [selectedChatModel, setSelectedChatModel] = useState('deepseek/deepseek-chat-v3.1:free'); // Default to free model
  const [chatInput, setChatInput] = useState('');
  const [isChatGenerating, setIsChatGenerating] = useState(false);
  const [chatApiKey, setChatApiKey] = useState('');
  const [availableChatModels, setAvailableChatModels] = useState([]);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingConversationTitle, setEditingConversationTitle] = useState('');
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  
  // Generate title from first user message
  const generateConversationTitle = (messages) => {
    const firstUserMessage = messages.find(msg => msg.role === 'user');
    if (firstUserMessage) {
      const content = typeof firstUserMessage.content === 'string' 
        ? firstUserMessage.content 
        : String(firstUserMessage.content || '');
      
      // Check if there are attachments that might have useful names
      if (firstUserMessage.attachments && firstUserMessage.attachments.length > 0) {
        const attachmentNames = firstUserMessage.attachments.map(att => att.name).join(', ');
        if (content.trim()) {
          // Combine content and attachment names
          const combined = `${content} [${attachmentNames}]`;
          const text = combined
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1')
            .replace(/`(.*?)`/g, '$1')
            .replace(/\n/g, ' ')
            .trim();
          return text.length > 50 ? text.substring(0, 50) + '...' : text || 'New Chat';
        } else {
          // Use attachment name as title
          return attachmentNames.length > 50 ? attachmentNames.substring(0, 50) + '...' : attachmentNames || 'New Chat';
        }
      }
      
      const text = content
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1')
        .replace(/\n/g, ' ')
        .trim();
      
      // Only return 'New Chat' if text is truly empty
      if (!text || text.length === 0) {
        return 'New Chat';
      }
      
      return text.length > 50 ? text.substring(0, 50) + '...' : text;
    }
    return 'New Chat';
  };
  
  // Load chat history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('fiddyscript_chat_history');
    if (savedHistory) {
      try {
        const history = JSON.parse(savedHistory);
        // Migrate titles and sanitize attachment data for existing conversations
        const migratedHistory = history.map(conv => {
          // Remove large attachment data if present (prevent storage overflow)
          const sanitizedMessages = (conv.messages || []).map(msg => {
            if (msg.attachments && msg.attachments.length > 0) {
              return {
                ...msg,
                attachments: msg.attachments.map(att => ({
                  type: att.type,
                  name: att.name,
                  mimeType: att.mimeType,
                  // Remove data to prevent storage overflow
                  data: undefined
                }))
              };
            }
            return msg;
          });
          // Regenerate title if it's still "New Chat" or missing
          const newTitle = generateConversationTitle(sanitizedMessages);
          return {
            ...conv,
            messages: sanitizedMessages,
            title: (conv.title && conv.title !== 'New Chat') ? conv.title : newTitle,
          };
        });
        setChatHistory(migratedHistory);
        // Save sanitized history back to prevent future errors
        localStorage.setItem('fiddyscript_chat_history', JSON.stringify(migratedHistory));
        // Load most recent conversation if available
        if (migratedHistory.length > 0) {
          const mostRecent = migratedHistory[migratedHistory.length - 1];
          setCurrentConversationId(mostRecent.id);
          setChatMessages(mostRecent.messages || []);
          setSelectedChatModel(mostRecent.model || 'deepseek/deepseek-chat-v3.1:free');
        }
      } catch (e) {
        console.error('Error loading chat history:', e);
        // If error is related to large data, try to clear corrupted history
        if (e.message && (e.message.includes('exceeded') || e.message.includes('too large'))) {
          console.warn('Chat history contains data that is too large. Clearing history to prevent errors.');
          try {
            localStorage.removeItem('fiddyscript_chat_history');
            setChatHistory([]);
            setChatMessages([]);
            setCurrentConversationId(null);
          } catch (clearError) {
            console.error('Error clearing corrupted history:', clearError);
          }
        }
      }
    }
    
    // Load minimized state
    const savedMinimized = localStorage.getItem('fiddyscript_chat_minimized');
    if (savedMinimized === 'true') {
      setIsChatMinimized(true);
    }
    
    // Load API key and models
    const apiKey = localStorage.getItem('openrouter_api_key');
    if (apiKey) {
      setChatApiKey(apiKey);
    }
    
    // Load models asynchronously (may come from cache or API)
    const loadModels = async () => {
      try {
        const models = await getAvailableModels(false, apiKey);
        setAvailableChatModels(models);
        
        // Load default model from settings if no conversation is loaded
        if (!currentConversationId || chatMessages.length === 0) {
          const savedDefaultModel = localStorage.getItem('default_ai_model');
          if (savedDefaultModel && models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
            setSelectedChatModel(savedDefaultModel);
            return; // Use default model, don't override
          }
        }
        
        // If selected model is unavailable or paid, switch to first available free model
        const selectedModelObj = models.find(m => m.id === selectedChatModel);
        if (!selectedModelObj || selectedModelObj.isAvailable === false || !selectedModelObj.isFree) {
          const firstFreeAvailable = models.find(m => m.isAvailable !== false && m.isFree === true);
          if (firstFreeAvailable) {
            setSelectedChatModel(firstFreeAvailable.id);
          } else {
            // Fallback to first available model if no free models found
            const firstAvailable = models.find(m => m.isAvailable !== false);
            if (firstAvailable) {
              setSelectedChatModel(firstAvailable.id);
            }
          }
        }
      } catch (error) {
        console.error('Error loading chat models:', error);
        // Fallback to hardcoded models if async fails
        try {
          const { AI_MODELS } = await import('../services/openRouterService');
          setAvailableChatModels(AI_MODELS);
        } catch (e) {
          console.error('Failed to load fallback models:', e);
          setAvailableChatModels([]);
        }
      }
    };
    
    loadModels();
  }, []);
  
  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem('fiddyscript_chat_history', JSON.stringify(chatHistory));
    }
  }, [chatHistory]);
  
  // Save current conversation to history when messages change
  useEffect(() => {
    if (currentConversationId && chatMessages.length > 0) {
      setChatHistory(prev => {
        // Remove large attachment data to prevent storage overflow
        // Keep only metadata (name, type, mimeType) but remove base64 data
        const sanitizedMessages = chatMessages.map(msg => {
          if (msg.attachments && msg.attachments.length > 0) {
            return {
              ...msg,
              attachments: msg.attachments.map(att => ({
                type: att.type,
                name: att.name,
                mimeType: att.mimeType,
                // Remove data to prevent storage overflow - images won't be available in history
                // but conversation will load without errors
                data: undefined
              }))
            };
          }
          return msg;
        });
        
        const generatedTitle = generateConversationTitle(chatMessages);
        const updated = prev.map(conv => 
          conv.id === currentConversationId 
            ? { 
                ...conv, 
                messages: sanitizedMessages, 
                model: selectedChatModel,
                // Always regenerate title if it's still "New Chat" or if we have messages now
                title: (conv.title && conv.title !== 'New Chat') ? conv.title : generatedTitle
              }
            : conv
        );
        // If conversation doesn't exist, add it
        if (!updated.find(c => c.id === currentConversationId)) {
          updated.push({
            id: currentConversationId,
            title: generateConversationTitle(chatMessages),
            timestamp: new Date(),
            model: selectedChatModel,
            messages: sanitizedMessages
          });
        }
        return updated;
      });
    }
  }, [chatMessages, currentConversationId, selectedChatModel]);
  
  // Track if content has been loaded from localStorage to prevent overwriting during initial load
  const contentLoadedRef = useRef(false);
  
  // Content persistence with localStorage
  useEffect(() => {
    // Load saved content on component mount
    const savedContent = localStorage.getItem('scriptEditorContent');
    console.log('Loading saved content:', savedContent ? savedContent.length : 0, 'characters');
    if (savedContent) {
      contentLoadedRef.current = true; // Mark that we're loading saved content
      setContent(savedContent);
      // Reset flag after a short delay to allow state to update
      setTimeout(() => {
        contentLoadedRef.current = false;
      }, 100);
    } else {
      // No saved content, mark as loaded so empty state doesn't overwrite future saves
      contentLoadedRef.current = true;
      setTimeout(() => {
        contentLoadedRef.current = false;
      }, 100);
    }
    
    // Listen for content insertion events (global listener for WorkPlan, etc.)
    const handleContentInsertion = (event) => {
      if (event.detail && event.detail.content) {
        const htmlContent = event.detail.content;
        setContent(prevContent => {
          const separator = prevContent ? '<br><br>---<br><br>' : '';
          const newContent = prevContent + separator + htmlContent;
          // Also update localStorage
          localStorage.setItem('scriptEditorContent', newContent);
          return newContent;
        });
        console.log('Content inserted via global event listener');
      }
    };
    
    window.addEventListener('insertContentToEditor', handleContentInsertion);
    return () => window.removeEventListener('insertContentToEditor', handleContentInsertion);
  }, []);

  // Auto-scroll to bottom of chat messages
  useEffect(() => {
    if (showChatModal && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showChatModal]);

  // Auto-focus chat input when modal opens
  useEffect(() => {
    if (showChatModal && chatInputRef.current) {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [showChatModal]);

  // Handle Escape key to minimize chat modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showChatModal) {
        if (isChatMinimized) {
          setIsChatMinimized(false);
          localStorage.setItem('fiddyscript_chat_minimized', 'false');
        } else {
          setIsChatMinimized(true);
          localStorage.setItem('fiddyscript_chat_minimized', 'true');
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showChatModal, isChatMinimized]);

  // Save minimized state
  useEffect(() => {
    if (showChatModal) {
      localStorage.setItem('fiddyscript_chat_minimized', isChatMinimized.toString());
    }
  }, [isChatMinimized, showChatModal]);

  // Save content to localStorage whenever it changes (but not during initial load)
  useEffect(() => {
    // Don't save if we're still in the initial load phase
    if (contentLoadedRef.current) {
      console.log('Skipping save during initial load');
      return;
    }
    
    // Don't save empty content unless it was explicitly cleared by user
    if (!content.trim() && !contentLoadedRef.current) {
      console.log('Skipping save of empty content');
      return;
    }
    
    console.log('Saving content to localStorage:', content.length, 'characters');
    localStorage.setItem('scriptEditorContent', content);
  }, [content]);

  // Clear localStorage when content is empty
  const clearSavedContent = () => {
    console.log('Clearing saved content from localStorage');
    localStorage.removeItem('scriptEditorContent');
  };
  
  // Phase 4: Export & Integration state
  const [pdfExportOptions, setPdfExportOptions] = useState({
    filename: 'document',
    includeHeader: true,
    includeFooter: true,
    pageSize: 'A4',
    orientation: 'portrait'
  });
  
  const [wordPressOptions, setWordPressOptions] = useState({
    title: '',
    author: 'AI Content Assistant',
    category: 'General',
    tags: [],
    featuredImage: '',
    seoDescription: '',
    publishStatus: 'draft'
  });
  
  const [imageLibrary, setImageLibrary] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedEditorImage, setSelectedEditorImage] = useState(null);
  
  // Image Library and Insert Image state
  const [imageUrl, setImageUrl] = useState('');
  
  // Debug imageUrl state changes
  useEffect(() => {
    console.log('imageUrl state changed to:', imageUrl);
  }, [imageUrl]);

  // Save cursor position when modal opens (using refs to persist across re-renders)
  const savedCursorPositionRef = useRef(null);
  const savedCursorOffsetsRef = useRef(null);
  const [selectedImageSource, setSelectedImageSource] = useState('pexels');
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [selectedSearchImage, setSelectedSearchImage] = useState(null);
  const [selectedImageSize, setSelectedImageSize] = useState('');
  
  // WordPress modal state (using state instead of refs for proper re-rendering)
  const [selectedWordPressSite, setSelectedWordPressSite] = useState(null);
  const [wordPressCategories, setWordPressCategories] = useState([]);
  const [wordPressTags, setWordPressTags] = useState([]);
  const [wordPressPostOptions, setWordPressPostOptions] = useState({
    title: '',
    status: 'draft',
    excerpt: '',
    category: '',
    allowComments: 'yes',
    scheduledDate: '',
    scheduledTime: '',
    tags: [],
    newCategoryName: '',
    newCategoryDescription: '',
    seoDescription: '',
    seoKeywords: ''
  });

  // New state for category/tag creation modals
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [showNewTagModal, setShowNewTagModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '' });
  const [newTagData, setNewTagData] = useState({ name: '', description: '' });

  // Modal form states
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [customEmoji, setCustomEmoji] = useState('');
  const [customChar, setCustomChar] = useState('');

  // Phase 5: Humanizer state
  const [humanizerInputText, setHumanizerInputText] = useState('');
  const [humanizerOutputText, setHumanizerOutputText] = useState('');
  const [imperfectionPercentage, setImperfectionPercentage] = useState(1);
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [showImperfectionTypes, setShowImperfectionTypes] = useState(true);
  const [imperfectionTypes, setImperfectionTypes] = useState({
    misspellings: true,
    homophones: true,
    capitalization: true,
    typos: true,
    punctuation: true,
    spacing: true,
    repeatedWords: true,
    grammar: true,
    wordChoice: true,
    fillerWords: true
  });

  // Phase 5: Humanizer imperfection options
  const imperfectionOptions = [
    { 
      key: 'misspellings', 
      label: 'Add some common misspellings', 
      description: 'ex: "seperate" instead of "separate"',
      examples: ['seperate', 'recieve', 'occassion', 'definately', 'accomodate']
    },
    { 
      key: 'homophones', 
      label: 'Add some common homophone confusions', 
      description: 'ex: "there" instead of "their"',
      examples: ['there', 'their', 'they\'re', 'your', 'you\'re', 'its', 'it\'s', 'to', 'too', 'two']
    },
    { 
      key: 'capitalization', 
      label: 'Add some capitalization issues', 
      description: 'ex: "THis" instead of "This"',
      examples: ['THis', 'THat', 'THey', 'THere', 'THen']
    },
    { 
      key: 'typos', 
      label: 'Add some common typos', 
      description: 'ex: "teh" instead of "the"',
      examples: ['teh', 'thier', 'recieve', 'occassion', 'definately']
    },
    { 
      key: 'punctuation', 
      label: 'Add some punctuation issues', 
      description: 'ex: "Ive" instead of "I\'ve"',
      examples: ['Ive', 'dont', 'cant', 'wont', 'shouldnt', 'couldnt']
    },
    { 
      key: 'spacing', 
      label: 'Add some common spacing issues', 
      description: 'ex: two spaces after a comma or period',
      examples: ['  ', ' ,', ' .', ' !', ' ?']
    },
    { 
      key: 'repeatedWords', 
      label: 'Add some repeated letters and words', 
      description: 'ex: "that that"',
      examples: ['that that', 'the the', 'is is', 'and and', 'but but']
    },
    {
      key: 'grammar',
      label: 'Add some grammatical errors',
      description: 'ex: "He go to school" instead of "He goes to school"',
      examples: ['He go', 'She eat', "I don't have no"]
    },
    {
      key: 'wordChoice',
      label: 'Add some word choice errors',
      description: 'ex: "accept" instead of "except"',
      examples: ['accept', 'affect', 'effect', 'then', 'than']
    },
    {
      key: 'fillerWords',
      label: 'Add some filler words',
      description: 'ex: "um", "like", "you know"',
      examples: ['um', 'like', 'you know', 'actually', 'basically', 'sort of', 'kind of']
    }
  ];

  // Reset modal states when modals open
  useEffect(() => {
    if (showModal) {
      if (modalTitle === 'Post to WordPress') {
        console.log('Resetting WordPress modal state');
        setWordPressPostOptions({
          title: '',
          status: 'draft',
          excerpt: '',
          category: '',
          allowComments: 'yes',
          scheduledDate: '',
          scheduledTime: '',
          tags: [],
          newCategoryName: '',
          newCategoryDescription: '',
          seoDescription: '',
          seoKeywords: ''
        });
        setSelectedWordPressSite(null);
        setWordPressCategories([]);
        setWordPressTags([]);
        setShowNewCategoryModal(false);
        setShowNewTagModal(false);
              } else if (modalTitle === 'Insert Image') {
          console.log('Insert Image modal opened - NOT resetting imageUrl');
          // Only reset search-related state, keep the URL
          setSelectedSearchImage(null);
          setImageSearchResults([]);
        }
    }
  }, [showModal, modalTitle]);

  // Force re-render when WordPress state changes
  useEffect(() => {
    console.log('WordPress state changed:', {
      selectedWordPressSite: selectedWordPressSite?.label,
      wordPressCategories: wordPressCategories.length,
      wordPressPostOptions
    });
  }, [selectedWordPressSite, wordPressCategories, wordPressPostOptions]);
  const [wordPressLogins, setWordPressLogins] = useState([]);

  // Load WordPress logins on component mount
  useEffect(() => {
    const savedWordPressLogins = localStorage.getItem('wordPressLogins');
    if (savedWordPressLogins) {
      try {
        const parsed = JSON.parse(savedWordPressLogins);
        setWordPressLogins(parsed);
      } catch (error) {
        console.error('Error loading WordPress logins:', error);
      }
    }
  }, []);

  // Debug WordPress site selection
  useEffect(() => {
    console.log('WordPress site selection changed:', selectedWordPressSite);
    console.log('Current showModal state:', showModal);
  }, [selectedWordPressSite, showModal]);
  
  const editorRef = useRef(null);
  const contentRef = useRef('');

  // Calculate word count and detect content mode
  useEffect(() => {
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    setWordCount(words);
    
    // Detect if content contains HTML
    const hasHTML = /<[^>]*>/.test(content);
    if (hasHTML && contentMode === 'text') {
      setContentMode('html');
    } else if (!hasHTML && contentMode === 'html' && content.length > 0) {
      setContentMode('text');
    }
  }, [content, contentMode]);

  // Initialize editor when component mounts
  useEffect(() => {
    console.log('=== EDITOR INITIALIZATION ===');
    console.log('Editor ref:', editorRef.current);
    console.log('Show source code:', showSourceCode);
    console.log('Current content length:', content.length);
    
    if (editorRef.current && !showSourceCode) {
      console.log('Setting up contentEditable div');
      editorRef.current.contentEditable = true;
      
      // Set initial content only if editor is completely empty
      if (!editorRef.current.innerHTML.trim() && content.trim()) {
        console.log('Setting initial content in editor');
        editorRef.current.innerHTML = content;
        contentRef.current = content;
      }
      
      // Add click event listener for image selection
      editorRef.current.addEventListener('click', handleImageClick);
      
      editorRef.current.focus();
      console.log('Editor initialized and focused');
    }
    
    // Cleanup event listener on unmount
    return () => {
      if (editorRef.current) {
        editorRef.current.removeEventListener('click', handleImageClick);
      }
    };
  }, [showSourceCode, content]); // Add content dependency back to sync editor with content state

  // Sync editor content when content state changes (for persistence)
  useEffect(() => {
    if (editorRef.current && !showSourceCode && content !== contentRef.current) {
      console.log('Syncing editor content with state:', content.length, 'characters');
      editorRef.current.innerHTML = content;
      contentRef.current = content;
    }
  }, [content, showSourceCode]);

  // Track cursor position and format state
  const handleEditorSelect = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const start = getTextOffset(range.startContainer, range.startOffset);
        const end = getTextOffset(range.endContainer, range.endOffset);
        setCursorPosition({ start, end });
        
        // Update format state
        if (!showSourceCode) {
          setFormatState({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strikethrough: document.queryCommandState('strikeThrough'),
            fontSize: document.queryCommandValue('fontSize') || '3',
            fontName: document.queryCommandValue('fontName') || 'Arial'
          });
        }
      }
    }
  };

  // Helper function to get text offset
  const getTextOffset = (node, offset) => {
    let totalOffset = 0;
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentNode;
    while (currentNode = walker.nextNode()) {
      if (currentNode === node) {
        return totalOffset + offset;
      }
      totalOffset += currentNode.textContent.length;
    }
    return totalOffset;
  };

  // Helper function to get node from text offset
  const getNodeFromOffset = (container, targetOffset) => {
    let currentOffset = 0;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentNode;
    while (currentNode = walker.nextNode()) {
      const nodeLength = currentNode.textContent.length;
      if (currentOffset + nodeLength >= targetOffset) {
        return {
          node: currentNode,
          offset: targetOffset - currentOffset
        };
      }
      currentOffset += nodeLength;
    }
    
    // If we reach here, return the last text node
    const lastNode = container.lastChild;
    if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
      return {
        node: lastNode,
        offset: lastNode.textContent.length
      };
    }
    
    return null;
  };

  // Handle content change
  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  // Handle rich editor content change
  const handleRichEditorChange = (e) => {
    const htmlContent = e.currentTarget.innerHTML;
    contentRef.current = htmlContent;
    console.log('Rich editor content changed, length:', htmlContent.length);
    
    // Save current selection before updating content
    const selection = window.getSelection();
    let savedSelection = null;
    
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      savedSelection = {
        startContainer: range.startContainer,
        startOffset: range.startOffset,
        endContainer: range.endContainer,
        endOffset: range.endOffset
      };
    }
    
    // Update content state
    setContent(htmlContent);
    
    // Restore selection after content update
    if (savedSelection) {
      setTimeout(() => {
        try {
          const newSelection = window.getSelection();
          const newRange = document.createRange();
          newRange.setStart(savedSelection.startContainer, savedSelection.startOffset);
          newRange.setEnd(savedSelection.endContainer, savedSelection.endOffset);
          newSelection.removeAllRanges();
          newSelection.addRange(newRange);
        } catch (error) {
          console.log('Selection restore failed:', error);
        }
      }, 0);
    }
  };

  // Handle image click in editor
  const handleImageClick = (e) => {
    if (e.target.tagName === 'IMG') {
      console.log('Image clicked in editor:', e.target.src);
      console.log('Setting selectedEditorImage...');
      setSelectedEditorImage({
        element: e.target,
        src: e.target.src,
        alt: e.target.alt || '',
        width: e.target.width,
        height: e.target.height
      });
      
      // Add visual feedback for selected image
      const allImages = editorRef.current.querySelectorAll('img');
      allImages.forEach(img => img.style.outline = 'none');
      e.target.style.outline = '2px solid #007bff';
      console.log('Image selected and outlined');
    }
  };

  // Handle paste event to preserve formatting
  const handlePaste = (e) => {
    // Prevent recursive calls
    if (isPasting) {
      console.log('Paste already in progress, skipping...');
      return;
    }
    
    console.log('=== PASTE EVENT TRIGGERED ===');
    e.preventDefault();
    setIsPasting(true);
    
    try {
      // Try to get HTML content first
      let htmlContent = e.clipboardData.getData('text/html');
      let plainText = e.clipboardData.getData('text/plain');
      
      console.log('Paste event - HTML content length:', htmlContent ? htmlContent.length : 0);
      console.log('Paste event - Plain text length:', plainText ? plainText.length : 0);
      console.log('Paste event - HTML content preview:', htmlContent ? htmlContent.substring(0, 100) : 'none');
      console.log('Paste event - Plain text preview:', plainText ? plainText.substring(0, 100) : 'none');
      
      // If no HTML content, use plain text
      if (!htmlContent && plainText) {
        // Convert plain text to basic HTML with line breaks
        htmlContent = plainText
          .replace(/\n/g, '<br>')
          .replace(/\r/g, '')
          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
        console.log('Converted plain text to HTML:', htmlContent.substring(0, 100));
      }
      
      // If we have content to paste
      if (htmlContent) {
        // Use execCommand to insert HTML
        console.log('Attempting to insert HTML content...');
        const success = document.execCommand('insertHTML', false, htmlContent);
        console.log('insertHTML success:', success);
      } else if (plainText) {
        // Fallback to plain text
        console.log('Attempting to insert plain text...');
        const success = document.execCommand('insertText', false, plainText);
        console.log('insertText success:', success);
      } else {
        console.log('No content found in clipboard');
      }
      
      // Update content state
      setTimeout(() => {
        if (editorRef.current) {
          const newContent = editorRef.current.innerHTML;
          console.log('Content after paste:', newContent.length, 'characters');
          console.log('Content preview:', newContent.substring(0, 100));
          setContent(newContent);
        }
        setIsPasting(false);
      }, 100);
      
    } catch (error) {
      console.error('Paste error:', error);
      setIsPasting(false);
      // Fallback: try to get plain text and insert it
      try {
        const plainText = e.clipboardData.getData('text/plain');
        if (plainText) {
          document.execCommand('insertText', false, plainText);
        }
      } catch (fallbackError) {
        console.error('Fallback paste error:', fallbackError);
      }
    }
    console.log('=== PASTE EVENT END ===');
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (showSourceCode) return; // Don't handle shortcuts in source mode
    
    // Set typing state for regular keys
    if (!e.ctrlKey && !e.metaKey && e.key.length === 1) {
      setIsTyping(true);
    }
    
    // Ctrl/Cmd + key combinations
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          formatText('bold');
          break;
        case 'i':
          e.preventDefault();
          formatText('italic');
          break;
        case 'u':
          e.preventDefault();
          formatText('underline');
          break;
        case 'z':
          if (e.shiftKey) {
            e.preventDefault();
            formatText('redo');
          } else {
            e.preventDefault();
            formatText('undo');
          }
          break;
        case 'y':
          e.preventDefault();
          formatText('redo');
          break;
        case 'a':
          e.preventDefault();
          formatText('selectAll');
          break;
        case 'c':
          e.preventDefault();
          formatText('copy');
          break;
        case 'v':
          // Don't prevent default for paste - let the native paste event handle it
          // The handlePaste function will be called automatically
          break;
        case 'x':
          e.preventDefault();
          formatText('cut');
          break;
        case 'k':
          e.preventDefault();
          insertContent('link');
          break;
        case 'l':
          e.preventDefault();
          formatText('justifyLeft');
          break;
        case 'e':
          e.preventDefault();
          formatText('justifyCenter');
          break;
        case 'r':
          e.preventDefault();
          formatText('justifyRight');
          break;
        case 'j':
          e.preventDefault();
          formatText('justifyFull');
          break;
        default:
          break;
      }
    }
    
    // Tab key handling
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        formatText('outdent');
      } else {
        formatText('indent');
      }
    }
  };

  // Handle key up to reset typing state
  const handleKeyUp = (e) => {
    if (showSourceCode) return;
    
    // Reset typing state after a short delay
    setTimeout(() => {
      setIsTyping(false);
      // Update content state with the current editor content
      if (editorRef.current && contentRef.current !== content) {
        setContent(contentRef.current);
      }
    }, 50);
  };

  // Handle editor blur to save content
  const handleEditorBlur = () => {
    if (editorRef.current && contentRef.current !== content) {
      setContent(contentRef.current);
    }
    setIsTyping(false);
  };

  // Handle context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(false);
    };

    if (showContextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showContextMenu]);



  // Menu handlers


  // File operations
  const handleNewDocument = () => {
    setContent('');
  };

  const handleSave = () => {
    // Save functionality would be implemented here
    console.log('Saving document...');
  };

  const handleClearAll = () => {
    console.log('Clear All button clicked');
    setContent('');
    clearSavedContent();
    
    // Also clear the editor's innerHTML if it exists
    if (editorRef.current) {
      if (showSourceCode) {
        editorRef.current.value = '';
        console.log('Cleared source editor value');
      } else {
        editorRef.current.innerHTML = '';
        console.log('Cleared rich editor innerHTML');
      }
    } else {
      console.log('Editor ref is null');
    }
  };

  // Formatting functions
  const formatText = (command, value = null) => {
    if (showSourceCode) {
      // If in source code mode, show a message
      alert('Switch to visual mode to use formatting tools');
      return;
    }
    
    // For undo/redo, ensure the editor has focus first
    if (command === 'undo' || command === 'redo') {
      if (editorRef.current) {
        // Focus the editor before executing undo/redo
        editorRef.current.focus();
        // Ensure focus is actually set by using a small delay
        setTimeout(() => {
          document.execCommand(command, false, value);
          // Update content state after undo/redo
          if (editorRef.current) {
            setContent(editorRef.current.innerHTML);
          }
        }, 10);
        return;
      }
    }
    
    // For other commands, ensure editor has focus too
    if (editorRef.current) {
      editorRef.current.focus();
      setTimeout(() => {
        document.execCommand(command, false, value);
        // Update content state for commands that modify content
        if (editorRef.current && ['cut', 'copy', 'paste', 'delete'].includes(command)) {
          setContent(editorRef.current.innerHTML);
        }
      }, 10);
    } else {
      document.execCommand(command, false, value);
    }
  };

  // Enhanced HTML formatting functions
  const insertHTML = (html) => {
    if (showSourceCode) {
      // Insert HTML directly into textarea
      const textarea = editorRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + html + content.substring(end);
      setContent(newContent);
      
      // Set cursor position after inserted HTML
      setTimeout(() => {
        textarea.setSelectionRange(start + html.length, start + html.length);
        textarea.focus();
      }, 0);
    } else {
      // Insert HTML into rich editor at cursor position
      if (!editorRef.current) return;
      
      // Focus the editor first
        editorRef.current.focus();
      
      // Get current selection
          const selection = window.getSelection();
      let range;
      
      // Try to get the current cursor position
      if (selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
        // Check if the range is within our editor
        if (!editorRef.current.contains(range.commonAncestorContainer)) {
          // If selection is outside editor, create range at the end
          range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        }
              } else {
        // If no selection, try to place at the end of the last text node
        const walker = document.createTreeWalker(
          editorRef.current,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let lastTextNode = null;
        let node;
        while (node = walker.nextNode()) {
          lastTextNode = node;
        }
        
        if (lastTextNode) {
          range = document.createRange();
          range.selectNodeContents(lastTextNode);
          range.collapse(false);
        } else {
          // Fallback to end of editor
          range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        }
      }
      
      // Create a temporary container for the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Insert the HTML at cursor position
            const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
            }
            range.insertNode(fragment);
            
            // Move cursor after inserted content
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
            
      // Update React state
      setContent(editorRef.current.innerHTML);
      
      // Ensure editor maintains focus
        setTimeout(() => {
        editorRef.current.focus();
      }, 10);
    }
  };

  // Enhanced paste function
  const handlePasteFromToolbar = () => {
    // Prevent recursive calls
    if (isPasting) {
      console.log('Paste already in progress, skipping toolbar paste...');
      return;
    }
    
    console.log('=== TOOLBAR PASTE START ===');
    try {
      // Focus the editor first
      if (editorRef.current) {
        editorRef.current.focus();
        console.log('Editor focused for paste');
      } else {
        console.log('ERROR: Editor ref is null');
        return;
      }
      
      // Use execCommand paste directly
      console.log('Attempting execCommand paste...');
      const success = document.execCommand('paste');
      console.log('execCommand paste result:', success);
      
      // Update content state after a short delay
      setTimeout(() => {
        if (editorRef.current) {
          const newContent = editorRef.current.innerHTML;
          console.log('Content after paste attempt:', newContent.length, 'characters');
          setContent(newContent);
        }
      }, 200);
      
    } catch (error) {
      console.error('Paste error:', error);
      // Final fallback - show a message to user
      alert('Paste failed. Please try using Ctrl+V or right-click and select Paste.');
    }
    console.log('=== TOOLBAR PASTE END ===');
  };

  const wrapSelection = (tag, attributes = '') => {
    if (showSourceCode) {
      const textarea = editorRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      const wrappedText = `<${tag}${attributes ? ' ' + attributes : ''}>${selectedText}</${tag}>`;
      const newContent = content.substring(0, start) + wrappedText + content.substring(end);
      setContent(newContent);
      
      // Set cursor position after wrapped text
      setTimeout(() => {
        textarea.setSelectionRange(start + wrappedText.length, start + wrappedText.length);
        textarea.focus();
      }, 0);
    } else {
      // Use execCommand for rich editor
      document.execCommand('formatBlock', false, tag);
    }
  };

  const insertContent = (type) => {
    switch (type) {
      case 'image':
        // Open the Insert Image modal instead of using prompt
        openModal('Insert Image', null);
        break;
      case 'link':
        openModal('Insert Link', null);
        break;
      case 'video':
        openModal('Insert Video', null);
        break;
      case 'table':
        openModal('Insert Table', null);
        break;
      default:
        break;
    }
  };

  const generateTable = (rows, cols) => {
    let table = '<table border="1" style="border-collapse: collapse;">';
    for (let i = 0; i < rows; i++) {
      table += '<tr>';
      for (let j = 0; j < cols; j++) {
        table += '<td style="padding: 8px; border: 1px solid #ccc;">Cell</td>';
      }
      table += '</tr>';
    }
    table += '</table>';
    return table;
  };

  // Modal system
  const openModal = (title, content) => {
    console.log('Opening modal:', title);
    console.log('Modal content type:', typeof content);
    
    // Save cursor position before opening Insert Image modal
    if (title === 'Insert Image' && !showSourceCode && editorRef.current) {
      console.log('Saving cursor position for Insert Image modal');
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        savedCursorPositionRef.current = range.cloneRange();
        
        // Also save text offsets as backup
        const startOffset = getTextOffset(range.startContainer, range.startOffset);
        const endOffset = getTextOffset(range.endContainer, range.endOffset);
        savedCursorOffsetsRef.current = { start: startOffset, end: endOffset };
        console.log('Cursor position saved with offsets:', { start: startOffset, end: endOffset });
      } else {
        console.log('No selection found, will insert at end');
        savedCursorPositionRef.current = null;
        savedCursorOffsetsRef.current = null;
      }
    }
    
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
    
    return content; // Return content for rendering in handleSidebarTool
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
    setModalTitle('');
    // Reset modal form states
    setLinkUrl('');
    setLinkText('');
    setVideoUrl('');
    setTableRows(3);
    setTableCols(3);
    setCustomEmoji('');
    setCustomChar('');
    // Reset Humanizer state
    setHumanizerInputText('');
    setHumanizerOutputText('');
    setImperfectionPercentage(1);
    setIsHumanizing(false);
    setShowImperfectionTypes(true);
    setImperfectionTypes({
      misspellings: true,
      homophones: true,
      capitalization: true,
      typos: true,
      punctuation: true,
      spacing: true,
      repeatedWords: true,
      grammar: true,
      wordChoice: true,
      fillerWords: true
    });
  };
  
  // AI Chat Modal Handlers
  const handleAIPromptsClick = async () => {
    // Check if API key is configured
    const apiKey = localStorage.getItem('openrouter_api_key');
    if (!apiKey || apiKey.trim() === '') {
      alert('Please configure your OpenRouter API key in Settings to use AI Chat.');
      return;
    }
    setChatApiKey(apiKey);
    setShowChatModal(true);
    
    // If no current conversation, create a new one
    if (!currentConversationId) {
      const newConvId = 'conv_' + Date.now();
      setCurrentConversationId(newConvId);
      setChatMessages([]);
      
      // Load default model from settings for new conversations
      try {
        const models = await getAvailableModels(false, apiKey);
        const savedDefaultModel = localStorage.getItem('default_ai_model');
        if (savedDefaultModel && models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
          setSelectedChatModel(savedDefaultModel);
        }
      } catch (error) {
        console.error('Error loading default model:', error);
      }
    }
  };
  
  const sendChatMessage = async () => {
    if ((!chatInput.trim() && selectedFiles.length === 0) || isChatGenerating || !chatApiKey) return;
    
    // Process attachments with size limits to prevent token overflow
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB max file size
    const MAX_TEXT_CONTENT_LENGTH = 50000; // ~50k characters max for text files (roughly 12k tokens)
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB max for images
    
    const attachments = await Promise.all(selectedFiles.map(async (fileObj) => {
      const fileSize = fileObj.file.size;
      
      if (fileObj.type === 'image') {
        // Check image size
        if (fileSize > MAX_IMAGE_SIZE) {
          alert(`Image "${fileObj.name}" is too large (${(fileSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${(MAX_IMAGE_SIZE / 1024 / 1024).toFixed(0)}MB.`);
          return null;
        }
        
        // Convert image to base64
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              type: 'image',
              name: fileObj.name,
              data: e.target.result,
              mimeType: fileObj.mimeType,
            });
          };
          reader.readAsDataURL(fileObj.file);
        });
      } else {
        // Check file size
        if (fileSize > MAX_FILE_SIZE) {
          alert(`File "${fileObj.name}" is too large (${(fileSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB. Please use a smaller file or split it into parts.`);
          return null;
        }
        
        // Read text file content with truncation for very long files
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            let fileContent = e.target.result;
            
            // Truncate if content is too long to prevent token overflow
            if (fileContent.length > MAX_TEXT_CONTENT_LENGTH) {
              const truncated = fileContent.substring(0, MAX_TEXT_CONTENT_LENGTH);
              const warning = `\n\n[Note: File content was truncated due to length. Original file was ${fileContent.length} characters. Only the first ${MAX_TEXT_CONTENT_LENGTH} characters are included.]`;
              fileContent = truncated + warning;
              alert(`File "${fileObj.name}" is very large. Only the first ${MAX_TEXT_CONTENT_LENGTH.toLocaleString()} characters will be processed to prevent token limit errors.`);
            }
            
            resolve({
              type: 'file',
              name: fileObj.name,
              data: fileContent,
              mimeType: fileObj.mimeType,
            });
          };
          reader.readAsText(fileObj.file);
        });
      }
    })).then(results => results.filter(r => r !== null)); // Filter out any failed reads
    
    const userMessage = {
      role: 'user',
      content: chatInput.trim(),
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    
    // Add user message immediately
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    setChatInput('');
    setSelectedFiles([]);
    setIsChatGenerating(true);
    
    // Create a placeholder assistant message for streaming
    const assistantMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      model: selectedChatModel
    };
    setChatMessages([...updatedMessages, assistantMessage]);
    
    try {
      // Limit messages to prevent token overflow - only send last 30 messages
      // This ensures we don't exceed context limits even with long conversations
      // Note: Each message can be large (especially with images), so we use a conservative limit
      const messagesToSend = updatedMessages.slice(-30);
      
      console.log(`📤 Sending ${messagesToSend.length} messages to API (out of ${updatedMessages.length} total in current conversation)`);
      console.log(`📊 Current conversation ID: ${currentConversationId}`);
      if (updatedMessages.length > 30) {
        console.warn(`⚠️ Conversation has ${updatedMessages.length} messages, limiting to last 30 to prevent token overflow`);
      }
      
      // Verify we're only sending messages from the current conversation
      // This is a safety check to ensure we're not accidentally including other conversations
      if (messagesToSend.length !== updatedMessages.length && updatedMessages.length > 30) {
        console.log(`ℹ️ Message limit applied: sending last 30 of ${updatedMessages.length} messages`);
      }
      
      // Prepare messages array for API with attachments support
      // IMPORTANT: Only include attachments from the CURRENT message (last one) to prevent token overflow
      // Previous messages should have their attachment data stripped out
      // Also truncate very long assistant messages to prevent token overflow
      const currentMessageIndex = messagesToSend.length - 1;
      const MAX_CONTENT_LENGTH = 2000; // Max characters per message to prevent overflow
      
      const apiMessages = messagesToSend.map((msg, index) => {
        const isCurrentMessage = index === currentMessageIndex && msg.role === 'user';
        
        // For assistant messages, truncate content and remove image URLs/base64
        if (msg.role === 'assistant') {
          let content = msg.content || '';
          
          // Remove base64 image data (very long strings)
          content = content.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{100,}/g, '[Generated Image]');
          
          // Remove long image URLs
          content = content.replace(/https?:\/\/[^\s]{200,}/g, '[Image URL]');
          
          // Remove markdown image syntax with long URLs
          content = content.replace(/!\[([^\]]*)\]\([^)]{200,}\)/g, '![$1]([Image URL])');
          
          // Truncate if still too long
          if (content.length > MAX_CONTENT_LENGTH) {
            content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
          }
          
          return {
            role: msg.role,
            content: content
          };
        }
        
        // Only include attachments from the current message being sent
        // For previous messages, strip attachment data to prevent token overflow
        if (msg.attachments && msg.attachments.length > 0) {
          if (isCurrentMessage && msg.attachments.some(att => att.data)) {
            // Current message with attachments - include them
            const contentArray = [];
            
            const textContent = typeof msg.content === 'string' ? msg.content : String(msg.content || '');
            
            // Check if this is an image generation model (models that generate images)
            const isImageGenerationModel = selectedChatModel && (
              selectedChatModel.toLowerCase().includes('nanobanana') ||
              selectedChatModel.toLowerCase().includes('gemini') ||
              selectedChatModel.toLowerCase().includes('image') ||
              selectedChatModel.toLowerCase().includes('dall-e') ||
              selectedChatModel.toLowerCase().includes('midjourney') ||
              selectedChatModel.toLowerCase().includes('stable-diffusion') ||
              selectedChatModel.toLowerCase().includes('flux')
            );
            
            // For image generation models, put images FIRST, then text
            // This ensures the reference image is properly recognized
            if (isImageGenerationModel) {
              // Add image attachments FIRST (important for image generation models)
              msg.attachments.forEach(att => {
                if (att.type === 'image' && att.data) {
                  // Remove data:image/...;base64, prefix if present
                  const base64Data = att.data.includes(',') ? att.data.split(',')[1] : att.data;
                  contentArray.push({
                    type: 'image_url',
                    image_url: {
                      url: `data:${att.mimeType || 'image/jpeg'};base64,${base64Data}`
                    }
                  });
                }
              });
              
            // Then add text content (with explicit reference to the image if needed)
            if (textContent && textContent.trim()) {
              // Check if text already mentions the image/logo/attached - if not, add a reference
              const lowerText = textContent.toLowerCase();
              const hasImageReference = lowerText.includes('image') || 
                                       lowerText.includes('attached') || 
                                       lowerText.includes('reference') ||
                                       lowerText.includes('logo') ||
                                       lowerText.includes('this image') ||
                                       lowerText.includes('the image') ||
                                       lowerText.includes('the logo') ||
                                       lowerText.includes('this logo');
              
              let finalText = textContent.trim();
              if (!hasImageReference && msg.attachments.some(a => a.type === 'image')) {
                // Add explicit reference to the attached image for image generation models
                // Be more specific about including the logo/image in the output
                finalText = `Use the attached image/logo as a reference and include it in the generated image. ${finalText}`;
              }
              
              contentArray.push({
                type: 'text',
                text: finalText
              });
            }
            } else {
              // For regular chat models, text first, then images (standard format)
              if (textContent && textContent.trim()) {
                contentArray.push({
                  type: 'text',
                  text: textContent.trim()
                });
              }
              
              // Add image attachments
              msg.attachments.forEach(att => {
                if (att.type === 'image' && att.data) {
                  // Remove data:image/...;base64, prefix if present
                  const base64Data = att.data.includes(',') ? att.data.split(',')[1] : att.data;
                  contentArray.push({
                    type: 'image_url',
                    image_url: {
                      url: `data:${att.mimeType || 'image/jpeg'};base64,${base64Data}`
                    }
                  });
                }
              });
            }
            
            // Handle file attachments (always after images/text)
            msg.attachments.forEach(att => {
              if (att.type === 'file' && att.data) {
                // Truncate file content if it's still too long (additional safety check)
                let fileContent = att.data;
                const MAX_FILE_CONTENT_LENGTH = 50000; // ~50k chars max
                if (fileContent.length > MAX_FILE_CONTENT_LENGTH) {
                  fileContent = fileContent.substring(0, MAX_FILE_CONTENT_LENGTH) + 
                    `\n\n[File content truncated. Original length: ${att.data.length} characters. Only first ${MAX_FILE_CONTENT_LENGTH} characters included to prevent token limit errors.]`;
                }
                
                // For text files, append to text content
                if (contentArray.length > 0 && contentArray[0].type === 'text') {
                  contentArray[0] = {
                    type: 'text',
                    text: `${contentArray[0].text}\n\n[File: ${att.name}]\n${fileContent}`
                  };
                } else {
                  contentArray.push({
                    type: 'text',
                    text: `[File: ${att.name}]\n${fileContent}`
                  });
                }
              }
            });
            
            return {
              role: msg.role,
              content: contentArray.length > 0 ? contentArray : textContent
            };
          } else {
            // Previous message with attachments - strip data, keep only text reference
            const attachmentRefs = msg.attachments.map(att => 
              att.type === 'image' ? `[Image: ${att.name}]` : `[File: ${att.name}]`
            ).join(', ');
            
            let content = msg.content || '';
            // Truncate if too long
            if (content.length > MAX_CONTENT_LENGTH) {
              content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
            }
            
            return {
              role: msg.role,
              content: content 
                ? `${content}\n${attachmentRefs ? `\n(Attachments: ${attachmentRefs})` : ''}`
                : attachmentRefs
            };
          }
        } else {
          // No attachments, but still truncate if too long
          let content = msg.content || '';
          if (content.length > MAX_CONTENT_LENGTH) {
            content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
          }
          
          return {
            role: msg.role,
            content: content
          };
        }
      });
      
      // Log message sizes for debugging
      const totalSize = JSON.stringify(apiMessages).length;
      console.log(`📊 Total API payload size: ${totalSize} characters`);
      apiMessages.forEach((msg, idx) => {
        const msgSize = typeof msg.content === 'string' ? msg.content.length : JSON.stringify(msg.content).length;
        console.log(`  Message ${idx + 1} (${msg.role}): ${msgSize} chars`);
      });
      
      // Use streaming for faster perceived response time
      let accumulatedContent = '';
      let chunkCount = 0;
      console.log('🚀 Starting chat message generation with model:', selectedChatModel);
      console.log('📤 API Messages:', JSON.stringify(apiMessages, null, 2));
      
      const response = await generateChatMessage(
        apiMessages, 
        selectedChatModel, 
        chatApiKey,
        (chunk) => {
          chunkCount++;
          // Update message in real-time as chunks arrive
          accumulatedContent += chunk;
          console.log(`📦 Chunk #${chunkCount} received (${chunk.length} chars):`, chunk.substring(0, 100));
          console.log(`📊 Total accumulated: ${accumulatedContent.length} chars`);
          
          setChatMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.content = accumulatedContent;
            }
            return newMessages;
          });
        }
      );
      
      console.log('✅ Streaming completed');
      console.log(`📈 Total chunks received: ${chunkCount}`);
      console.log(`📏 Final response length: ${response.length} characters`);
      console.log('📄 Final response content (first 1000 chars):', response.substring(0, 1000));
      console.log('🖼️ Response includes image markers:', response.includes('![Generated Image]') || response.includes('http'));
      console.log('🔍 Searching for image URLs in response...');
      
      // Check for various image URL patterns
      const imageUrlPatterns = [
        /https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp|svg)/gi,
        /https?:\/\/[^\s]+(?:cdn|storage|images?|media|img|assets)[^\s]*/gi,
        /data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+/g,
        /!\[([^\]]*)\]\(([^)]+)\)/g
      ];
      
      imageUrlPatterns.forEach((pattern, idx) => {
        const matches = response.match(pattern);
        if (matches) {
          console.log(`🖼️ Found ${matches.length} potential image(s) with pattern ${idx + 1}:`, matches);
        }
      });
      
      // Final update with complete message
      setChatMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = response;
        }
        return newMessages;
      });
      
      console.log('✅ Message updated in state');
    } catch (error) {
      console.error('❌ Chat error occurred:', error);
      console.error('❌ Error name:', error.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      // Remove the placeholder message and add error
      setChatMessages(prev => {
        const newMessages = prev.slice(0, -1); // Remove placeholder
        
        // Provide helpful error messages for common issues
        let errorContent = error.message || 'Unknown error';
        if (errorContent.includes('maximum context length') || errorContent.includes('token')) {
          errorContent = `Error: Document is too large for analysis. The file exceeds the token limit (200,000 tokens). Please try:\n\n` +
            `1. Use a smaller file or split the document into smaller parts\n` +
            `2. Extract only the relevant sections you need analyzed\n` +
            `3. Use a file size under 5MB for text files\n\n` +
            `Original error: ${errorContent}`;
        }
        
        const errorMessage = {
          role: 'assistant',
          content: `Error: ${errorContent}`,
          timestamp: new Date(),
          model: selectedChatModel,
          isError: true
        };
        return [...newMessages, errorMessage];
      });
    } finally {
      console.log('🏁 Chat generation finished, setting isChatGenerating to false');
      setIsChatGenerating(false);
    }
  };
  
  const handleNewChat = () => {
    // Ensure API key is still available
    if (!chatApiKey) {
      const apiKey = localStorage.getItem('openrouter_api_key');
      if (apiKey) {
        setChatApiKey(apiKey);
      }
    }
    
    // Reset generation state
    setIsChatGenerating(false);
    
    // Create new conversation
    const newConvId = 'conv_' + Date.now();
    setCurrentConversationId(newConvId);
    setChatMessages([]);
    setChatInput('');
    setSelectedFiles([]);
    setShowHistoryPanel(false);
    
    // Focus the input field
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      }
    }, 100);
  };
  
  const handleClearChat = () => {
    if (confirm('Are you sure you want to clear this conversation? This cannot be undone.')) {
      // Reset generation state
      setIsChatGenerating(false);
      
      // Clear messages and input
      setChatMessages([]);
      setChatInput('');
      
      // Focus the input field
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
        }
      }, 100);
    }
  };
  
  const handleClearAllChatHistory = () => {
    if (confirm('Are you sure you want to clear ALL chat history? This cannot be undone.')) {
      // Clear all history first
      setChatHistory([]);
      localStorage.removeItem('fiddyscript_chat_history');
      
      // Ensure API key is still available (reload from localStorage if needed)
      if (!chatApiKey) {
        const apiKey = localStorage.getItem('openrouter_api_key');
        if (apiKey) {
          setChatApiKey(apiKey);
        }
      }
      
      // Reset generation state to ensure nothing is stuck
      setIsChatGenerating(false);
      
      // Create a new conversation immediately so the interface doesn't get locked
      const newConvId = 'conv_' + Date.now();
      setCurrentConversationId(newConvId);
      setChatMessages([]);
      setChatInput('');
      
      // Focus the input field to ensure it's ready for typing
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
        }
      }, 100);
    }
  };
  
  const handleSelectConversation = (conversationId) => {
    const conversation = chatHistory.find(c => c.id === conversationId);
    if (conversation) {
      setCurrentConversationId(conversationId);
      setChatMessages(conversation.messages || []);
      setSelectedChatModel(conversation.model || 'openai/gpt-3.5-turbo');
      setShowHistoryPanel(false);
    }
  };

  const handleRenameConversation = (conversationId, newTitle) => {
    if (!newTitle.trim()) return;
    
    const updatedHistory = chatHistory.map(conv =>
      conv.id === conversationId ? { ...conv, title: newTitle.trim() } : conv
    );
    setChatHistory(updatedHistory);
    localStorage.setItem('fiddyscript_chat_history', JSON.stringify(updatedHistory));
    setEditingConversationId(null);
    setEditingConversationTitle('');
  };

  const handleDeleteConversation = (conversationId) => {
    if (!confirm('Are you sure you want to delete this conversation?')) return;
    
    const updatedHistory = chatHistory.filter(conv => conv.id !== conversationId);
    setChatHistory(updatedHistory);
    localStorage.setItem('fiddyscript_chat_history', JSON.stringify(updatedHistory));
    
    // If deleted conversation is current, switch to most recent or create new
    if (currentConversationId === conversationId) {
      if (updatedHistory.length > 0) {
        const mostRecent = updatedHistory[updatedHistory.length - 1];
        setCurrentConversationId(mostRecent.id);
        setChatMessages(mostRecent.messages || []);
        setSelectedChatModel(mostRecent.model || 'deepseek/deepseek-chat-v3.1:free');
      } else {
        const newConvId = 'conv_' + Date.now();
        setCurrentConversationId(newConvId);
        setChatMessages([]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      type: file.type.startsWith('image/') ? 'image' : 'file',
      name: file.name,
      file: file,
      mimeType: file.type,
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const addToScriptEditor = (messageContent) => {
    try {
      let processedContent = messageContent;
      
      // First, extract and preserve any existing HTML img tags
      // Store them temporarily so they don't get broken by markdown processing
      const imgTagMatches = [];
      if (messageContent.includes('<img')) {
        // Extract complete img tags with all attributes
        processedContent = messageContent.replace(/<img\s+[^>]*?src\s*=\s*["']([^"']+)["'][^>]*>/gi, (match, src) => {
          // Extract alt text if present
          const altMatch = match.match(/alt\s*=\s*["']([^"']*)["']/i);
          const imageAlt = altMatch ? altMatch[1] : 'Image';
          
          // Store the img tag for later restoration
          const placeholder = `__PRESERVED_IMG_${imgTagMatches.length}__`;
          imgTagMatches.push({
            placeholder,
            imgTag: match, // Preserve the full original img tag
            src,
            alt: imageAlt
          });
          return placeholder;
        });
        
        // Also handle any remaining img tags that might not have been caught (fallback)
        processedContent = processedContent.replace(/<img[^>]*>/gi, '');
      }
      
      // Now process markdown images and convert to HTML
      const htmlContent = markdownToHtml(processedContent);
      
      // Restore preserved img tags
      let finalContent = htmlContent;
      imgTagMatches.forEach(({ placeholder, imgTag }) => {
        finalContent = finalContent.replace(placeholder, imgTag);
      });
      
      // Ensure any remaining placeholders are removed (shouldn't happen, but safety check)
      finalContent = finalContent.replace(/__PRESERVED_IMG_\d+__/g, '');
      
      setContent(prevContent => {
        const separator = prevContent ? '<br><br>---<br><br>' : '';
        return prevContent + separator + finalContent;
      });
    } catch (error) {
      console.error('Error adding content to editor:', error);
      // Fallback to simple markdown conversion
      const htmlContent = markdownToHtml(messageContent);
      setContent(prevContent => {
        const separator = prevContent ? '<br><br>---<br><br>' : '';
        return prevContent + separator + htmlContent;
      });
    }
  };

  const copyMessage = async (content, buttonElement) => {
    try {
      // Strip HTML tags for plain text copy
      const textContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      await navigator.clipboard.writeText(textContent);
      // Show temporary feedback
      if (buttonElement) {
        const originalText = buttonElement.textContent;
        buttonElement.textContent = '✓ Copied!';
        buttonElement.style.opacity = '0.7';
        setTimeout(() => {
          buttonElement.textContent = originalText;
          buttonElement.style.opacity = '1';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const startEditingMessage = (index, content) => {
    setEditingMessageIndex(index);
    setEditingMessageContent(content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim());
  };

  const cancelEditing = () => {
    setEditingMessageIndex(null);
    setEditingMessageContent('');
  };

  const saveEditedMessage = async () => {
    if (editingMessageIndex === null || !editingMessageContent.trim()) return;
    
    // Update the message
    const updatedMessages = [...chatMessages];
    updatedMessages[editingMessageIndex].content = editingMessageContent.trim();
    
    // Remove all messages after the edited one (since we're changing the conversation)
    const messagesToKeep = updatedMessages.slice(0, editingMessageIndex + 1);
    setChatMessages(messagesToKeep);
    setEditingMessageIndex(null);
    setEditingMessageContent('');
    
    // If the edited message was a user message, regenerate the AI response
    if (messagesToKeep[messagesToKeep.length - 1].role === 'user') {
      setIsChatGenerating(true);
      try {
        // Limit messages to prevent token overflow - only send last 30 messages
        const limitedMessages = messagesToKeep.slice(-30);
        const MAX_CONTENT_LENGTH = 2000; // Max characters per message to prevent overflow
        
        // Strip attachment data from all messages to prevent token overflow
        // The edited message is the last one, so we don't need to include attachments
        const apiMessages = limitedMessages.map(msg => {
          let content = msg.content || '';
          
          // For assistant messages, remove image URLs/base64
          if (msg.role === 'assistant') {
            // Remove base64 image data (very long strings)
            content = content.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{100,}/g, '[Generated Image]');
            // Remove long image URLs
            content = content.replace(/https?:\/\/[^\s]{200,}/g, '[Image URL]');
            // Remove markdown image syntax with long URLs
            content = content.replace(/!\[([^\]]*)\]\([^)]{200,}\)/g, '![$1]([Image URL])');
          }
          
          if (msg.attachments && msg.attachments.length > 0) {
            // Strip attachment data, keep only text reference
            const attachmentRefs = msg.attachments.map(att => 
              att.type === 'image' ? `[Image: ${att.name}]` : `[File: ${att.name}]`
            ).join(', ');
            
            content = content 
              ? `${content}\n${attachmentRefs ? `\n(Attachments: ${attachmentRefs})` : ''}`
              : attachmentRefs;
          }
          
          // Truncate if too long
          if (content.length > MAX_CONTENT_LENGTH) {
            content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
          }
          
          return {
            role: msg.role,
            content: content
          };
        });
        
        let accumulatedContent = '';
        const response = await generateChatMessage(
          apiMessages,
          selectedChatModel,
          chatApiKey,
          (chunk) => {
            accumulatedContent += chunk;
            setChatMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content = accumulatedContent;
              }
              return newMessages;
            });
          }
        );
        
        setChatMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            lastMessage.content = response;
          }
          return newMessages;
        });
      } catch (error) {
        console.error('Chat error:', error);
        setChatMessages(prev => {
          const errorMessage = {
            role: 'assistant',
            content: `Error: ${error.message}`,
            timestamp: new Date(),
            model: selectedChatModel,
            isError: true
          };
          return [...prev, errorMessage];
        });
      } finally {
        setIsChatGenerating(false);
      }
    }
  };
  
  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Content deletion functions
  const deleteToTop = () => {
    // Check for selected image first
    if (selectedEditorImage) {
      selectedEditorImage.remove();
      setContent(editorRef.current.innerHTML);
      setSelectedEditorImage(null);
      return;
    }
    
    // Check for selected image in DOM
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedNode = range.commonAncestorContainer.nodeType === Node.TEXT_NODE 
        ? range.commonAncestorContainer.parentNode 
        : range.commonAncestorContainer;
      
      if (selectedNode && selectedNode.nodeName === 'IMG') {
        selectedNode.remove();
        setContent(editorRef.current.innerHTML);
        setSelectedEditorImage(null);
        return;
      }
    }
    
    // Fallback to text deletion
    if (cursorPosition.start > 0) {
      const newContent = content.substring(cursorPosition.start);
      setContent(newContent);
      // Reset cursor to beginning
      setCursorPosition({ start: 0, end: 0 });
    }
  };

  const deleteToBottom = () => {
    if (cursorPosition.start < content.length) {
      const newContent = content.substring(0, cursorPosition.start);
      setContent(newContent);
      // Keep cursor at current position
      setCursorPosition({ start: cursorPosition.start, end: cursorPosition.start });
    }
  };

  // Enhanced remove links function
  const removeLinks = () => {
    // More comprehensive link removal
    let cleaned = content
      // Remove <a> tags but preserve text content
      .replace(/<a[^>]*>(.*?)<\/a>/g, '$1')
      // Remove href attributes that might be left
      .replace(/\s*href\s*=\s*["'][^"']*["']/gi, '')
      // Remove target attributes
      .replace(/\s*target\s*=\s*["'][^"']*["']/gi, '')
      // Remove rel attributes
      .replace(/\s*rel\s*=\s*["'][^"']*["']/gi, '')
      // Clean up any remaining link-related attributes
      .replace(/\s*class\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\s*id\s*=\s*["'][^"']*["']/gi, '')
      // Remove empty tags
      .replace(/<[^>]*>\s*<\/[^>]*>/g, '')
      // Clean up extra spaces
      .replace(/\s+/g, ' ')
      .trim();
    
    setContent(cleaned);
  };

  // Convert HTML to plain text
  const convertToText = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    setContent(textContent);
    setContentMode('text');
  };

  // Convert text to HTML (basic formatting)
  const convertToHTML = () => {
    const htmlContent = content
      .split('\n')
      .map(line => {
        if (line.trim() === '') return '<br>';
        return `<p>${line}</p>`;
      })
      .join('');
    setContent(htmlContent);
    setContentMode('html');
  };

  // Toggle between HTML and text modes
  const toggleContentMode = () => {
    if (contentMode === 'html') {
      convertToText();
    } else {
      convertToHTML();
    }
  };

  // Sanitize HTML content for security
  const sanitizeHTML = (htmlContent) => {
    // Remove potentially dangerous tags and attributes
    let sanitized = htmlContent
      // Remove script tags and their content
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      // Remove event handlers
      .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
      // Remove javascript: URLs
      .replace(/javascript:/gi, '')
      // Remove data: URLs (potential security risk)
      .replace(/data:/gi, '')
      // Remove vbscript: URLs
      .replace(/vbscript:/gi, '')
      // Remove iframe tags
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      // Remove object tags
      .replace(/<object[^>]*>.*?<\/object>/gi, '')
      // Remove embed tags
      .replace(/<embed[^>]*>/gi, '')
      // Remove form tags
      .replace(/<form[^>]*>.*?<\/form>/gi, '')
      // Remove input tags
      .replace(/<input[^>]*>/gi, '')
      // Remove button tags
      .replace(/<button[^>]*>.*?<\/button>/gi, '')
      // Remove select tags
      .replace(/<select[^>]*>.*?<\/select>/gi, '')
      // Remove textarea tags
      .replace(/<textarea[^>]*>.*?<\/textarea>/gi, '')
      // Remove style tags
      .replace(/<style[^>]*>.*?<\/style>/gi, '')
      // Remove link tags
      .replace(/<link[^>]*>/gi, '')
      // Remove meta tags
      .replace(/<meta[^>]*>/gi, '')
      // Remove title tags
      .replace(/<title[^>]*>.*?<\/title>/gi, '')
      // Remove head tags
      .replace(/<head[^>]*>.*?<\/head>/gi, '')
      // Remove body tags
      .replace(/<body[^>]*>|<\/body>/gi, '')
      // Remove html tags
      .replace(/<html[^>]*>|<\/html>/gi, '')
      // Remove doctype
      .replace(/<!DOCTYPE[^>]*>/gi, '')
      // Remove comments
      .replace(/<!--.*?-->/g, '')
      // Remove any remaining potentially dangerous attributes
      .replace(/\s*(on\w+|javascript|vbscript|data)\s*=\s*["'][^"']*["']/gi, '')
      .trim();
    
    return sanitized;
  };

  // Auto punctuate function
  const autoPunctuate = () => {
    // Enhanced punctuation rules with better intelligence
    let punctuated = content
      // Remove HTML tags for processing
      .replace(/<[^>]*>/g, ' ')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim();
    
    // Advanced punctuation rules
    punctuated = punctuated
      // Add periods to sentences that don't have them (but are followed by capital letters)
      .replace(/([a-z])\s+([A-Z][a-z])/g, (match, end, start) => {
        // Don't add period if it's an abbreviation or common pattern
        const abbreviations = ['Mr', 'Mrs', 'Dr', 'Prof', 'Inc', 'Ltd', 'Co', 'Corp', 'LLC'];
        if (abbreviations.some(abbr => start.startsWith(abbr))) {
          return match;
        }
        return end + '. ' + start;
      })
      // Ensure proper spacing after punctuation
      .replace(/([.!?,;:])\s*/g, '$1 ')
      // Fix common punctuation mistakes
      .replace(/\s+([.!?,;:])/g, '$1')
      .replace(/([.!?,;:])\s*([A-Z])/g, '$1 $2')
      // Handle quotes properly
      .replace(/"\s+/g, '" ')
      .replace(/\s+"/g, ' "')
      // Handle parentheses
      .replace(/\(\s+/g, '(')
      .replace(/\s+\)/g, ')')
      // Remove multiple spaces again
      .replace(/\s+/g, ' ')
      .trim();
    
    setContent(punctuated);
  };

  // Content analysis function
  const analyzeContent = () => {
    const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = text.split(/\s+/).filter(word => word.length > 0);
    const sentences = text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
    const paragraphs = content.split(/<\/?p[^>]*>/).filter(p => p.trim().length > 0);
    
    const analysis = {
      wordCount: words.length,
      sentenceCount: sentences.length,
      paragraphCount: paragraphs.length,
      averageWordsPerSentence: sentences.length > 0 ? (words.length / sentences.length).toFixed(1) : 0,
      averageWordsPerParagraph: paragraphs.length > 0 ? (words.length / paragraphs.length).toFixed(1) : 0,
      readingTime: Math.ceil(words.length / 200), // Average reading speed
      characterCount: text.length,
      characterCountNoSpaces: text.replace(/\s/g, '').length
    };
    
    // Add HTML-specific analysis
    if (contentMode === 'html') {
      const htmlTags = content.match(/<[^>]*>/g) || [];
      const openTags = content.match(/<([a-z][a-z0-9]*)/gi) || [];
      const closeTags = content.match(/<\/([a-z][a-z0-9]*)/gi) || [];
      
      const tagCounts = {};
      openTags.forEach(tag => {
        const tagName = tag.replace('<', '').toLowerCase();
        tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
      });
      
      analysis.htmlStats = {
        totalTags: htmlTags.length,
        openTags: openTags.length,
        closeTags: closeTags.length,
        tagCounts: tagCounts,
        mostUsedTags: Object.entries(tagCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5)
          .map(([tag, count]) => ({ tag, count }))
      };
    }
    
    return analysis;
  };

  // Content validation function
  const validateContent = () => {
    const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const issues = [];
    
    // Check for common issues
    if (text.length === 0) {
      issues.push({ type: 'error', message: 'Content is empty' });
    }
    
    if (text.length < 50) {
      issues.push({ type: 'warning', message: 'Content is very short (less than 50 characters)' });
    }
    
    // Check for repeated words
    const words = text.toLowerCase().split(/\s+/);
    const wordCount = {};
    words.forEach(word => {
      wordCount[word] = (wordCount[word] || 0) + 1;
    });
    
    Object.entries(wordCount).forEach(([word, count]) => {
      if (count > 5 && word.length > 3) {
        issues.push({ type: 'warning', message: `Word "${word}" is repeated ${count} times` });
      }
    });
    
    // Check for long sentences
    const sentences = text.split(/[.!?]+/);
    sentences.forEach((sentence, index) => {
      const sentenceWords = sentence.trim().split(/\s+/);
      if (sentenceWords.length > 30) {
        issues.push({ type: 'warning', message: `Sentence ${index + 1} is very long (${sentenceWords.length} words)` });
      }
    });
    
    // Check for missing punctuation
    const lines = text.split('\n');
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed.length > 0 && !trimmed.match(/[.!?]$/)) {
        issues.push({ type: 'info', message: `Line ${index + 1} might need punctuation` });
      }
    });
    
    // HTML-specific validation
    if (contentMode === 'html') {
      // Check for unclosed tags
      const openTags = content.match(/<([a-z][a-z0-9]*)/gi) || [];
      const closeTags = content.match(/<\/([a-z][a-z0-9]*)/gi) || [];
      
      const openTagCounts = {};
      const closeTagCounts = {};
      
      openTags.forEach(tag => {
        const tagName = tag.replace('<', '').toLowerCase();
        openTagCounts[tagName] = (openTagCounts[tagName] || 0) + 1;
      });
      
      closeTags.forEach(tag => {
        const tagName = tag.replace('</', '').toLowerCase();
        closeTagCounts[tagName] = (closeTagCounts[tagName] || 0) + 1;
      });
      
      Object.keys(openTagCounts).forEach(tagName => {
        const openCount = openTagCounts[tagName];
        const closeCount = closeTagCounts[tagName] || 0;
        if (openCount > closeCount) {
          issues.push({ 
            type: 'warning', 
            message: `Unclosed <${tagName}> tag (${openCount - closeCount} unclosed)` 
          });
        }
      });
      
      // Check for potentially problematic HTML
      if (content.includes('<script')) {
        issues.push({ type: 'warning', message: 'Content contains script tags (security risk)' });
      }
      
      if (content.includes('javascript:')) {
        issues.push({ type: 'error', message: 'Content contains JavaScript code (security risk)' });
      }
    }
    
    return issues;
  };

  // Format content function
  const formatContent = () => {
    let formatted = content
      // Remove HTML tags for processing
      .replace(/<[^>]*>/g, ' ')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim();
    
    // Apply formatting rules
    formatted = formatted
      // Capitalize first letter of sentences
      .replace(/(^|\.\s+)([a-z])/g, (match, prefix, letter) => prefix + letter.toUpperCase())
      // Ensure proper spacing around punctuation
      .replace(/([.!?,;:])\s*/g, '$1 ')
      .replace(/\s+([.!?,;:])/g, '$1')
      // Fix common formatting issues
      .replace(/\s+([.!?,;:])\s+([A-Z])/g, '$1 $2')
      // Handle quotes
      .replace(/"\s+/g, '" ')
      .replace(/\s+"/g, ' "')
      // Handle apostrophes
      .replace(/\s+'/g, "'")
      .replace(/'\s+/g, "'")
      // Remove multiple spaces
      .replace(/\s+/g, ' ')
      .trim();
    
    setContent(formatted);
  };

  // Smart content cleanup
  const smartCleanup = () => {
    if (contentMode === 'html') {
      // Clean HTML content while preserving formatting
    let cleaned = content
        // Remove empty elements
        .replace(/<[^>]*>\s*<\/[^>]*>/g, '')
        // Remove excessive whitespace but preserve line breaks
        .replace(/\s+/g, ' ')
        // Clean up empty paragraphs
        .replace(/<p>\s*<\/p>/g, '')
        // Ensure proper spacing around elements
        .replace(/>\s+</g, '><')
        .replace(/>\s+</g, '> <')
        // Remove any remaining script tags
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        // Remove any remaining style tags
        .replace(/<style[^>]*>.*?<\/style>/gi, '')
        // Remove unwanted attributes but keep essential ones
        .replace(/\s*(class|id|style)\s*=\s*["'][^"']*["']/gi, '')
        .trim();
      
      setContent(cleaned);
    } else {
      // Clean text content
      let cleaned = content
      // Remove extra whitespace
      .replace(/\s+/g, ' ')
      // Remove leading/trailing whitespace
      .trim()
      // Fix common typos
      .replace(/\bteh\b/gi, 'the')
      .replace(/\brecieve\b/gi, 'receive')
      .replace(/\boccured\b/gi, 'occurred')
      .replace(/\bseperate\b/gi, 'separate')
      .replace(/\bdefinately\b/gi, 'definitely')
      // Fix spacing around punctuation
      .replace(/([.!?,;:])\s*/g, '$1 ')
      .replace(/\s+([.!?,;:])/g, '$1')
      // Remove multiple spaces again
      .replace(/\s+/g, ' ')
      .trim();
    
    setContent(cleaned);
    }
  };

  // Content import functions
  const scrapeURL = async (url) => {
    try {
      // Use a CORS proxy to fetch content
      const response = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (data.contents) {
        // Parse the HTML content
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.contents, 'text/html');
        
        // Remove unwanted elements but preserve formatting
        const unwantedElements = doc.querySelectorAll('script, style, nav, header, footer, aside, .ads, .advertisement, .sidebar, .comments, .social-share');
        unwantedElements.forEach(el => el.remove());
        
        // Extract main content
        const mainContent = doc.querySelector('main, article, .content, .post, .entry, .article, .story, .text') || doc.body;
        
        // Preserve HTML formatting while cleaning up
        let htmlContent = mainContent.innerHTML;
        
        // Clean up the HTML while preserving formatting
        htmlContent = htmlContent
          // Remove empty elements
          .replace(/<[^>]*>\s*<\/[^>]*>/g, '')
          // Remove excessive whitespace but preserve line breaks
          .replace(/\s+/g, ' ')
          // Clean up empty paragraphs
          .replace(/<p>\s*<\/p>/g, '')
          // Ensure proper spacing around elements
          .replace(/>\s+</g, '><')
          .replace(/>\s+</g, '> <')
          .trim();
        
        // Sanitize HTML for security
        htmlContent = sanitizeHTML(htmlContent);
        
        // Also get text content for analysis
        const textContent = mainContent.textContent || mainContent.innerText || '';
        const cleanedText = textContent
          .replace(/\s+/g, ' ')
          .replace(/\n+/g, '\n')
          .trim();
        
        return {
          success: true,
          title: doc.title || 'Scraped Content',
          content: htmlContent, // Return HTML content
          textContent: cleanedText, // Also provide text version
          url: url
        };
      } else {
        throw new Error('Failed to fetch content');
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to scrape URL'
      };
    }
  };

  const processFile = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          let processedContent = '';
          
          if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
            processedContent = content;
          } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
            // For PDF, we would need a PDF parsing library
            // This is a simplified version
            processedContent = content.replace(/[^\x00-\x7F]/g, ''); // Remove non-ASCII
          } else if (file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
            // For Word documents, we would need a library like mammoth.js
            // This is a simplified version
            processedContent = content.replace(/[^\x00-\x7F]/g, ''); // Remove non-ASCII
          } else {
            processedContent = content;
          }
          
          resolve({
            success: true,
            filename: file.name,
            content: processedContent,
            size: file.size
          });
        } catch (error) {
          resolve({
            success: false,
            error: 'Failed to process file'
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          success: false,
          error: 'Failed to read file'
        });
      };
      
      reader.readAsText(file);
    });
  };

  // Advanced transcription using OpenAI Whisper API
  const transcribeWithWhisper = async (audioUrl, apiKey) => {
    try {
      const OpenAI = await import('openai');
      const openai = new OpenAI.default({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });
      
      // Download audio from YouTube
      const ytdl = await import('ytdl-core');
      const audioStream = ytdl.default(audioUrl, { filter: 'audioonly' });
      
      // Convert stream to buffer (simplified - in real implementation you'd need proper stream handling)
      const chunks = [];
      for await (const chunk of audioStream) {
        chunks.push(chunk);
      }
      const audioBuffer = Buffer.concat(chunks);
      
      // Transcribe with Whisper
      const transcription = await openai.audio.transcriptions.create({
        file: new File([audioBuffer], 'audio.mp3', { type: 'audio/mpeg' }),
        model: 'whisper-1',
      });
      
      return transcription.text;
    } catch (error) {
      throw new Error(`Whisper transcription failed: ${error.message}`);
    }
  };

  const extractYouTubeTranscript = async (videoUrl, method = 'auto') => {
    try {
      // Extract video ID from URL
      const videoId = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }
      
      // Method 1: Try to get existing captions/transcripts first
      if (method === 'captions' || method === 'auto') {
        try {
          const { YoutubeTranscript } = await import('youtube-transcript-api');
          const transcriptList = await YoutubeTranscript.fetchTranscript(videoId);
          
          const transcript = transcriptList
            .map(part => part.text)
            .join(' ');
          
          return {
            success: true,
            videoId: videoId,
            transcript: transcript,
            url: videoUrl,
            method: 'captions'
          };
        } catch (captionError) {
          if (method === 'captions') {
            throw new Error(`No captions available for this video. Error: ${captionError.message}`);
          }
          // If auto mode, continue to other methods
          console.log('No captions available, trying other methods...');
        }
      }
      
      // Method 2: OpenAI Whisper API
      if (method === 'whisper') {
        try {
          // Check if API key is available (you'd store this in settings/localStorage)
          const apiKey = localStorage.getItem('openai_api_key');
          if (!apiKey) {
            return {
              success: true,
              videoId: videoId,
              transcript: `OpenAI Whisper API requires an API key.
              
              To use Whisper transcription:
              1. Get an API key from https://platform.openai.com/
              2. Add it to your app settings
              3. Try again with the API key configured
              
              Video ID: ${videoId}
              URL: ${videoUrl}`,
              url: videoUrl,
              method: 'whisper-setup',
              videoInfo: { title: 'YouTube Video', lengthSeconds: 0 }
            };
          }
          
          // Get video info for Whisper
          const ytdl = await import('ytdl-core');
          const info = await ytdl.getInfo(videoUrl);
          
          // For now, return a message about Whisper setup
          // In a full implementation, you'd download the audio and transcribe
          return {
            success: true,
            videoId: videoId,
            transcript: `Whisper API transcription setup:
              
              Video Title: ${info.videoDetails.title}
              Duration: ${info.videoDetails.lengthSeconds} seconds
              
              To enable full Whisper transcription:
              1. Ensure API key is configured
              2. Audio download and transcription will be implemented
              3. This will provide high-accuracy speech-to-text
              
              Video ID: ${videoId}
              URL: ${videoUrl}`,
            url: videoUrl,
            method: 'whisper',
            videoInfo: info.videoDetails
          };
        } catch (whisperError) {
          throw new Error(`Whisper transcription failed: ${whisperError.message}`);
        }
      }
      
      // Method 3: Browser Speech Recognition
      if (method === 'browser') {
        try {
          const ytdl = await import('ytdl-core');
          const info = await ytdl.getInfo(videoUrl);
          
          return {
            success: true,
            videoId: videoId,
            transcript: `Browser Speech Recognition Guide:
              
              Video Title: ${info.videoDetails.title}
              Duration: ${info.videoDetails.lengthSeconds} seconds
              
              To transcribe using browser speech recognition:
              1. Open the video in a new tab
              2. Play the video audio
              3. Use browser's speech recognition feature
              4. Copy the transcribed text to this editor
              
              Alternative: Use browser extensions for YouTube transcription
              
              Video ID: ${videoId}
              URL: ${videoUrl}`,
            url: videoUrl,
            method: 'browser',
            videoInfo: info.videoDetails
          };
        } catch (browserError) {
          throw new Error(`Browser method failed: ${browserError.message}`);
        }
      }
      
      // Method 4: Fallback to alternatives (for auto mode)
      console.log('No captions available, trying speech-to-text...');
      
      // Use ytdl-core to download audio and transcribe
      const ytdl = await import('ytdl-core');
      
      // Get video info
      const info = await ytdl.getInfo(videoUrl);
      const audioFormats = ytdl.filterFormats(info.formats, 'audioonly');
      
      if (audioFormats.length === 0) {
        throw new Error('No audio formats available for this video');
      }
      
      // Get the best audio format
      const audioFormat = audioFormats[0];
      
      // Use browser's Web Speech API as fallback
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        return {
          success: true,
          videoId: videoId,
          transcript: `Speech-to-text transcription is available for this video.
          
          Video Title: ${info.videoDetails.title}
          Duration: ${info.videoDetails.lengthSeconds} seconds
          Audio Format: ${audioFormat.qualityLabel || 'Unknown'}
          
          To transcribe this video:
          1. Play the video in the browser
          2. Use the browser's speech recognition
          3. Or use a third-party transcription service
          
          Video ID: ${videoId}
          URL: ${videoUrl}`,
          url: videoUrl,
          method: 'speech-to-text',
          videoInfo: info.videoDetails
        };
      } else {
        // Provide alternative solutions with API options
        return {
          success: true,
          videoId: videoId,
          transcript: `Advanced transcription options for this video:
          
          Video Title: ${info.videoDetails.title}
          Duration: ${info.videoDetails.lengthSeconds} seconds
          
          Available Methods:
          1. 🎯 OpenAI Whisper API (Recommended)
             - High accuracy speech-to-text
             - Supports multiple languages
             - Requires API key
          
          2. 🌐 Google Cloud Speech-to-Text
             - Enterprise-grade accuracy
             - Real-time transcription
             - Requires Google Cloud account
          
          3. 📱 Browser Speech Recognition
             - Free, built-in browser feature
             - Play video and use microphone
             - Limited to supported browsers
          
          4. 💼 Professional Services
             - Rev.com, Trint, Otter.ai
             - Human-reviewed transcripts
             - Paid services with high accuracy
          
          To enable API-based transcription:
          1. Get API keys from OpenAI or Google Cloud
          2. Configure in app settings
          3. Use the "Advanced Transcription" option
          
          Video ID: ${videoId}
          URL: ${videoUrl}`,
          url: videoUrl,
          method: 'alternatives',
          videoInfo: info.videoDetails
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to extract transcript'
      };
    }
  };

  // Phase 5: Humanizer functions
  const handleImperfectionToggle = (key) => {
    setImperfectionTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const humanizeText = async () => {
    if (!humanizerInputText.trim()) return;

    setIsHumanizing(true);
    
    setTimeout(() => {
      let humanizedText = humanizerInputText;
      
      // Apply selected imperfection types
      Object.entries(imperfectionTypes).forEach(([type, enabled]) => {
        if (!enabled) return;
        
        switch (type) {
          case 'misspellings':
            humanizedText = humanizedText
              .replace(/\bseparate\b/gi, 'seperate')
              .replace(/\breceive\b/gi, 'recieve')
              .replace(/\boccasion\b/gi, 'occassion')
              .replace(/\bdefinitely\b/gi, 'definately')
              .replace(/\baccommodate\b/gi, 'accomodate');
            break;
          case 'homophones':
            humanizedText = humanizedText
              .replace(/\btheir\b/gi, 'there')
              .replace(/\byou're\b/gi, 'your')
              .replace(/\bit's\b/gi, 'its')
              .replace(/\btoo\b/gi, 'to')
              .replace(/\btwo\b/gi, 'to');
            break;
          case 'capitalization':
            humanizedText = humanizedText
              .replace(/\bThis\b/g, 'THis')
              .replace(/\bThat\b/g, 'THat')
              .replace(/\bThey\b/g, 'THey')
              .replace(/\bThere\b/g, 'THere')
              .replace(/\bThen\b/g, 'THen');
            break;
          case 'typos':
            humanizedText = humanizedText
              .replace(/\bthe\b/gi, 'teh')
              .replace(/\btheir\b/gi, 'thier')
              .replace(/\breceive\b/gi, 'recieve')
              .replace(/\boccasion\b/gi, 'occassion')
              .replace(/\bdefinitely\b/gi, 'definately');
            break;
          case 'punctuation':
            humanizedText = humanizedText
              .replace(/\bI've\b/gi, 'Ive')
              .replace(/\bdon't\b/gi, 'dont')
              .replace(/\bcan't\b/gi, 'cant')
              .replace(/\bwon't\b/gi, 'wont')
              .replace(/\bshouldn't\b/gi, 'shouldnt')
              .replace(/\bcouldn't\b/gi, 'couldnt');
            break;
          case 'spacing':
            humanizedText = humanizedText
              .replace(/\. /g, '.  ')
              .replace(/, /g, ',  ')
              .replace(/! /g, '!  ')
              .replace(/\? /g, '?  ');
            break;
          case 'repeatedWords':
            humanizedText = humanizedText
              .replace(/\bthat\b/gi, 'that that')
              .replace(/\bthe\b/gi, 'the the')
              .replace(/\bis\b/gi, 'is is')
              .replace(/\band\b/gi, 'and and')
              .replace(/\bbut\b/gi, 'but but');
            break;
          case 'grammar':
            humanizedText = humanizedText
              .replace(/\bHe goes\b/gi, 'He go')
              .replace(/\bShe eats\b/gi, 'She eat')
              .replace(/\bI don't have any\b/gi, "I don't have no");
            break;
          case 'wordChoice':
            humanizedText = humanizedText
              .replace(/\bexcept\b/gi, 'accept')
              .replace(/\beffect\b/gi, 'affect')
              .replace(/\bthan\b/gi, 'then');
            break;
          case 'fillerWords':
            const fillerWords = ['um', 'like', 'you know', 'actually', 'basically', 'sort of', 'kind of'];
            const sentences = humanizedText.split(/[.!?]+/);
            const humanizedSentences = sentences.map(sentence => {
              if (Math.random() < 0.3) { // 30% chance to add filler word
                const filler = fillerWords[Math.floor(Math.random() * fillerWords.length)];
                return sentence.trim() + ' ' + filler;
              }
              return sentence.trim();
            });
            humanizedText = humanizedSentences.join('. ') + '.';
            break;
        }
      });
      
      // Apply imperfection percentage
      const words = humanizedText.split(' ');
      const wordsToModify = Math.floor(words.length * (imperfectionPercentage / 100));
      
      // Randomly apply imperfections to some words
      for (let i = 0; i < wordsToModify; i++) {
        const randomIndex = Math.floor(Math.random() * words.length);
        const word = words[randomIndex];
        
        // Apply random imperfection
        const imperfections = [
          () => word.toLowerCase(),
          () => word.toUpperCase(),
          () => word.split('').reverse().join(''),
          () => word + word[word.length - 1],
          () => word.slice(0, -1) + word[word.length - 1] + word[word.length - 1]
        ];
        
        const randomImperfection = imperfections[Math.floor(Math.random() * imperfections.length)];
        words[randomIndex] = randomImperfection();
      }
      
      setHumanizerOutputText(words.join(' '));
      setIsHumanizing(false);
    }, 1000);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(humanizerOutputText);
  };

  const clearAll = () => {
    setHumanizerInputText('');
    setHumanizerOutputText('');
  };

  const swapText = () => {
    setHumanizerInputText(humanizerOutputText);
    setHumanizerOutputText(humanizerInputText);
  };

  // Enhanced modal content for Phase 3
  const renderScrapeURLModal = () => (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Enter URL to scrape:
        </label>
        <input
          type="url"
          id="scrape-url-input"
          placeholder="https://example.com"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          className="modal-btn secondary"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          className="modal-btn"
          onClick={async () => {
            const urlInput = document.getElementById('scrape-url-input');
            const url = urlInput.value.trim();
            
            if (!url) {
              alert('Please enter a valid URL');
              return;
            }
            
            // Show loading state
            const loadingModal = openModal('Scraping URL...', 
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading" style={{ margin: '0 auto 16px' }}></div>
                <p>Scraping content from: {url}</p>
              </div>
            );
            
            const result = await scrapeURL(url);
            closeModal();
            
            if (result.success) {
              // Ask user which format they want
              openModal('Content Format Selection', 
                <div>
                  <h4>Content extracted from: {result.title}</h4>
                  <p><strong>URL:</strong> {result.url}</p>
                  <p><strong>HTML Content:</strong> {result.content.length} characters</p>
                  <p><strong>Text Content:</strong> {result.textContent.length} characters</p>
                  
                  <div style={{ marginTop: '16px' }}>
                    <h5>Choose format to load:</h5>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                      <button
                        className="modal-btn"
                        onClick={() => {
                          setContent(result.content); // HTML content
                          closeModal();
                          openModal('Scrape Successful', 
                            <div>
                              <h4>HTML Content Loaded</h4>
                              <p><strong>Format:</strong> HTML with formatting preserved</p>
                  <p><strong>Content length:</strong> {result.content.length} characters</p>
                              <div style={{ 
                                maxHeight: '200px', 
                                overflow: 'auto', 
                                background: '#f8f9fa', 
                                padding: '12px', 
                                borderRadius: '4px',
                                marginTop: '12px'
                              }}>
                                <div style={{ margin: 0, fontSize: '12px' }} 
                                     dangerouslySetInnerHTML={{ __html: result.content.substring(0, 500) + '...' }}>
                                </div>
                              </div>
                              <p style={{ marginTop: '12px', fontSize: '12px', color: '#6c757d' }}>
                                HTML content with original formatting has been loaded into the editor.
                              </p>
                            </div>
                          );
                        }}
                      >
                        Load HTML (Preserve Formatting)
                      </button>
                      <button
                        className="modal-btn secondary"
                        onClick={() => {
                          setContent(result.textContent); // Text content
                          closeModal();
                          openModal('Scrape Successful', 
                            <div>
                              <h4>Text Content Loaded</h4>
                              <p><strong>Format:</strong> Plain text only</p>
                              <p><strong>Content length:</strong> {result.textContent.length} characters</p>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflow: 'auto', 
                    background: '#f8f9fa', 
                    padding: '12px', 
                    borderRadius: '4px',
                    marginTop: '12px'
                  }}>
                    <p style={{ margin: 0, fontSize: '12px' }}>
                                  {result.textContent.substring(0, 500)}...
                    </p>
                  </div>
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#6c757d' }}>
                                Plain text content has been loaded into the editor.
                              </p>
                            </div>
                          );
                        }}
                      >
                        Load Text Only
                      </button>
                    </div>
                  </div>
                </div>
              );
            } else {
              openModal('Scrape Failed', 
                <div>
                  <h4 style={{ color: '#dc3545' }}>Failed to scrape URL</h4>
                  <p><strong>Error:</strong> {result.error}</p>
                  <p style={{ fontSize: '12px', color: '#6c757d' }}>
                    Make sure the URL is accessible and contains text content.
                  </p>
                </div>
              );
            }
          }}
        >
          Scrape Content
        </button>
      </div>
    </div>
  );

  const renderFileToTextModal = () => (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Select file to convert to text:
        </label>
        <input
          type="file"
          id="file-input"
          accept=".txt,.doc,.docx,.pdf"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
          Supported formats: TXT, DOC, DOCX, PDF
        </p>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          className="modal-btn secondary"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          className="modal-btn"
          onClick={async () => {
            const fileInput = document.getElementById('file-input');
            const file = fileInput.files[0];
            
            if (!file) {
              alert('Please select a file');
              return;
            }
            
            // Show loading state
            openModal('Processing File...', 
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading" style={{ margin: '0 auto 16px' }}></div>
                <p>Processing: {file.name}</p>
                <p style={{ fontSize: '12px', color: '#6c757d' }}>
                  Size: {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            );
            
            const result = await processFile(file);
            closeModal();
            
            if (result.success) {
              setContent(result.content);
              openModal('File Processed Successfully', 
                <div>
                  <h4>File processed: {result.filename}</h4>
                  <p><strong>File size:</strong> {(result.size / 1024).toFixed(1)} KB</p>
                  <p><strong>Content length:</strong> {result.content.length} characters</p>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflow: 'auto', 
                    background: '#f8f9fa', 
                    padding: '12px', 
                    borderRadius: '4px',
                    marginTop: '12px'
                  }}>
                    <p style={{ margin: 0, fontSize: '12px' }}>
                      {result.content.substring(0, 500)}...
                    </p>
                  </div>
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#6c757d' }}>
                    Content has been loaded into the editor.
                  </p>
                </div>
              );
            } else {
              openModal('File Processing Failed', 
                <div>
                  <h4 style={{ color: '#dc3545' }}>Failed to process file</h4>
                  <p><strong>Error:</strong> {result.error}</p>
                  <p style={{ fontSize: '12px', color: '#6c757d' }}>
                    Make sure the file is in a supported format and not corrupted.
                  </p>
                </div>
              );
            }
          }}
        >
          Process File
        </button>
      </div>
    </div>
  );

  const renderYouTubeToTextModal = () => (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Enter YouTube video URL:
        </label>
        <input
          type="url"
          id="youtube-url-input"
          placeholder="https://www.youtube.com/watch?v=..."
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
          Supports YouTube video URLs (watch, youtu.be, etc.)
        </p>
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Transcription Method:
        </label>
        <select
          id="transcription-method"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="auto">🔄 Auto (Try captions first, then alternatives)</option>
          <option value="captions">📝 YouTube Captions Only</option>
          <option value="whisper">🤖 OpenAI Whisper API</option>
          <option value="browser">📱 Browser Speech Recognition</option>
        </select>
        <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
          Choose your preferred transcription method
        </p>
      </div>
      
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          className="modal-btn secondary"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          className="modal-btn"
          onClick={async () => {
            const urlInput = document.getElementById('youtube-url-input');
            const methodSelect = document.getElementById('transcription-method');
            const url = urlInput.value.trim();
            const method = methodSelect.value;
            
            if (!url) {
              alert('Please enter a valid YouTube URL');
              return;
            }
            
            // Show loading state
            let loadingMessage = 'Extracting transcript from YouTube video...';
            if (method === 'whisper') {
              loadingMessage = 'Transcribing with OpenAI Whisper API...';
            } else if (method === 'browser') {
              loadingMessage = 'Preparing browser speech recognition...';
            }
            
            openModal('Extracting Transcript...', 
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading" style={{ margin: '0 auto 16px' }}></div>
                <p>{loadingMessage}</p>
                <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
                  This may take a few seconds depending on video length
                </p>
              </div>
            );
            
            const result = await extractYouTubeTranscript(url, method);
            closeModal();
            
            if (result.success) {
              setContent(result.transcript);
              
              let modalTitle = 'Transcript Extracted';
              let modalContent = (
                <div>
                  <h4>Transcript extracted successfully</h4>
                  <p><strong>Video ID:</strong> {result.videoId}</p>
                  <p><strong>URL:</strong> {result.url}</p>
                  <p><strong>Method:</strong> {result.method === 'captions' ? 'YouTube Captions' : 
                    result.method === 'whisper' ? 'OpenAI Whisper API' :
                    result.method === 'speech-to-text' ? 'Speech-to-Text' : 'Alternative Methods'}</p>
                  <p><strong>Transcript length:</strong> {result.transcript.length} characters</p>
                  
                  {result.videoInfo && (
                    <div style={{ 
                      background: '#e8f4fd', 
                      padding: '8px', 
                      borderRadius: '4px',
                      marginBottom: '12px'
                    }}>
                      <p style={{ margin: 0, fontSize: '12px' }}>
                        <strong>Video Title:</strong> {result.videoInfo.title}<br/>
                        <strong>Duration:</strong> {Math.floor(result.videoInfo.lengthSeconds / 60)}:{String(result.videoInfo.lengthSeconds % 60).padStart(2, '0')}
                      </p>
                    </div>
                  )}
                  
                  <div style={{ 
                    maxHeight: '200px', 
                    overflow: 'auto', 
                    background: '#f8f9fa', 
                    padding: '12px', 
                    borderRadius: '4px',
                    marginTop: '12px'
                  }}>
                    <p style={{ margin: 0, fontSize: '12px' }}>
                      {result.transcript.substring(0, 500)}...
                    </p>
                  </div>
                  
                  {result.method === 'captions' && (
                    <p style={{ marginTop: '12px', fontSize: '12px', color: '#28a745' }}>
                      ✅ Real transcript extracted from YouTube captions
                    </p>
                  )}
                  
                  {result.method === 'speech-to-text' && (
                    <p style={{ marginTop: '12px', fontSize: '12px', color: '#ffc107' }}>
                      ⚠️ Speech-to-text transcription guide provided. Use browser's speech recognition or third-party services.
                    </p>
                  )}
                  
                  {result.method === 'alternatives' && (
                    <p style={{ marginTop: '12px', fontSize: '12px', color: '#17a2b8' }}>
                      ℹ️ Alternative transcription methods provided. Consider using professional transcription services.
                    </p>
                  )}
                </div>
              );
              
              openModal(modalTitle, modalContent);
            } else {
              openModal('Transcript Extraction Failed', 
                <div>
                  <h4 style={{ color: '#dc3545' }}>Failed to extract transcript</h4>
                  <p><strong>Error:</strong> {result.error}</p>
                  <p style={{ fontSize: '12px', color: '#6c757d' }}>
                    Make sure the video has available transcripts and the URL is valid.
                  </p>
                </div>
              );
            }
          }}
        >
          Extract Transcript
        </button>
      </div>
    </div>
  );

  // Phase 4: Image Library Modal
  // Insert Image Modal - For local file upload and URL insertion
  // Completely new, simple Insert Image modal
  const renderInsertImageModal = () => (
    <div className="insert-image-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Insert Image</h3>
        <button className="close-x" onClick={closeModal}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="form-row">
          <label>Insert image from computer:</label>
          <div className="file-upload-section">
            <input
              type="file"
              accept="image/*"
              onChange={handleInsertImageUpload}
              id="insert-image-upload"
              style={{ display: 'none' }}
            />
            <label htmlFor="insert-image-upload" className="btn-choose-file">
              📁 Choose File
            </label>
          </div>
        </div>
        
        <div className="form-row">
          <label>Or insert image from URL:</label>
          <input
            ref={(input) => {
              if (input && imageUrl) {
                input.value = imageUrl;
              }
            }}
            type="url"
            placeholder="https://website.com/image.jpg"
            defaultValue={imageUrl || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              console.log('URL input changed to:', newValue);
              setImageUrl(newValue);
            }}
            onInput={(e) => {
              const newValue = e.target.value;
              console.log('URL input onInput:', newValue);
              setImageUrl(newValue);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onBlur={(e) => {
              console.log('URL input onBlur:', e.target.value);
              setImageUrl(e.target.value);
            }}
            className="url-input"
          />
        </div>
      </div>
      
      <div className="modal-buttons">
        <button 
          className="btn-insert-image"
          onClick={(e) => {
            e.stopPropagation();
            console.log('=== Insert Image button clicked ===');
            
            // Get URL directly from input field instead of state
            const urlInput = e.target.closest('.insert-image-modal').querySelector('input[type="url"]');
            const currentUrl = urlInput ? urlInput.value : '';
            
            console.log('URL from input field:', currentUrl);
            console.log('imageUrl state value:', imageUrl);
            
            if (currentUrl && currentUrl.trim()) {
              console.log('Calling insertImageFromUrl with:', currentUrl);
              try {
                insertImageFromUrl(currentUrl);
                console.log('insertImageFromUrl call completed');
              } catch (error) {
                console.error('Error calling insertImageFromUrl:', error);
              }
            } else {
              console.log('No valid URL, showing alert');
              alert('Please enter a valid image URL');
            }
          }}
        >
          Insert Image
        </button>
        <button className="btn-cancel" onClick={(e) => {
          e.stopPropagation();
          closeModal();
        }}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Image Library Modal - For searching online image services
  const renderImageLibraryModal = () => (
    <div className="image-library-modal">
      <div className="modal-header">
        <h3>Image Library</h3>
        <button className="close-x" onClick={closeModal}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="image-source-tabs">
          <button 
            className={`tab ${selectedImageSource === 'pexels' ? 'active' : ''}`}
            onClick={() => setSelectedImageSource('pexels')}
          >
            Pexels
          </button>
          <button 
            className={`tab ${selectedImageSource === 'pixabay' ? 'active' : ''}`}
            onClick={() => setSelectedImageSource('pixabay')}
          >
            Pixabay
          </button>
          <button 
            className={`tab ${selectedImageSource === 'unsplash' ? 'active' : ''}`}
            onClick={() => setSelectedImageSource('unsplash')}
          >
            Unsplash
          </button>
          <button 
            className={`tab ${selectedImageSource === 'iconfinder' ? 'active' : ''}`}
            onClick={() => setSelectedImageSource('iconfinder')}
          >
            Iconfinder
          </button>
        </div>
        
        <div className="search-section">
          <label>Search phrase:</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Enter search term..."
              value={imageSearchQuery || ''}
              onChange={(e) => setImageSearchQuery(e.target.value)}
              className="search-input"
            />
            <button 
              className="btn-search"
              onClick={() => searchImages(imageSearchQuery)}
            >
              Search
            </button>
            <button 
              className="btn-trending"
              onClick={() => getTrendingImages()}
            >
              Trending
            </button>
          </div>
        </div>
        
        <div className="image-results">
          {imageSearchResults.length === 0 ? (
            <div className="no-results">
              <p>No images found. Try searching for something or click "Trending".</p>
            </div>
          ) : (
            <div className="image-grid">
              {imageSearchResults.map((image) => (
                <div
                  key={image.id}
                  className={`image-result-item ${selectedSearchImage?.id === image.id ? 'selected' : ''}`}
                  onClick={() => setSelectedSearchImage(image)}
                >
                  <img src={image.previewUrl} alt={image.title} />
                  <div className="image-info">
                    <span className="image-title">{image.title}</span>
                    <span className="image-author">{image.author}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="modal-footer">
        <div className="footer-left">
          <button className="btn-more" onClick={() => loadMoreImages()}>
            More
          </button>
          <button className="btn-clear" onClick={() => clearImageResults()}>
            Clear
          </button>
        </div>
        
        <div className="footer-right">
          <input
            type="text"
            placeholder="Image size..."
            value={selectedImageSize || ''}
            onChange={(e) => setSelectedImageSize(e.target.value)}
            className="size-input"
          />
          <button 
            className="btn-copy"
            onClick={() => copyImageUrl(selectedSearchImage)}
          >
            Copy
          </button>
          <button 
            className="btn-insert"
            onClick={() => insertSearchImage(selectedSearchImage)}
          >
            Insert
          </button>
          <button 
            className="btn-download"
            onClick={() => downloadImage(selectedSearchImage)}
          >
            Download
          </button>
        </div>
        
        <button className="btn-close" onClick={closeModal}>
          Close
        </button>
      </div>
    </div>
  );

  // Insert Link Modal
  const renderInsertLinkModal = () => {
    const handleInsertLink = () => {
      if (linkUrl && linkText) {
        const linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
        insertHTML(linkHTML);
        setLinkUrl('');
        setLinkText('');
        closeModal();
      } else {
        alert('Please enter both URL and link text');
      }
    };

    return (
      <div className="insert-link-modal">
        <div className="modal-header">
          <h3>Insert Link</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <label>Link URL:</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="url-input"
              autoFocus
            />
          </div>
          <div className="form-row">
            <label>Link Text:</label>
            <input
              type="text"
              placeholder="Click here"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              className="text-input"
            />
          </div>
        </div>
        <div className="modal-buttons">
          <button className="btn-insert" onClick={handleInsertLink}>Insert Link</button>
          <button className="btn-cancel" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Insert Video Modal
  const renderInsertVideoModal = () => {
    const handleInsertVideo = () => {
      if (videoUrl) {
        const videoHTML = `<video controls style="max-width: 100%; height: auto;"><source src="${videoUrl}" type="video/mp4">Your browser does not support the video tag.</video>`;
        insertHTML(videoHTML);
        setVideoUrl('');
        closeModal();
      } else {
        alert('Please enter a video URL');
      }
    };

    return (
      <div className="insert-video-modal">
        <div className="modal-header">
          <h3>Insert Video</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <label>Video URL:</label>
            <input
              type="url"
              placeholder="https://example.com/video.mp4"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="url-input"
              autoFocus
            />
          </div>
        </div>
        <div className="modal-buttons">
          <button className="btn-insert" onClick={handleInsertVideo}>Insert Video</button>
          <button className="btn-cancel" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Insert Table Modal
  const renderInsertTableModal = () => {
    const handleInsertTable = () => {
      const tableHTML = generateTable(tableRows, tableCols);
      insertHTML(tableHTML);
      setTableRows(3);
      setTableCols(3);
      closeModal();
    };

    return (
      <div className="insert-table-modal">
        <div className="modal-header">
          <h3>Insert Table</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <label>Number of Rows:</label>
            <input
              type="number"
              min="1"
              max="20"
              value={tableRows}
              onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
              className="number-input"
              autoFocus
            />
          </div>
          <div className="form-row">
            <label>Number of Columns:</label>
            <input
              type="number"
              min="1"
              max="10"
              value={tableCols}
              onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
              className="number-input"
            />
          </div>
        </div>
        <div className="modal-buttons">
          <button className="btn-insert" onClick={handleInsertTable}>Insert Table</button>
          <button className="btn-cancel" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Insert Emoji Modal
  const renderInsertEmojiModal = () => {
    const commonEmojis = ['😊', '😂', '😍', '🎉', '👍', '👎', '❤️', '💯', '🔥', '😎', '🤔', '😢', '😡', '😴', '🤗', '👋', '🎯', '⭐', '💪', '🎊'];

    const handleInsertEmoji = (emoji) => {
      formatText('insertText', emoji);
      closeModal();
    };

    const handleInsertCustomEmoji = () => {
      if (customEmoji) {
        formatText('insertText', customEmoji);
        setCustomEmoji('');
        closeModal();
      } else {
        alert('Please enter an emoji');
      }
    };

    return (
      <div className="insert-emoji-modal">
        <div className="modal-header">
          <h3>Insert Emoji</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="emoji-grid">
            {commonEmojis.map((emoji, index) => (
              <button
                key={index}
                className="emoji-btn"
                onClick={() => handleInsertEmoji(emoji)}
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
          <div className="form-row">
            <label>Or enter custom emoji:</label>
            <input
              type="text"
              placeholder="Enter emoji here"
              value={customEmoji}
              onChange={(e) => setCustomEmoji(e.target.value)}
              className="text-input"
              autoFocus
            />
          </div>
        </div>
        <div className="modal-buttons">
          <button className="btn-insert" onClick={handleInsertCustomEmoji}>Insert Custom</button>
          <button className="btn-cancel" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Insert Special Character Modal
  const renderInsertSpecialCharModal = () => {
    const commonChars = ['©', '®', '™', '€', '£', '¥', '¢', '∞', '≠', '≈', '≤', '≥', '±', '×', '÷', '°', '²', '³', '½', '¼', '¾', '‰', '§', '¶', '†', '‡'];

    const handleInsertChar = (char) => {
      formatText('insertText', char);
      closeModal();
    };

    const handleInsertCustomChar = () => {
      if (customChar) {
        formatText('insertText', customChar);
        setCustomChar('');
        closeModal();
      } else {
        alert('Please enter a character');
      }
    };

    return (
      <div className="insert-special-char-modal">
        <div className="modal-header">
          <h3>Insert Special Character</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="char-grid">
            {commonChars.map((char, index) => (
              <button
                key={index}
                className="char-btn"
                onClick={() => handleInsertChar(char)}
                title={char}
              >
                {char}
              </button>
            ))}
          </div>
          <div className="form-row">
            <label>Or enter custom character:</label>
            <input
              type="text"
              placeholder="Enter character here"
              value={customChar}
              onChange={(e) => setCustomChar(e.target.value)}
              className="text-input"
              autoFocus
            />
          </div>
        </div>
        <div className="modal-buttons">
          <button className="btn-insert" onClick={handleInsertCustomChar}>Insert Custom</button>
          <button className="btn-cancel" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Phase 5: Humanizer Modal
  const renderHumanizerModal = () => {
    return (
      <div className="humanizer-modal">
        <div className="modal-header">
          <h3>AI Content Humanizer</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="humanizer-input-section">
            <label>Input Text:</label>
            <textarea
              value={humanizerInputText}
              onChange={(e) => setHumanizerInputText(e.target.value)}
              placeholder="Enter text to humanize..."
              className="humanizer-textarea"
              rows={6}
            />
          </div>

          <div className="humanizer-controls">
            <div className="imperfection-slider">
              <label>Imperfection Level: {imperfectionPercentage}%</label>
              <input
                type="range"
                min="1"
                max="50"
                value={imperfectionPercentage}
                onChange={(e) => setImperfectionPercentage(parseInt(e.target.value))}
                className="slider"
              />
            </div>

            <div className="imperfection-types">
              <button 
                className="toggle-types-btn"
                onClick={() => setShowImperfectionTypes(!showImperfectionTypes)}
              >
                {showImperfectionTypes ? 'Hide' : 'Show'} Imperfection Types
              </button>
              
              {showImperfectionTypes && (
                <div className="imperfection-grid">
                  {imperfectionOptions.map((option) => (
                    <label key={option.key} className="imperfection-option">
                      <input
                        type="checkbox"
                        checked={imperfectionTypes[option.key]}
                        onChange={() => handleImperfectionToggle(option.key)}
                      />
                      <span className="option-label">{option.label}</span>
                      <span className="option-description">{option.description}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="humanizer-actions">
            <button 
              className="humanize-btn"
              onClick={humanizeText}
              disabled={isHumanizing || !humanizerInputText.trim()}
            >
              {isHumanizing ? 'Humanizing...' : 'Humanize Text'}
            </button>
            <button className="clear-btn" onClick={clearAll}>Clear All</button>
          </div>

          {humanizerOutputText && (
            <div className="humanizer-output-section">
              <label>Humanized Output:</label>
              <textarea
                value={humanizerOutputText}
                readOnly
                className="humanizer-textarea"
                rows={6}
              />
              <div className="output-actions">
                <button className="copy-btn" onClick={copyToClipboard}>Copy to Clipboard</button>
                <button className="swap-btn" onClick={swapText}>Swap Text</button>
                <button 
                  className="apply-btn" 
                  onClick={() => {
                    setContent(humanizerOutputText);
                    closeModal();
                  }}
                >
                  Apply to Editor
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Fetch WordPress categories
  const fetchWordPressCategories = async (site) => {
    try {
      console.log('=== STARTING CATEGORY FETCH ===');
      console.log('Site details:', site);
      
      let siteUrl = site.url;
      if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
        siteUrl = 'https://' + siteUrl;
      }
      if (siteUrl.endsWith('/')) {
        siteUrl = siteUrl.slice(0, -1);
      }
      
      console.log('Fetching from URL:', `${siteUrl}/wp-json/wp/v2/categories`);
      
      const response = await fetch(`${siteUrl}/wp-json/wp/v2/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${btoa(`${site.username}:${site.appPassword}`)}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Category fetch response status:', response.status);
      
      if (response.ok) {
        const categories = await response.json();
        console.log('Categories fetched successfully:', categories);
        setWordPressCategories(categories);
        console.log('Categories state updated');
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch categories:', response.status, errorText);
        setWordPressCategories([]);
      }
    } catch (error) {
      console.error('Error fetching WordPress categories:', error);
      setWordPressCategories([]);
    }
  };

  // Fetch WordPress tags
  const fetchWordPressTags = async (site) => {
    try {
      console.log('=== STARTING TAG FETCH ===');
      console.log('Site details:', site);
      
      let siteUrl = site.url;
      if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
        siteUrl = 'https://' + siteUrl;
      }
      if (siteUrl.endsWith('/')) {
        siteUrl = siteUrl.slice(0, -1);
      }
      
      console.log('Fetching from URL:', `${siteUrl}/wp-json/wp/v2/tags`);
      
      const response = await fetch(`${siteUrl}/wp-json/wp/v2/tags`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${btoa(`${site.username}:${site.appPassword}`)}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Tag fetch response status:', response.status);
      
      if (response.ok) {
        const tags = await response.json();
        console.log('Tags fetched successfully:', tags);
        setWordPressTags(tags);
        console.log('Tags state updated');
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch tags:', response.status, errorText);
        setWordPressTags([]);
      }
    } catch (error) {
      console.error('Error fetching WordPress tags:', error);
      setWordPressTags([]);
    }
  };

  // Create new WordPress category
  const createWordPressCategory = async (site, categoryName, description = '') => {
    try {
      console.log('Creating new WordPress category:', categoryName);
      console.log('Site details:', site);
      
      let siteUrl = site.url;
      if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
        siteUrl = 'https://' + siteUrl;
      }
      if (siteUrl.endsWith('/')) {
        siteUrl = siteUrl.slice(0, -1);
      }
      
      const categoryData = {
        name: categoryName,
        description: description
      };
      
      const response = await fetch(`${siteUrl}/wp-json/wp/v2/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${site.username}:${site.appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });
      
      if (response.ok) {
        const newCategory = await response.json();
        console.log('Created new category:', newCategory);
        
        // Refresh categories list
        await fetchWordPressCategories(site);
        
        return { success: true, category: newCategory };
      } else {
        const errorText = await response.text();
        console.error('Failed to create category:', errorText);
        throw new Error(`Failed to create category: ${errorText}`);
      }
    } catch (error) {
      console.error('Error creating WordPress category:', error);
      throw error;
    }
  };

  // Create new WordPress tag
  const createWordPressTag = async (site, tagName, description = '') => {
    try {
      console.log('Creating new WordPress tag:', tagName);
      console.log('Site details:', site);
      
      let siteUrl = site.url;
      if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
        siteUrl = 'https://' + siteUrl;
      }
      if (siteUrl.endsWith('/')) {
        siteUrl = siteUrl.slice(0, -1);
      }
      
      const tagData = {
        name: tagName,
        description: description
      };
      
      const response = await fetch(`${siteUrl}/wp-json/wp/v2/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${site.username}:${site.appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tagData)
      });
      
      if (response.ok) {
        const newTag = await response.json();
        console.log('Created new tag:', newTag);
        
        // Refresh tags list
        await fetchWordPressTags(site);
        
        return { success: true, tag: newTag };
      } else {
        const errorText = await response.text();
        console.error('Failed to create tag:', errorText);
        throw new Error(`Failed to create tag: ${errorText}`);
      }
    } catch (error) {
      console.error('Error creating WordPress tag:', error);
      throw error;
    }
  };

  // Phase 4: WordPress Posting Modal - Tiny & Simple
  const renderWordPressPostModal = () => {
    
    // Debug logging
    console.log('Rendering WordPress modal with:', {
      selectedWordPressSite: selectedWordPressSite?.label,
      wordPressCategories: wordPressCategories.length,
      wordPressPostOptions
    });
    
    if (wordPressLogins.length === 0) {
      return (
        <div className="tiny-wp-modal">
          <div className="modal-header">
            <h3>No WordPress Sites</h3>
            <button className="close-x" onClick={closeModal}>×</button>
          </div>
          <p>Add sites in Settings first.</p>
          <div className="modal-buttons">
            <button onClick={closeModal}>OK</button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="tiny-wp-modal">
        <div className="modal-header">
          <h3>Post to WordPress</h3>
          <button className="close-x" onClick={closeModal}>×</button>
        </div>
        
        <div className="modal-body" onClick={(e) => e.stopPropagation()}>
          <div className="form-row">
            <label>WordPress Site:</label>
            <select 
              value={selectedWordPressSite?.id || ''} 
              onChange={async (e) => {
                const siteId = parseInt(e.target.value);
                const site = wordPressLogins.find(s => s.id === siteId);
                console.log('Site selection changed to:', site);
                
                setSelectedWordPressSite(site);
                
                if (site) {
                  console.log('Fetching categories and tags for site:', site.label);
                  await Promise.all([
                    fetchWordPressCategories(site),
                    fetchWordPressTags(site)
                  ]);
                } else {
                  console.log('No site selected, clearing categories and tags');
                  setWordPressCategories([]);
                  setWordPressTags([]);
                }
              }}
            >
              <option value="">Choose site...</option>
              {wordPressLogins.map(login => (
                <option key={login.id} value={login.id}>
                  {login.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-row">
            <label>Post Title:</label>
            <input
              type="text"
              value={wordPressPostOptions.title || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Title input changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, title: newValue }));
              }}
              placeholder="Enter post title"
            />
          </div>
          
          <div className="form-row">
            <label>Status:</label>
            <select
              value={wordPressPostOptions.status || 'draft'}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Status changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, status: newValue }));
              }}
            >
              <option value="draft">Draft</option>
              <option value="publish">Publish</option>
              <option value="future">Schedule</option>
            </select>
          </div>

          {/* Scheduled Post Fields */}
          {wordPressPostOptions.status === 'future' && (
            <>
              <div className="form-row">
                <label>Schedule Date:</label>
                <input
                  type="date"
                  value={wordPressPostOptions.scheduledDate || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Schedule date changed to:', newValue);
                    setWordPressPostOptions(prev => ({ ...prev, scheduledDate: newValue }));
                  }}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="form-row">
                <label>Schedule Time:</label>
                <input
                  type="time"
                  value={wordPressPostOptions.scheduledTime || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Schedule time changed to:', newValue);
                    setWordPressPostOptions(prev => ({ ...prev, scheduledTime: newValue }));
                  }}
                />
              </div>
              <div className="form-row">
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  Note: Scheduled posts use your local PC time, not WordPress server time.
                </small>
              </div>
            </>
          )}
          
          <div className="form-row">
            <label>Categories:</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={wordPressPostOptions.category || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Category changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, category: newValue }));
              }}
                style={{ flex: 1 }}
            >
              <option value="">Select category... ({wordPressCategories.length} available)</option>
              {wordPressCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
                              <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewCategoryData({ name: '', description: '' });
                    setShowNewCategoryModal(true);
                  }}
                style={{ 
                  padding: '4px 8px', 
                  fontSize: '12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                + New
              </button>
            </div>
          </div>

          <div className="form-row">
            <label>Tags:</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Add tags (comma separated)"
                  value={(wordPressPostOptions.tags || []).join(', ') || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const tags = newValue.split(',').map(tag => tag.trim()).filter(tag => tag);
                    console.log('Tags changed to:', tags);
                    setWordPressPostOptions(prev => ({ ...prev, tags: tags }));
                  }}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewTagData({ name: '', description: '' });
                    setShowNewTagModal(true);
                  }}
                  style={{ 
                    padding: '4px 8px', 
                    fontSize: '12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + New
                </button>
              </div>
              {(wordPressPostOptions.tags || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(wordPressPostOptions.tags || []).map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTags = (wordPressPostOptions.tags || []).filter((_, i) => i !== index);
                          setWordPressPostOptions(prev => ({ ...prev, tags: newTags }));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          marginLeft: '4px'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <label>SEO Description:</label>
            <textarea
              value={wordPressPostOptions.seoDescription || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('SEO description changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, seoDescription: newValue }));
              }}
              placeholder="Enter SEO meta description (optional)"
              rows="2"
              maxLength="160"
            />
            <small style={{ color: '#6c757d', fontSize: '11px' }}>
              {wordPressPostOptions.seoDescription?.length || 0}/160 characters
            </small>
          </div>

          <div className="form-row">
            <label>SEO Keywords:</label>
            <input
              type="text"
              value={wordPressPostOptions.seoKeywords || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('SEO keywords changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, seoKeywords: newValue }));
              }}
              placeholder="Enter SEO keywords (comma separated)"
            />
          </div>
          
          <div className="form-row">
            <label>Allow Comments:</label>
            <select
              value={wordPressPostOptions.allowComments || 'yes'}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Allow comments changed to:', newValue);
                setWordPressPostOptions(prev => ({ ...prev, allowComments: newValue }));
              }}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        
        <div className="modal-buttons">
          <button className="btn-help">Help</button>
          <button 
            className="btn-post"
            disabled={!selectedWordPressSite}
            onClick={async () => {
              if (selectedWordPressSite) {
                const result = await handleWordPressPost(selectedWordPressSite, wordPressPostOptions);
                if (result.success) {
                  closeModal();
                }
              }
            }}
          >
            Post to WordPress
          </button>
          <button className="btn-close" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // New Category Modal
  const renderNewCategoryModal = () => (
    <div className="tiny-wp-modal">
      <div className="modal-header">
        <h3>Create New Category</h3>
        <button className="close-x" onClick={() => setShowNewCategoryModal(false)}>×</button>
      </div>
      
      <div className="modal-body" onClick={(e) => e.stopPropagation()}>
        <div className="form-row">
          <label>Category Name:</label>
          <input
            type="text"
            value={newCategoryData.name}
            onChange={(e) => setNewCategoryData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter category name"
            autoFocus
          />
        </div>
      </div>
      
      <div className="modal-buttons">
        <button 
          className="btn-post"
          disabled={!newCategoryData.name.trim()}
          onClick={async () => {
            try {
              const result = await createWordPressCategory(selectedWordPressSite, newCategoryData.name.trim());
              if (result.success) {
                // Set the new category as selected
                setWordPressPostOptions(prev => ({
                  ...prev,
                  category: result.category.id.toString()
                }));
                alert('Category created successfully!');
                setShowNewCategoryModal(false);
                setNewCategoryData({ name: '', description: '' });
              }
            } catch (error) {
              alert(`Failed to create category: ${error.message}`);
            }
          }}
        >
          Create Category
        </button>
        <button className="btn-close" onClick={() => setShowNewCategoryModal(false)}>Cancel</button>
      </div>
    </div>
  );

  // New Tag Modal
  const renderNewTagModal = () => (
    <div className="tiny-wp-modal">
      <div className="modal-header">
        <h3>Create New Tag</h3>
        <button className="close-x" onClick={() => setShowNewTagModal(false)}>×</button>
      </div>
      
      <div className="modal-body" onClick={(e) => e.stopPropagation()}>
        <div className="form-row">
          <label>Tag Name:</label>
          <input
            type="text"
            value={newTagData.name}
            onChange={(e) => setNewTagData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter tag name"
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        </div>
      </div>
      
      <div className="modal-buttons">
        <button 
          className="btn-post"
          disabled={!newTagData.name.trim()}
          onClick={async () => {
            try {
              const result = await createWordPressTag(selectedWordPressSite, newTagData.name.trim());
              if (result.success) {
                // Add the new tag to the current tags list
                const newTagName = newTagData.name.trim();
                setWordPressPostOptions(prev => ({
                  ...prev,
                  tags: [...(prev.tags || []), newTagName]
                }));
                alert('Tag created successfully!');
                setShowNewTagModal(false);
                setNewTagData({ name: '', description: '' });
              }
            } catch (error) {
              alert(`Failed to create tag: ${error.message}`);
            }
          }}
        >
          Create Tag
        </button>
        <button className="btn-close" onClick={() => setShowNewTagModal(false)}>Cancel</button>
      </div>
    </div>
  );

    // Phase 4: Professional Image Editor Modal
  const renderImageEditorModal = () => (
    <div className="modal-content" style={{ 
      maxWidth: '1200px', 
      maxHeight: '90vh', 
      width: '95vw',
      height: 'auto',
      overflow: 'auto',
      position: 'relative',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      border: '1px solid #ddd'
    }}>
      <div className="image-editor" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        background: 'white',
        borderRadius: '8px'
      }}>
        {/* Header with Actions */}
        <div className="modal-header" style={{ 
          padding: '15px 20px', 
          borderBottom: '1px solid #e0e0e0', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            🎨 Image Editor
          </h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setSelectedEditorImage({
                        src: e.target.result,
                        alt: file.name
                      });
                    };
                    reader.readAsDataURL(file);
                  }
                };
                input.click();
              }}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              📁 Replace Image
            </button>
            <button 
              onClick={() => {
                if (selectedEditorImage) {
                  insertImageIntoEditor(selectedEditorImage);
                  closeModal();
                }
              }}
              style={{
                padding: '8px 16px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#218838'}
              onMouseOut={(e) => e.target.style.background = '#28a745'}
            >
              ✅ Insert Image
            </button>
          <button 
            onClick={closeModal}
            style={{
                padding: '8px 12px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
                borderRadius: '6px',
              cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
            }}
              onMouseOver={(e) => e.target.style.background = '#c82333'}
              onMouseOut={(e) => e.target.style.background = '#dc3545'}
          >
            ✕ Close
          </button>
          </div>
        </div>
        
        {/* Main Content - Side by Side Layout */}
          <div className="image-editor-content" style={{ 
            display: 'flex', 
            flex: 1, 
            overflow: 'hidden',
          minHeight: '400px',
          maxHeight: '70vh',
          background: 'white',
          maxWidth: '100%'
          }}>
            {/* Left Panel - Image Preview */}
            <div className="image-preview-panel" style={{ 
              flex: 1, 
            padding: '20px', 
              display: 'flex', 
              flexDirection: 'column',
              background: '#f8f9fa',
            borderRight: '1px solid #e0e0e0',
            minWidth: '0',
            overflow: 'hidden'
            }}>
            {selectedEditorImage ? (
              <>
                {/* Image Preview Container */}
              <div className="image-preview-container" style={{ 
                flex: 1, 
                border: '2px dashed #ccc', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'white',
                position: 'relative',
                overflow: 'hidden',
                minHeight: '300px',
                marginBottom: '15px'
              }}>
                <img 
                  src={selectedEditorImage.src} 
                  alt={selectedEditorImage.alt} 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain',
                    cursor: 'crosshair'
                  }}
                  draggable={false}
                />
                {/* Crop Overlay */}
                <div 
                  className="crop-overlay" 
                  style={{
                    position: 'absolute',
                    top: '10%',
                    left: '10%',
                      width: '80%',
                      height: '80%',
                      border: '2px dashed #007bff',
                      backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    cursor: 'move',
                    display: 'none',
                    userSelect: 'none'
                  }}
                  onMouseDown={(e) => {
                    if (e.target.className === 'crop-overlay') {
                      const overlay = e.target;
                      const startX = e.clientX;
                      const startY = e.clientY;
                        const startLeft = parseInt(overlay.style.left) || 10;
                        const startTop = parseInt(overlay.style.top) || 10;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                          const containerWidth = overlay.parentElement.offsetWidth;
                          const containerHeight = overlay.parentElement.offsetHeight;
                          const overlayWidth = overlay.offsetWidth;
                          const overlayHeight = overlay.offsetHeight;
                          
                          const newLeft = Math.max(0, Math.min(100 - (overlayWidth / containerWidth * 100), startLeft + (deltaX / containerWidth * 100)));
                          const newTop = Math.max(0, Math.min(100 - (overlayHeight / containerHeight * 100), startTop + (deltaY / containerHeight * 100)));
                          
                          overlay.style.left = `${newLeft}%`;
                          overlay.style.top = `${newTop}%`;
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }
                  }}
                >
                    {/* Resize Handles */}
                  <div 
                      className="resize-handle top-left"
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      left: '-5px',
                      width: '10px',
                      height: '10px',
                      background: '#007bff',
                        border: '1px solid white',
                      cursor: 'nw-resize',
                        borderRadius: '50%'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const overlay = e.target.parentElement;
                      const startX = e.clientX;
                      const startY = e.clientY;
                        const startLeft = parseInt(overlay.style.left) || 10;
                        const startTop = parseInt(overlay.style.top) || 10;
                        const startWidth = overlay.offsetWidth;
                        const startHeight = overlay.offsetHeight;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                          const containerWidth = overlay.parentElement.offsetWidth;
                          const containerHeight = overlay.parentElement.offsetHeight;
                          
                          const newLeft = Math.max(0, Math.min(startLeft + (deltaX / containerWidth * 100), startLeft + startWidth / containerWidth * 100 - 10));
                          const newTop = Math.max(0, Math.min(startTop + (deltaY / containerHeight * 100), startTop + startHeight / containerHeight * 100 - 10));
                          const newWidth = Math.max(10, startWidth - deltaX);
                          const newHeight = Math.max(10, startHeight - deltaY);
                          
                          overlay.style.left = `${newLeft}%`;
                          overlay.style.top = `${newTop}%`;
                          overlay.style.width = `${newWidth}px`;
                          overlay.style.height = `${newHeight}px`;
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    />
                  <div 
                      className="resize-handle top-right"
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      width: '10px',
                      height: '10px',
                      background: '#007bff',
                        border: '1px solid white',
                      cursor: 'ne-resize',
                        borderRadius: '50%'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const overlay = e.target.parentElement;
                      const startX = e.clientX;
                      const startY = e.clientY;
                        const startTop = parseInt(overlay.style.top) || 10;
                        const startWidth = overlay.offsetWidth;
                        const startHeight = overlay.offsetHeight;
                      
                      const handleMouseMove = (moveEvent) => {
                          const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                          const containerWidth = overlay.parentElement.offsetWidth;
                          const containerHeight = overlay.parentElement.offsetHeight;
                          
                          const newTop = Math.max(0, Math.min(startTop + (deltaY / containerHeight * 100), startTop + startHeight / containerHeight * 100 - 10));
                          const newWidth = Math.max(10, startWidth + deltaX);
                          const newHeight = Math.max(10, startHeight - deltaY);
                          
                          overlay.style.top = `${newTop}%`;
                          overlay.style.width = `${newWidth}px`;
                          overlay.style.height = `${newHeight}px`;
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    />
                  <div 
                      className="resize-handle bottom-left"
                    style={{
                      position: 'absolute',
                      bottom: '-5px',
                      left: '-5px',
                      width: '10px',
                      height: '10px',
                      background: '#007bff',
                        border: '1px solid white',
                      cursor: 'sw-resize',
                        borderRadius: '50%'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const overlay = e.target.parentElement;
                      const startX = e.clientX;
                      const startY = e.clientY;
                        const startLeft = parseInt(overlay.style.left) || 10;
                        const startWidth = overlay.offsetWidth;
                        const startHeight = overlay.offsetHeight;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                          const deltaY = moveEvent.clientY - startY;
                          const containerWidth = overlay.parentElement.offsetWidth;
                          const containerHeight = overlay.parentElement.offsetHeight;
                          
                          const newLeft = Math.max(0, Math.min(startLeft + (deltaX / containerWidth * 100), startLeft + startWidth / containerWidth * 100 - 10));
                          const newWidth = Math.max(10, startWidth - deltaX);
                          const newHeight = Math.max(10, startHeight + deltaY);
                          
                          overlay.style.left = `${newLeft}%`;
                          overlay.style.width = `${newWidth}px`;
                          overlay.style.height = `${newHeight}px`;
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    />
                  <div 
                      className="resize-handle bottom-right"
                    style={{
                      position: 'absolute',
                      bottom: '-5px',
                      right: '-5px',
                      width: '10px',
                      height: '10px',
                      background: '#007bff',
                        border: '1px solid white',
                      cursor: 'se-resize',
                        borderRadius: '50%'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const overlay = e.target.parentElement;
                      const startX = e.clientX;
                      const startY = e.clientY;
                        const startWidth = overlay.offsetWidth;
                        const startHeight = overlay.offsetHeight;
                      
                      const handleMouseMove = (moveEvent) => {
                          const deltaX = moveEvent.clientX - startX;
                          const deltaY = moveEvent.clientY - startY;
                          
                          const newWidth = Math.max(10, startWidth + deltaX);
                          const newHeight = Math.max(10, startHeight + deltaY);
                          
                          overlay.style.width = `${newWidth}px`;
                          overlay.style.height = `${newHeight}px`;
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    />
                </div>
              </div>
              
                {/* Image Action Buttons */}
                <div className="image-actions" style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'center',
                  flexWrap: 'wrap'
              }}>
                <button 
                    className="btn btn-primary"
                  style={{
                      padding: '8px 16px',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                  }}
                    onMouseOver={(e) => e.target.style.background = '#0056b3'}
                    onMouseOut={(e) => e.target.style.background = '#007bff'}
                  onClick={() => {
                    const overlay = document.querySelector('.crop-overlay');
                    if (overlay) {
                      overlay.style.display = overlay.style.display === 'none' ? 'block' : 'none';
                    }
                  }}
                >
                  ✂️ Show Crop
                </button>
                <button 
                    className="btn btn-success"
                  style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                  }}
                    onMouseOver={(e) => e.target.style.background = '#218838'}
                    onMouseOut={(e) => e.target.style.background = '#28a745'}
                  onClick={() => {
                    const overlay = document.querySelector('.crop-overlay');
                      const img = document.querySelector('.image-preview-container img');
                      if (overlay && overlay.style.display !== 'none' && img) {
                        // Get the container and overlay dimensions
                        const container = document.querySelector('.image-preview-container');
                        const containerRect = container.getBoundingClientRect();
                        const overlayRect = overlay.getBoundingClientRect();
                        
                        // Calculate crop dimensions relative to the image
                        const cropLeft = Math.max(0, (overlayRect.left - containerRect.left) / containerRect.width * 100);
                        const cropTop = Math.max(0, (overlayRect.top - containerRect.top) / containerRect.height * 100);
                        const cropWidth = Math.min(100 - cropLeft, overlayRect.width / containerRect.width * 100);
                        const cropHeight = Math.min(100 - cropTop, overlayRect.height / containerRect.height * 100);
                        
                        // Apply the crop using CSS clip-path
                        img.style.clipPath = `inset(${cropTop}% ${100 - cropLeft - cropWidth}% ${100 - cropTop - cropHeight}% ${cropLeft}%)`;
                        
                        // Hide the overlay after cropping
                        overlay.style.display = 'none';
                        
                        console.log('Crop applied:', { cropLeft, cropTop, cropWidth, cropHeight });
                        alert('✅ Crop applied successfully!');
                    } else {
                      alert('Please show the crop area first by clicking "Show Crop"');
                    }
                  }}
                >
                  ✂️ Apply Crop
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  style={{
                      padding: '8px 16px',
                    border: '1px solid #6c757d',
                    background: 'white',
                    color: '#6c757d',
                      borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                  }}
                    onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseOut={(e) => e.target.style.background = 'white'}
                  onClick={() => {
                    const img = document.querySelector('.image-preview-container img');
                    if (img) {
                      const currentTransform = img.style.transform || '';
                      const currentRotation = currentTransform.match(/rotate\(([^)]+)\)/);
                      const currentAngle = currentRotation ? parseInt(currentRotation[1]) : 0;
                      const newAngle = currentAngle + 90;
                      img.style.transform = currentTransform.replace(/rotate\([^)]*\)/g, '') + ` rotate(${newAngle}deg)`;
                    }
                  }}
                >
                  🔄 Rotate 90°
                </button>
                <button 
                  className="btn btn-outline-warning"
                  style={{
                      padding: '8px 16px',
                    border: '1px solid #fd7e14',
                    background: 'white',
                    color: '#fd7e14',
                      borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                  }}
                    onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseOut={(e) => e.target.style.background = 'white'}
                  onClick={() => {
                    const img = document.querySelector('.image-preview-container img');
                    if (img) {
                      const currentTransform = img.style.transform || '';
                      img.style.transform = currentTransform.replace(/scaleX\([^)]*\)/g, '') + ' scaleX(-1)';
                    }
                  }}
                >
                  ↔️ Flip Horizontal
                </button>
                  <button 
                    className="btn btn-outline-info"
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #17a2b8',
                      background: 'white',
                      color: '#17a2b8',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseOut={(e) => e.target.style.background = 'white'}
                    onClick={() => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                        img.style.clipPath = 'none';
                        console.log('Crop reset');
                        alert('🔄 Crop reset successfully!');
                      }
                    }}
                  >
                    🔄 Reset Crop
                  </button>
              </div>
              </>
            ) : (
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                border: '2px dashed #ccc',
                borderRadius: '8px',
                background: 'white',
                color: '#666',
                fontSize: '16px'
              }}>
                📁 No image selected. Click "Replace Image" to upload an image.
              </div>
            )}
            </div>

          {/* Right Panel - Controls */}
            <div className="controls-panel" style={{ 
            width: '280px', 
              background: 'white', 
              borderLeft: '1px solid #e0e0e0',
            padding: '15px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            gap: '15px',
            minWidth: '280px',
            maxHeight: '70vh'
            }}>
              {/* Transform Controls */}
              <div className="control-section" style={{ 
                background: '#f8f9fa',
              padding: '12px',
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}>
                <h4 style={{ 
                margin: '0 0 8px 0', 
                  color: '#333', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600'
                }}>
                  📏 Transform
                </h4>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#666', 
                  marginBottom: '10px',
                  lineHeight: '1.3'
                }}>
                  Adjust scale and rotation
                </div>
              
              <div className="control-group" style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontSize: '12px', fontWeight: '500' }}>Scale (%):</label>
                  <input
                    type="range"
                    min="25"
                    max="200"
                    defaultValue="100"
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e0e0e0',
                    outline: 'none',
                    WebkitAppearance: 'none'
                  }}
                    onChange={(e) => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                        const scale = e.target.value / 100;
                      const currentTransform = img.style.transform || '';
                      img.style.transform = currentTransform.replace(/scale\([^)]*\)/g, '') + ` scale(${scale})`;
                      }
                    }}
                  />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '5px' }}>
                  <span>25%</span>
                  <span>100%</span>
                  <span>200%</span>
                  </div>
                </div>
              
              <div className="control-group">
                <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontSize: '12px', fontWeight: '500' }}>Rotation (deg):</label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    defaultValue="0"
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e0e0e0',
                    outline: 'none',
                    WebkitAppearance: 'none'
                  }}
                    onChange={(e) => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                      const rotation = e.target.value;
                      const currentTransform = img.style.transform || '';
                      img.style.transform = currentTransform.replace(/rotate\([^)]*\)/g, '') + ` rotate(${rotation}deg)`;
                      }
                    }}
                  />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '5px' }}>
                  <span>0°</span>
                  <span>180°</span>
                  <span>360°</span>
                  </div>
                </div>
              </div>

            {/* Filters Controls */}
              <div className="control-section" style={{ 
                background: '#f8f9fa',
              padding: '12px',
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}>
                <h4 style={{ 
                margin: '0 0 8px 0', 
                  color: '#333', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600'
                }}>
                ⭐ Filters
                </h4>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#666', 
                  marginBottom: '10px',
                  lineHeight: '1.3'
                }}>
                  Enhance brightness, contrast, and saturation
                </div>
              
              <div className="control-group" style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontSize: '12px', fontWeight: '500' }}>Brightness:</label>
                  <input
                    type="range"
                  min="50"
                  max="150"
                    defaultValue="100"
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e0e0e0',
                    outline: 'none',
                    WebkitAppearance: 'none'
                  }}
                    onChange={(e) => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                      const brightness = e.target.value;
                      img.style.filter = `brightness(${brightness}%) contrast(${img.dataset.contrast || 100}%) saturate(${img.dataset.saturate || 100}%)`;
                      img.dataset.brightness = brightness;
                      }
                    }}
                  />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '5px' }}>
                  <span>50%</span>
                  <span>100%</span>
                  <span>150%</span>
                  </div>
                </div>
              
              <div className="control-group" style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontSize: '12px', fontWeight: '500' }}>Contrast:</label>
                  <input
                    type="range"
                  min="50"
                  max="150"
                    defaultValue="100"
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e0e0e0',
                    outline: 'none',
                    WebkitAppearance: 'none'
                  }}
                    onChange={(e) => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                      const contrast = e.target.value;
                      img.style.filter = `brightness(${img.dataset.brightness || 100}%) contrast(${contrast}%) saturate(${img.dataset.saturate || 100}%)`;
                      img.dataset.contrast = contrast;
                      }
                    }}
                  />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '5px' }}>
                  <span>50%</span>
                  <span>100%</span>
                  <span>150%</span>
                  </div>
                </div>
              
              <div className="control-group">
                <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontSize: '12px', fontWeight: '500' }}>Saturation:</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    defaultValue="100"
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e0e0e0',
                    outline: 'none',
                    WebkitAppearance: 'none'
                  }}
                    onChange={(e) => {
                      const img = document.querySelector('.image-preview-container img');
                      if (img) {
                      const saturate = e.target.value;
                      img.style.filter = `brightness(${img.dataset.brightness || 100}%) contrast(${img.dataset.contrast || 100}%) saturate(${saturate}%)`;
                      img.dataset.saturate = saturate;
                      }
                    }}
                  />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '5px' }}>
                  <span>0%</span>
                  <span>100%</span>
                  <span>200%</span>
                  </div>
                </div>
              </div>
                </div>
              </div>
      </div>
    </div>
  );

  // Phase 4: Export & Integration functions
  
  // PDF Export handler
  const handlePDFExport = async () => {
    if (!content.trim()) {
      alert('No content to export. Please add some content first.');
      return;
    }
    
    try {
      const filename = pdfExportOptions.filename || 'document';
      const result = await exportToPDF(content, filename);
      
      if (result.success) {
        alert('PDF export initiated! Check your print dialog to save as PDF.');
      } else {
        alert(`PDF export failed: ${result.error}`);
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert(`PDF export failed: ${error.message}`);
    }
  };
  
  // WordPress posting handler
  const handleWordPressPost = async (selectedLogin, postOptions) => {
    console.log('=== WORDPRESS POSTING START ===');
    console.log('Selected login:', selectedLogin);
    console.log('Post options:', postOptions);
    console.log('Content length:', content.length);
    
    if (!content.trim()) {
      alert('No content to post. Please add some content first.');
      return { success: false, error: 'No content to post' };
    }
    
    if (!selectedLogin || !selectedLogin.url || !selectedLogin.username || !selectedLogin.appPassword) {
      alert('Invalid WordPress credentials. Please check your settings.');
      return { success: false, error: 'Invalid credentials' };
    }
    
    try {
      // Handle scheduled posting
      let postDate = null;
      if (postOptions.status === 'future' && postOptions.scheduledDate && postOptions.scheduledTime) {
        const scheduledDateTime = new Date(`${postOptions.scheduledDate}T${postOptions.scheduledTime}`);
        if (scheduledDateTime > new Date()) {
          postDate = scheduledDateTime.toISOString();
        }
      }

      // Handle tags - create new ones if needed
      let tagIds = [];
      console.log('Processing tags:', postOptions.tags);
      console.log('Available WordPress tags:', wordPressTags);
      
      if (postOptions.tags && Array.isArray(postOptions.tags) && postOptions.tags.length > 0) {
        for (const tagName of postOptions.tags) {
          try {
            console.log(`Processing tag: "${tagName}"`);
            // Check if tag already exists
            const existingTag = wordPressTags.find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
            if (existingTag) {
              console.log(`Found existing tag: ${existingTag.name} (ID: ${existingTag.id})`);
              tagIds.push(existingTag.id);
            } else {
              console.log(`Creating new tag: "${tagName}"`);
              // Create new tag
              const newTagResult = await createWordPressTag(selectedLogin, tagName);
              if (newTagResult.success) {
                console.log(`Successfully created tag: ${newTagResult.tag.name} (ID: ${newTagResult.tag.id})`);
                tagIds.push(newTagResult.tag.id);
              }
            }
          } catch (error) {
            console.error(`Failed to create tag "${tagName}":`, error);
          }
        }
      }
      
      console.log('Final tag IDs:', tagIds);

      // Prepare post data
      const postData = {
        title: postOptions.title || content.substring(0, 50) + '...',
        content: content,
        status: postOptions.status || 'draft',
        excerpt: postOptions.seoDescription || postOptions.excerpt || content.substring(0, 160) + '...',
        categories: postOptions.category ? [parseInt(postOptions.category)] : [],
        tags: tagIds
      };

      // Add SEO meta fields if provided
      if (postOptions.seoDescription || postOptions.seoKeywords) {
        postData.meta = {};
        if (postOptions.seoDescription) {
          postData.meta._yoast_wpseo_metadesc = postOptions.seoDescription;
        }
        if (postOptions.seoKeywords) {
          postData.meta._yoast_wpseo_focuskw = postOptions.seoKeywords;
        }
      }

      // Add scheduled date if set
      if (postDate) {
        postData.date = postDate;
      }
      
      // Ensure URL has proper format
      let postUrl = selectedLogin.url;
      if (!postUrl.startsWith('http://') && !postUrl.startsWith('https://')) {
        postUrl = 'https://' + postUrl;
      }
      if (postUrl.endsWith('/')) {
        postUrl = postUrl.slice(0, -1);
      }
      
      console.log('Posting to WordPress:', postUrl);
      console.log('Post data:', postData);
      
      // Make API request to WordPress
      const response = await fetch(`${postUrl}/wp-json/wp/v2/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${selectedLogin.username}:${selectedLogin.appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });
      
      console.log('WordPress response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('WordPress post result:', result);
        const statusText = postData.status === 'future' ? 'Scheduled' : postData.status;
        const dateText = postData.date ? `\nScheduled for: ${new Date(postData.date).toLocaleString()}` : '';
        const tagsText = tagIds.length > 0 ? `\nTags: ${tagIds.length} applied` : '';
        
        alert(`✅ Post ${statusText.toLowerCase()} successfully!\n\nTitle: ${postData.title}\nStatus: ${statusText}${dateText}${tagsText}\nPost ID: ${result.id}\n\nView post: ${result.link}`);
        return { success: true, postId: result.id, link: result.link };
      } else {
        const errorText = await response.text();
        console.error('WordPress API error response:', errorText);
        let errorMessage = `WordPress API Error (${response.status}): ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `WordPress API Error: ${errorData.message}`;
          }
        } catch (e) {
          // If we can't parse JSON, use the raw text
          if (errorText) {
            errorMessage = `WordPress API Error: ${errorText}`;
          }
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('WordPress posting error:', error);
      alert(`❌ Failed to post to WordPress: ${error.message}`);
      return { success: false, error: error.message };
    }
  };
  
  // Make images resizable in the editor
  const makeImageResizable = (img) => {
    console.log('makeImageResizable called for image:', img);
    let isResizing = false;
    let resizeHandle = null;
    let startX, startY, startWidth, startHeight;
    let originalAspectRatio;
    
    // Add visual feedback
    img.style.cursor = 'pointer';
    img.style.userSelect = 'none';
    img.style.border = '2px solid transparent';
    img.style.transition = 'border-color 0.2s ease';
    img.style.position = 'relative';
    img.style.display = 'inline-block';
    
    // Store original aspect ratio
    originalAspectRatio = img.naturalWidth / img.naturalHeight;
    
    // Create resize handles
    const createResizeHandles = () => {
      const handles = ['nw', 'ne', 'sw', 'se'];
      handles.forEach(pos => {
        const handle = document.createElement('div');
        handle.className = `resize-handle ${pos}`;
        handle.style.cssText = `
          position: absolute;
          width: 12px;
          height: 12px;
          background: #007bff;
          border: 2px solid white;
          border-radius: 50%;
          cursor: ${pos === 'nw' ? 'nw-resize' : pos === 'ne' ? 'ne-resize' : pos === 'sw' ? 'sw-resize' : 'se-resize'};
          z-index: 10000;
          display: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          pointer-events: auto;
        `;
        
        // Position handles relative to the image
        switch(pos) {
          case 'nw': handle.style.top = '0px'; handle.style.left = '0px'; break;
          case 'ne': handle.style.top = '0px'; handle.style.right = '0px'; break;
          case 'sw': handle.style.bottom = '0px'; handle.style.left = '0px'; break;
          case 'se': handle.style.bottom = '0px'; handle.style.right = '0px'; break;
        }
        
        handle.onmousedown = (e) => {
          e.preventDefault();
          e.stopPropagation();
          isResizing = true;
          resizeHandle = pos;
          startX = e.clientX;
          startY = e.clientY;
          startWidth = img.offsetWidth;
          startHeight = img.offsetHeight;
        };
        
              // Append to the image's parent container
      const parent = img.parentElement;
      if (parent && editorRef.current && editorRef.current.contains(img)) {
        parent.style.position = 'relative';
        parent.appendChild(handle);
        
        // Position handles correctly at image corners using absolute positioning
        const rect = img.getBoundingClientRect();
        const parentRect = img.parentElement.getBoundingClientRect();
        
        switch(pos) {
          case 'nw': 
            handle.style.top = '-6px'; 
            handle.style.left = '-6px'; 
            break;
          case 'ne': 
            handle.style.top = '-6px'; 
            handle.style.left = `${img.offsetWidth - 6}px`; 
            break;
          case 'sw': 
            handle.style.top = `${img.offsetHeight - 6}px`; 
            handle.style.left = '-6px'; 
            break;
          case 'se': 
            handle.style.top = `${img.offsetHeight - 6}px`; 
            handle.style.left = `${img.offsetWidth - 6}px`; 
            break;
        }
      }
      });
    };
    
    // Show/hide resize handles
    const toggleResizeHandles = (show) => {
      const handles = img.parentElement.querySelectorAll('.resize-handle');
      console.log('toggleResizeHandles called, show:', show, 'handles found:', handles.length);
      handles.forEach(handle => {
        handle.style.display = show ? 'block' : 'none';
        console.log('Handle display set to:', handle.style.display);
      });
    };
    
    const handleMouseMove = (e) => {
      if (isResizing) {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        
        let newWidth = startWidth;
        let newHeight = startHeight;
        
        // Calculate new dimensions based on handle
        switch(resizeHandle) {
          case 'se':
            newWidth = Math.max(50, startWidth + deltaX);
            newHeight = newWidth / originalAspectRatio; // Maintain aspect ratio
            break;
          case 'sw':
            newWidth = Math.max(50, startWidth - deltaX);
            newHeight = newWidth / originalAspectRatio; // Maintain aspect ratio
            break;
          case 'ne':
            newWidth = Math.max(50, startWidth + deltaX);
            newHeight = newWidth / originalAspectRatio; // Maintain aspect ratio
            break;
          case 'nw':
            newWidth = Math.max(50, startWidth - deltaX);
            newHeight = newWidth / originalAspectRatio; // Maintain aspect ratio
            break;
        }
        
        // Ensure minimum size
        if (newWidth < 50) newWidth = 50;
        if (newHeight < 50) newHeight = 50;
        
        img.style.width = `${newWidth}px`;
        img.style.height = `${newHeight}px`;
      }
    };
    
    const handleMouseUp = () => {
      if (isResizing) {
        isResizing = false;
        resizeHandle = null;
      }
    };
    
    // Add event listeners to this specific image
    img.addEventListener('mousemove', handleMouseMove);
    img.addEventListener('mouseup', handleMouseUp);
    
                  // Click to select image
              img.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Image clicked, selecting...');
                
                // Clear any existing text selection
                window.getSelection().removeAllRanges();
                
                // Create a range for the image
                const range = document.createRange();
                range.selectNode(img);
                window.getSelection().addRange(range);
                
                // Show resize handles
                img.style.border = '2px solid #007bff';
                toggleResizeHandles(true);
                
                // Store selected image for deletion
                setSelectedEditorImage(img);
              };

              // Hover effects
              img.onmouseenter = () => {
                console.log('Image hover enter');
                if (!isResizing) {
                  img.style.border = '2px solid #007bff';
                  toggleResizeHandles(true);
                  console.log('Resize handles should be visible');
                }
              };

              img.onmouseleave = () => {
                console.log('Image hover leave');
                if (!isResizing) {
                  img.style.border = '2px solid transparent';
                  toggleResizeHandles(false);
                }
              };
    
    // Initialize resize handles
    createResizeHandles();
    
    // Debug: Make handles visible for testing
    setTimeout(() => {
      console.log('Making handles visible for testing...');
      toggleResizeHandles(true);
    }, 1000);
  };
  
  // Image management functions
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          name: file.name,
          url: e.target.result,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString()
        };
        setImageLibrary(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const insertImageIntoEditor = (image) => {
    if (showSourceCode) {
      // Insert HTML for source code mode
      const imageHTML = `<img src="${image.url}" alt="${image.name}" style="max-width: 100%; height: auto;" />`;
      insertHTML(imageHTML);
    } else {
      // Insert image using execCommand for rich editor
      document.execCommand('insertImage', false, image.url);
      
      // Make the newly inserted image resizable
      setTimeout(() => {
        const images = editorRef.current.querySelectorAll('img');
        const lastImage = images[images.length - 1];
        if (lastImage) {
          makeImageResizable(lastImage);
        }
      }, 100);
    }
    setSelectedImage(null);
  };

  // Insert image from URL
  // Simple image URL validation function
  const isValidImageUrl = (url) => {
    if (!url || !url.trim()) return false;
    try {
      // Remove query params and hash, then check for image extensions
      const cleanUrl = url.split('?')[0].split('#')[0].trim().toLowerCase();
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
      return validExtensions.some(ext => cleanUrl.endsWith(ext));
    } catch (e) {
      return false;
    }
  };

  // Completely new, simple image insertion from URL
  const insertImageFromUrl = (url) => {
    console.log('=== NEW insertImageFromUrl called with:', url);
    
    // Validate URL
    if (!url || !url.trim()) {
      alert('Please enter a valid image URL');
      return;
    }
    
    if (!isValidImageUrl(url)) {
      alert('Please enter a valid image URL (must end with .jpg, .png, .gif, etc.)');
      return;
    }
    
    console.log('URL validation passed, inserting image...');
    
    // Create image HTML
    const imageHTML = `<img src="${url.trim()}" alt="Image" style="max-width: 100%; height: auto;" />`;
    
    // Insert at current cursor position
    if (!editorRef.current) return;
    
    // Focus the editor first
    editorRef.current.focus();
    
          // Use selection-based insertion for precise cursor positioning
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      // Create image element
      const imgElement = document.createElement('img');
      imgElement.src = url.trim();
      imgElement.alt = 'Inserted image';
      imgElement.style.maxWidth = '100%';
      imgElement.style.height = 'auto';
      
      // Insert at cursor position
      range.deleteContents();
      range.insertNode(imgElement);
      range.collapse(false);
      
      // Update selection
      selection.removeAllRanges();
      selection.addRange(range);
    
    // Update React state
    setContent(editorRef.current.innerHTML);
    
    // Make the newly inserted image resizable
    setTimeout(() => {
      const images = editorRef.current.querySelectorAll('img');
      const lastImage = images[images.length - 1];
      if (lastImage) {
        makeImageResizable(lastImage);
      }
    }, 100);
    
    // Clear URL and close modal
    setImageUrl('');
    closeModal();
    
    console.log('=== Image insertion completed');
  };

  // Completely new, simple file upload handler
  const handleInsertImageUpload = (event) => {
    console.log('=== NEW handleInsertImageUpload called');
    
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Please select a valid image file');
      return;
    }
    
    const file = imageFiles[0];
    console.log('Selected file:', file.name, file.type);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      console.log('Image URL generated, inserting...');
      
      // Create image HTML
      const imageHTML = `<img src="${imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" />`;
      
      // Insert at current cursor position using execCommand
      if (!editorRef.current) return;
      
      // Focus the editor first
      editorRef.current.focus();
      
      // Use selection-based insertion for precise cursor positioning
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      // Create image element
      const imgElement = document.createElement('img');
      imgElement.src = imageUrl;
      imgElement.alt = file.name;
      imgElement.style.maxWidth = '100%';
      imgElement.style.height = 'auto';
      
      // Insert at cursor position
      range.deleteContents();
      range.insertNode(imgElement);
      range.collapse(false);
      
      // Update selection
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Update React state
      setContent(editorRef.current.innerHTML);
      
      // Make the newly inserted image resizable
      setTimeout(() => {
        const images = editorRef.current.querySelectorAll('img');
        const lastImage = images[images.length - 1];
        if (lastImage) {
          makeImageResizable(lastImage);
        }
      }, 100);
      
      // Close modal
      closeModal();
      
      console.log('=== File upload insertion completed');
    };
    
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };
    
    reader.readAsDataURL(file);
  };

  // Search images from online services
  const searchImages = async (query) => {
    if (!query.trim()) {
      alert('Please enter a search term');
      return;
    }

    try {
      let apiUrl = '';
      const settings = JSON.parse(localStorage.getItem('imageApiSettings') || '{}');
      
      switch (selectedImageSource) {
        case 'pexels':
          const pexelsKey = settings.pexelsApiKey || '';
          if (!pexelsKey) {
            alert('Please add Pexels API key in Settings');
            return;
          }
          apiUrl = `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}&per_page=20`;
          break;
        case 'pixabay':
          const pixabayKey = settings.pixabayApiKey || '';
          if (!pixabayKey) {
            alert('Please add Pixabay API key in Settings');
            return;
          }
          apiUrl = `https://pixabay.com/api/?key=${pixabayKey}&q=${encodeURIComponent(query)}&image_type=photo&per_page=20`;
          break;
        case 'unsplash':
          const unsplashKey = settings.unsplashApiKey || '';
          if (!unsplashKey) {
            alert('Please add Unsplash API key in Settings');
            return;
          }
          apiUrl = `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=20`;
          break;
        case 'iconfinder':
          const iconfinderKey = settings.iconfinderApiKey || '';
          if (!iconfinderKey) {
            alert('Please add Iconfinder API key in Settings');
            return;
          }
          apiUrl = `https://api.iconfinder.com/v4/icons/search?query=${encodeURIComponent(query)}&count=20`;
          break;
        default:
          alert('Invalid image source selected');
          return;
      }

      const headers = {};
      if (selectedImageSource === 'pexels') {
        headers['Authorization'] = settings.pexelsApiKey;
      } else if (selectedImageSource === 'unsplash') {
        headers['Authorization'] = `Client-ID ${settings.unsplashApiKey}`;
      } else if (selectedImageSource === 'iconfinder') {
        headers['Authorization'] = `Bearer ${settings.iconfinderApiKey}`;
      }

      const response = await fetch(apiUrl, { headers });
      const data = await response.json();

      let results = [];
      switch (selectedImageSource) {
        case 'pexels':
          results = data.photos?.map(photo => ({
            id: photo.id,
            title: photo.alt || 'Pexels Image',
            author: photo.photographer,
            previewUrl: photo.src.medium,
            fullUrl: photo.src.large2x,
            downloadUrl: photo.src.original
          })) || [];
          break;
        case 'pixabay':
          results = data.hits?.map(hit => ({
            id: hit.id,
            title: hit.tags || 'Pixabay Image',
            author: hit.user,
            previewUrl: hit.webformatURL,
            fullUrl: hit.largeImageURL,
            downloadUrl: hit.largeImageURL
          })) || [];
          break;
        case 'unsplash':
          results = data.results?.map(photo => ({
            id: photo.id,
            title: photo.alt_description || 'Unsplash Image',
            author: photo.user.name,
            previewUrl: photo.urls.small,
            fullUrl: photo.urls.regular,
            downloadUrl: photo.urls.full
          })) || [];
          break;
        case 'iconfinder':
          results = data.icons?.map(icon => ({
            id: icon.icon_id,
            title: icon.tags.join(', ') || 'Icon',
            author: icon.published_by.name,
            previewUrl: icon.raster_sizes[0]?.formats[0]?.preview_url,
            fullUrl: icon.raster_sizes[0]?.formats[0]?.download_url,
            downloadUrl: icon.raster_sizes[0]?.formats[0]?.download_url
          })) || [];
          break;
      }

      setImageSearchResults(results);
    } catch (error) {
      console.error('Error searching images:', error);
      alert('Error searching images. Please check your API keys and try again.');
    }
  };

  // Get trending images
  const getTrendingImages = async () => {
    try {
      const settings = JSON.parse(localStorage.getItem('imageApiSettings') || '{}');
      let apiUrl = '';
      
      switch (selectedImageSource) {
        case 'pexels':
          apiUrl = 'https://api.pexels.com/v1/curated?per_page=20';
          break;
        case 'pixabay':
          apiUrl = `https://pixabay.com/api/?key=${settings.pixabayApiKey}&image_type=photo&order=popular&per_page=20`;
          break;
        case 'unsplash':
          apiUrl = 'https://api.unsplash.com/photos?order_by=popular&per_page=20';
          break;
        default:
          alert('Trending not available for this source');
          return;
      }

      const headers = {};
      if (selectedImageSource === 'pexels') {
        headers['Authorization'] = settings.pexelsApiKey;
      } else if (selectedImageSource === 'unsplash') {
        headers['Authorization'] = `Client-ID ${settings.unsplashApiKey}`;
      }

      const response = await fetch(apiUrl, { headers });
      const data = await response.json();

      // Process results similar to searchImages
      let results = [];
      // ... (similar processing logic as above)
      setImageSearchResults(results);
    } catch (error) {
      console.error('Error getting trending images:', error);
      alert('Error loading trending images.');
    }
  };

  // Load more images
  const loadMoreImages = () => {
    // Implementation for pagination
    console.log('Loading more images...');
  };

  // Clear image results
  const clearImageResults = () => {
    setImageSearchResults([]);
    setSelectedSearchImage(null);
    setImageSearchQuery('');
  };

  // Copy image URL
  const copyImageUrl = (image) => {
    if (image) {
      navigator.clipboard.writeText(image.fullUrl);
      alert('Image URL copied to clipboard!');
    }
  };

  // Insert search image into editor
  const insertSearchImage = (image) => {
    if (image) {
        const imageHTML = `<img src="${image.fullUrl}" alt="${image.title}" style="max-width: 100%; height: auto;" />`;
      
      // Insert at current cursor position
      if (!editorRef.current) return;
      
      // Focus the editor first
      editorRef.current.focus();
      
      // Use selection-based insertion for precise cursor positioning
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      // Create image element
      const imgElement = document.createElement('img');
      imgElement.src = image.fullUrl;
      imgElement.alt = image.title;
      imgElement.style.maxWidth = '100%';
      imgElement.style.height = 'auto';
      
      // Insert at cursor position
      range.deleteContents();
      range.insertNode(imgElement);
      range.collapse(false);
      
      // Update selection
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Update React state
      setContent(editorRef.current.innerHTML);
      
      // Make the newly inserted image resizable
      setTimeout(() => {
        const images = editorRef.current.querySelectorAll('img');
        const lastImage = images[images.length - 1];
        if (lastImage) {
          makeImageResizable(lastImage);
        }
      }, 100);
      
      closeModal();
    }
  };

  // Download image
  const downloadImage = (image) => {
    if (image) {
      const link = document.createElement('a');
      link.href = image.downloadUrl;
      link.download = `${image.title}.jpg`;
      link.click();
    }
  };

  // Generate PowerPoint from AI content
  const generatePowerPoint = useCallback(async (content, fileName) => {
    if (!PptxGenJS) {
      throw new Error('pptxgenjs library not available. Please install it: npm install pptxgenjs');
    }

    const pptx = new PptxGenJS.default();
    
    // Set presentation properties
    pptx.author = 'Fiddyscript AI Content Assistant';
    pptx.company = 'Fiddyscript';
    pptx.title = fileName.replace('.pptx', '').replace(/_/g, ' ');
    pptx.subject = 'AI Generated Presentation';
    
    // Parse content to extract slides
    const contentStr = typeof content === 'string' ? content : String(content || '');
    
    // Split content into potential slides
    // Look for patterns like: Slide 1:, # Title, ## Title, or numbered sections
    const lines = contentStr.split('\n');
    let slides = [];
    let currentSlide = { title: '', content: [] };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this is a slide title/header
      if (line.match(/^(?:Slide\s+\d+|#+\s+|##\s+|###\s+|\d+[\.\)]\s+[A-Z])/i)) {
        // Save previous slide if it has content
        if (currentSlide.title || currentSlide.content.length > 0) {
          slides.push({ ...currentSlide });
        }
        
        // Start new slide
        currentSlide = {
          title: line.replace(/^(?:Slide\s+\d+[:\-]?\s*|#+\s+|\d+[\.\)]\s*)/i, '').trim(),
          content: []
        };
      } else if (line.length > 0) {
        // Add content to current slide
        currentSlide.content.push(line);
      }
    }
    
    // Add the last slide
    if (currentSlide.title || currentSlide.content.length > 0) {
      slides.push(currentSlide);
    }
    
    // If no slides were detected, create slides from paragraphs
    if (slides.length === 0) {
      const paragraphs = contentStr.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      slides = paragraphs.map((para, idx) => ({
        title: `Slide ${idx + 1}`,
        content: para.split('\n').filter(l => l.trim().length > 0)
      }));
    }
    
    // Ensure we have at least one slide
    if (slides.length === 0) {
      slides = [{
        title: pptx.title || 'Presentation',
        content: contentStr.split('\n').filter(l => l.trim().length > 0)
      }];
    }
    
    // Create slides
    slides.forEach((slideData, index) => {
      const slide = pptx.addSlide();
      
      // Add title
      slide.addText(slideData.title, {
        x: 0.5,
        y: 0.5,
        w: 9,
        h: 0.8,
        fontSize: 32,
        bold: true,
        color: '363636',
        align: 'left'
      });
      
      // Process content
      const bulletPoints = [];
      const paragraphs = [];
      
      slideData.content.forEach(line => {
        const trimmed = line.trim();
        // Check if it's a bullet point
        if (trimmed.match(/^[-•*]\s+/) || trimmed.match(/^\d+[\.\)]\s+/)) {
          bulletPoints.push(trimmed.replace(/^[-•*\d+\.\)]\s+/, ''));
        } else if (trimmed.length > 0) {
          paragraphs.push(trimmed);
        }
      });
      
      // Add bullet points if available
      if (bulletPoints.length > 0) {
        slide.addText(bulletPoints, {
          x: 0.5,
          y: 1.5,
          w: 9,
          h: 5,
          fontSize: 18,
          bullet: true,
          color: '363636',
          align: 'left',
          valign: 'top'
        });
      } else if (paragraphs.length > 0) {
        // Add paragraphs as text
        slide.addText(paragraphs.join('\n'), {
          x: 0.5,
          y: 1.5,
          w: 9,
          h: 5,
          fontSize: 18,
          color: '363636',
          align: 'left',
          valign: 'top'
        });
      }
    });
    
    // Generate the PowerPoint file
    return pptx;
  }, []);

  // Download function for file references in chat
  const downloadFile = useCallback(async (fileName, fileType = 'pptx', aiContent = null) => {
    try {
      // Generate filename with timestamp if needed
      const timestamp = Date.now();
      let finalFileName = fileName;
      
      // If it's a sandbox URL, extract the filename
      if (fileName.startsWith('sandbox:/')) {
        finalFileName = fileName.replace('sandbox:/', '').trim();
      }
      
      // Ensure filename has proper extension
      if (!finalFileName.includes('.')) {
        finalFileName = `${finalFileName}.${fileType}`;
      }
      
      // For PowerPoint files, generate actual PPTX
      if (fileType === 'pptx' || finalFileName.toLowerCase().endsWith('.pptx')) {
        try {
          // Try to get AI content from the current conversation
          let contentToUse = aiContent;
          
          // If no content provided, try to get it from the last assistant message
          if (!contentToUse && chatMessages && chatMessages.length > 0) {
            const lastAssistantMsg = [...chatMessages].reverse().find(msg => msg.role === 'assistant');
            if (lastAssistantMsg) {
              contentToUse = lastAssistantMsg.content;
            }
          }
          
          // If still no content, use a default message
          if (!contentToUse) {
            contentToUse = `Slide 1: ${finalFileName.replace('.pptx', '')}\n\nThis presentation was generated by Fiddyscript AI Content Assistant.\n\nPlease provide more detailed content for a better presentation.`;
          }
          
          // Generate PowerPoint
          const pptx = await generatePowerPoint(contentToUse, finalFileName);
          
          // Save the file
          await pptx.writeFile({ fileName: finalFileName });
          
          alert(`✅ PowerPoint file "${finalFileName}" generated successfully!`);
        } catch (pptxError) {
          console.error('Error generating PowerPoint:', pptxError);
          
          // Fallback: Create a placeholder text file
          const content = `Error generating PowerPoint file: ${pptxError.message}\n\n` +
            `File: ${finalFileName}\n` +
            `Original reference: ${fileName}\n\n` +
            `Please ensure pptxgenjs is installed: npm install pptxgenjs`;
          
          const blob = new Blob([content], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = finalFileName.replace('.pptx', '.txt');
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
          
          alert(`⚠️ Could not generate PowerPoint. Error: ${pptxError.message}`);
        }
      } else {
        // For other file types, create a placeholder
        const content = `Placeholder file for: ${finalFileName}\n\nOriginal reference: ${fileName}`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  }, [chatMessages, generatePowerPoint]);

  // Download function for generated images in chat
  const downloadGeneratedImage = useCallback(async (imageUrl, alt = 'Generated Image') => {
    try {
      // Generate filename with timestamp
      const timestamp = Date.now();
      const extension = imageUrl.includes('data:image/') 
        ? imageUrl.match(/data:image\/([^;]+)/)?.[1] || 'png'
        : 'png';
      const fileName = `ai-generated-image-${timestamp}.${extension}`;

      if (imageUrl.startsWith('data:image/')) {
        // Base64 image - convert directly to blob without fetch (to avoid CSP issues)
        // Extract base64 data and mime type
        const matches = imageUrl.match(/^data:image\/([^;]+);base64,(.+)$/);
        if (!matches) {
          throw new Error('Invalid base64 image format');
        }
        
        const mimeType = matches[1];
        const base64Data = matches[2];
        
        // Convert base64 to binary
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${mimeType}` });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        // External URL - fetch and download
        fetch(imageUrl)
          .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.blob();
          })
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }, 100);
          })
          .catch(error => {
            console.error('Error downloading image:', error);
            // Fallback: open in new tab
            window.open(imageUrl, '_blank');
          });
      }
    } catch (error) {
      console.error('Error in downloadGeneratedImage:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  }, []);

  // Make download functions available globally for inline handlers
  useEffect(() => {
    window.downloadGeneratedImage = downloadGeneratedImage;
    window.downloadFile = downloadFile;
    return () => {
      delete window.downloadGeneratedImage;
      delete window.downloadFile;
    };
  }, [downloadGeneratedImage, downloadFile]);

  // Set up event delegation for download buttons - use a more specific container
  const chatMessagesRef = useRef(null);
  
  useEffect(() => {
    const handleDownloadClick = (e) => {
      // Check if clicked element is a download button or inside one
      const downloadImageButton = e.target.closest('.chat-download-image-button');
      const downloadFileButton = e.target.closest('.chat-download-file-button');
      
      if (downloadImageButton) {
        // Aggressively prevent all default behaviors and propagation
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Cancel the event completely
        if (e.cancelable) {
          e.cancelBubble = true;
        }
        
        let imageUrl = downloadImageButton.getAttribute('data-image-url');
        const imageAlt = downloadImageButton.getAttribute('data-image-alt') || 'Generated Image';
        
        // Decode the URL if it was encoded
        if (imageUrl) {
          try {
            imageUrl = decodeURIComponent(imageUrl);
          } catch (e) {
            // If decoding fails, use the original
            console.warn('Failed to decode image URL, using original');
          }
        }
        
        // Execute download immediately
        if (imageUrl) {
          downloadGeneratedImage(imageUrl, imageAlt).catch(error => {
            console.error('Error downloading image:', error);
          });
        } else {
          console.error('No image URL found in download button');
        }
        
        return false; // Additional way to prevent default
      } else if (downloadFileButton) {
        // Handle file download
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Cancel the event completely
        if (e.cancelable) {
          e.cancelBubble = true;
        }
        
        const fileRef = downloadFileButton.getAttribute('data-file-ref');
        const fileName = downloadFileButton.getAttribute('data-file-name');
        const fileType = downloadFileButton.getAttribute('data-file-type') || 'pptx';
        
        if (fileRef) {
          const decodedRef = decodeURIComponent(fileRef);
          const decodedName = decodeURIComponent(fileName);
          
          // Try to find the message content that contains this file reference
          let aiContent = null;
          if (chatMessages && chatMessages.length > 0) {
            // Find the assistant message that contains this file reference
            const messageWithFile = [...chatMessages].reverse().find(msg => 
              msg.role === 'assistant' && 
              msg.content && 
              (typeof msg.content === 'string' ? msg.content : String(msg.content || '')).includes(decodedRef)
            );
            if (messageWithFile) {
              aiContent = typeof messageWithFile.content === 'string' 
                ? messageWithFile.content 
                : String(messageWithFile.content || '');
            }
          }
          
          downloadFile(decodedRef, fileType, aiContent).catch(error => {
            console.error('Error downloading file:', error);
          });
        } else {
          console.error('No file reference found in download button');
        }
        
        return false; // Additional way to prevent default
      }
    };

    // Use the ref if available, otherwise query for the container
    const messagesContainer = chatMessagesRef.current || document.querySelector('.ai-chat-messages');
    if (messagesContainer) {
      // Use capture phase with high priority
      messagesContainer.addEventListener('click', handleDownloadClick, { capture: true, passive: false });
      
      return () => {
        messagesContainer.removeEventListener('click', handleDownloadClick, { capture: true });
      };
    }
    
    // Fallback to document if container not found
    document.addEventListener('click', handleDownloadClick, { capture: true, passive: false });
    return () => {
      document.removeEventListener('click', handleDownloadClick, { capture: true });
    };
  }, [downloadGeneratedImage, downloadFile]);

  // Sidebar tool functions
  // Render AI Chat Modal
  const renderAIChatModal = () => {
    const formatMessageContent = (text) => {
      if (!text) return { __html: '' };
      
      // Debug logging
      if (process.env.NODE_ENV === 'development' && text.includes('Generated Image')) {
        console.log('formatMessageContent received text with image marker:', text.substring(0, 500));
      }
      
      // Detect and extract images from the content
      const images = [];
      const files = []; // Track file references
      const seenFileRefs = new Set(); // Track seen file references to prevent duplicates
      let processedText = text;
      
      // Extract markdown images ![alt](url)
      processedText = processedText.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
        images.push({ url, alt: alt || 'Image' });
        return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
      });
      
      // Extract plain image URLs (http/https URLs ending in image extensions)
      processedText = processedText.replace(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s]*)?)/gi, (match, url) => {
        // Check if this URL wasn't already captured as markdown image
        if (!images.find(img => img.url === url)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Found plain image URL in text:', url);
          }
          images.push({ url, alt: 'Image' });
          return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
        }
        return match;
      });
      
      // Also check for image URLs without file extensions (common in CDN URLs)
      processedText = processedText.replace(/(https?:\/\/[^\s]+(?:cdn|storage|images?|media|img|assets)[^\s]*)/gi, (match, url) => {
        // Only if it looks like an image URL and wasn't already captured
        if (!images.find(img => img.url === url) && 
            (url.includes('image') || url.includes('img') || url.includes('photo') || url.match(/\/[a-f0-9]{32,}/i))) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Found potential image URL (no extension):', url);
          }
          images.push({ url, alt: 'Generated Image' });
          return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
        }
        return match;
      });
      
      // Extract base64 image data (data:image/...;base64,...)
      processedText = processedText.replace(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+)/g, (match, dataUrl) => {
        const cleanUrl = dataUrl.trim();
        if (!images.find(img => img.url === cleanUrl)) {
          images.push({ url: cleanUrl, alt: 'Generated Image' });
          return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
        }
        return match;
      });
      
      // Try to extract images from JSON responses (common in some AI models)
      try {
        // Look for JSON objects with image URLs or base64 data
        const jsonMatches = processedText.match(/\{[^{}]*"(?:url|image|src|data)"\s*:\s*"([^"]+)"[^{}]*\}/g);
        if (jsonMatches) {
          jsonMatches.forEach(jsonStr => {
            try {
              const parsed = JSON.parse(jsonStr);
              const imageUrl = parsed.url || parsed.image || parsed.src || parsed.data;
              if (imageUrl && (imageUrl.startsWith('http') || imageUrl.startsWith('data:image'))) {
                if (!images.find(img => img.url === imageUrl)) {
                  images.push({ url: imageUrl, alt: 'Generated Image' });
                  processedText = processedText.replace(jsonStr, `__IMAGE_PLACEHOLDER_${images.length - 1}__`);
                }
              }
            } catch (e) {
              // Not valid JSON, skip
            }
          });
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }
      
      // Extract file references - sandbox URLs, markdown file links, etc.
      // Pattern: sandbox:/filename.ext or [filename](sandbox:/filename.ext) or similar patterns
      processedText = processedText.replace(/sandbox:\/([^\s\)\]]+\.(pptx?|docx?|pdf|xlsx?|txt|csv|json|xml|zip|rar|tar|gz))/gi, (match, fileName) => {
        const normalizedRef = fileName.toLowerCase();
        if (!seenFileRefs.has(normalizedRef)) {
          seenFileRefs.add(normalizedRef);
          const fileExt = fileName.split('.').pop().toLowerCase();
          files.push({ 
            ref: match, 
            fileName: fileName,
            fileType: fileExt
          });
          return `__FILE_PLACEHOLDER_${files.length - 1}__`;
        }
        return match;
      });
      
      // Also detect markdown file links: [filename](sandbox:/path) or [filename](path)
      processedText = processedText.replace(/\[([^\]]+)\]\(sandbox:\/([^\)]+)\)/gi, (match, linkText, filePath) => {
        const normalizedRef = filePath.toLowerCase();
        if (!seenFileRefs.has(normalizedRef)) {
          seenFileRefs.add(normalizedRef);
          const fileExt = filePath.split('.').pop().toLowerCase();
          files.push({ 
            ref: `sandbox:/${filePath}`, 
            fileName: linkText || filePath,
            fileType: fileExt
          });
          return `__FILE_PLACEHOLDER_${files.length - 1}__`;
        }
        return match;
      });
      
      // Detect file references in text like "Download the file here: [filename.pptx](sandbox:/filename.pptx)"
      processedText = processedText.replace(/\[([^\]]+\.(pptx?|docx?|pdf|xlsx?|txt|csv|json|xml|zip|rar|tar|gz))\]\(sandbox:\/([^\)]+)\)/gi, (match, linkText, ext, filePath) => {
        const normalizedRef = filePath.toLowerCase();
        if (!seenFileRefs.has(normalizedRef)) {
          seenFileRefs.add(normalizedRef);
          files.push({ 
            ref: `sandbox:/${filePath}`, 
            fileName: linkText || filePath,
            fileType: ext.toLowerCase()
          });
          return `__FILE_PLACEHOLDER_${files.length - 1}__`;
        }
        return match;
      });
      
      // Simple formatting for display - convert markdown-like patterns to HTML
      let formatted = processedText
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
      
      // Replace image placeholders with actual img tags and download buttons
      images.forEach((img, idx) => {
        const escapedUrl = img.url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const escapedAlt = (img.alt || 'Image').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        // For data attributes, we need to HTML encode the URL properly
        // Base64 URLs can be very long, so we'll use a data attribute with proper encoding
        const dataUrlEncoded = encodeURIComponent(img.url);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Rendering image ${idx}:`, escapedUrl.substring(0, 100));
        }
        formatted = formatted.replace(
          `__IMAGE_PLACEHOLDER_${idx}__`,
          `<div class="chat-generated-image">
            <img src="${escapedUrl}" alt="${escapedAlt}" onerror="console.error('Image load error:', this.src); this.style.display='none'; this.nextElementSibling.style.display='block';" />
            <div style="display:none; color: #dc3545; font-size: 12px; padding: 8px;">Failed to load image</div>
            <button class="chat-download-image-button" data-image-url="${dataUrlEncoded}" data-image-alt="${escapedAlt}" type="button" onclick="event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation(); if(window.downloadGeneratedImage){const url=decodeURIComponent('${dataUrlEncoded}'); window.downloadGeneratedImage(url, '${escapedAlt}');} return false;">⬇️ Download</button>
          </div>`
        );
      });
      
      // Replace file placeholders with download buttons
      files.forEach((file, idx) => {
        const escapedFileName = file.fileName.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const escapedRef = file.ref.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const fileRefEncoded = encodeURIComponent(file.ref);
        const fileNameEncoded = encodeURIComponent(file.fileName);
        formatted = formatted.replace(
          `__FILE_PLACEHOLDER_${idx}__`,
          `<div class="chat-generated-file" style="margin: 10px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 24px;">📄</span>
              <div style="flex: 1;">
                <div style="font-weight: bold; color: #333;">${escapedFileName}</div>
                <div style="font-size: 12px; color: #666; margin-top: 2px;">${escapedRef}</div>
              </div>
              <button class="chat-download-file-button" data-file-ref="${fileRefEncoded}" data-file-name="${fileNameEncoded}" data-file-type="${file.fileType}" type="button" onclick="event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation(); if(window.downloadFile){const ref=decodeURIComponent('${fileRefEncoded}'); const name=decodeURIComponent('${fileNameEncoded}'); const type='${file.fileType}'; window.downloadFile(ref, type);} return false;" style="padding: 6px 12px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">⬇️ Download</button>
            </div>
          </div>`
        );
      });
      
      if (process.env.NODE_ENV === 'development' && images.length > 0) {
        console.log(`formatMessageContent found ${images.length} images`);
      }
      
      if (process.env.NODE_ENV === 'development' && files.length > 0) {
        console.log(`formatMessageContent found ${files.length} file references`);
      }
      
      return { __html: formatted, files };
    };
    
    // If minimized, show minimized bar
    if (isChatMinimized) {
      const currentConv = chatHistory.find(c => c.id === currentConversationId);
      return (
        <div className="ai-chat-modal-content minimized">
          <div className="ai-chat-minimized-bar" onClick={() => {
            setIsChatMinimized(false);
            localStorage.setItem('fiddyscript_chat_minimized', 'false');
          }}>
            <span className="ai-chat-minimized-title">
              {currentConv?.title || 'AI Chat Assistant'}
            </span>
            <button 
              className="ai-chat-minimized-restore"
              onClick={(e) => {
                e.stopPropagation();
                setIsChatMinimized(false);
                localStorage.setItem('fiddyscript_chat_minimized', 'false');
              }}
            >
              Restore
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="ai-chat-modal-content">
        {/* Chat History Sidebar */}
        {showHistoryPanel && (
          <div className="ai-chat-history-panel">
            <div className="ai-chat-history-header">
              <h4>Chat History</h4>
              <button 
                className="ai-chat-history-close"
                onClick={() => setShowHistoryPanel(false)}
              >
                ×
              </button>
            </div>
            <input
              type="text"
              className="ai-chat-history-search"
              placeholder="Search conversations..."
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
            />
            <button 
              className="ai-chat-history-new"
              onClick={handleNewChat}
            >
              + New Chat
            </button>
            <div className="ai-chat-history-list">
              {chatHistory
                .filter(conv => {
                  if (!historySearchQuery) return true;
                  const searchLower = historySearchQuery.toLowerCase();
                  return (conv.title || '').toLowerCase().includes(searchLower) ||
                         (conv.messages || []).some(msg => msg.content.toLowerCase().includes(searchLower));
                })
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map(conv => (
                  <div
                    key={conv.id}
                    className={`ai-chat-history-item ${currentConversationId === conv.id ? 'active' : ''}`}
                    onClick={() => handleSelectConversation(conv.id)}
                    onDoubleClick={() => {
                      setEditingConversationId(conv.id);
                      setEditingConversationTitle(conv.title || '');
                    }}
                  >
                    <div className="ai-chat-history-item-content">
                      <div className="ai-chat-history-item-title">
                        {conv.title || 'New Chat'}
                      </div>
                      <div className="ai-chat-history-item-meta">
                        {new Date(conv.timestamp).toLocaleDateString()} • {conv.messages?.length || 0} messages
                      </div>
                    </div>
                    <button
                      className="ai-chat-history-item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation(conv.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              {chatHistory.length === 0 && (
                <div className="ai-chat-history-empty">No conversations yet</div>
              )}
            </div>
          </div>
        )}
        
        <div className="ai-chat-modal-main-content">
        {/* Welcome Header Section */}
        <div className="ai-chat-welcome-header">
          <div className="ai-chat-welcome-content">
            {chatHistory.length > 0 && (
              <button 
                className="ai-chat-history-toggle-left"
                onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                title="Toggle History"
              >
                ☰
              </button>
            )}
            <div className="ai-chat-welcome-text">
              <p className="ai-chat-welcome-greeting">Welcome to Fiddy AI Chat</p>
              <h1 className="ai-chat-welcome-title">Ask me anything—I'm here to help!</h1>
            </div>
            <div className="ai-chat-header-actions">
              <button 
                className="ai-chat-minimize-btn" 
                onClick={() => {
                  setIsChatMinimized(true);
                  localStorage.setItem('fiddyscript_chat_minimized', 'true');
                }}
                title="Minimize (ESC)"
              >
                −
              </button>
              <button className="modal-close" onClick={() => setShowChatModal(false)} title="Close">×</button>
            </div>
            <div className="ai-chat-new-chat-wrapper">
              <button className="ai-chat-new-btn-top" onClick={handleNewChat} title="New Chat">
                + New Chat
              </button>
            </div>
          </div>
        </div>
        
        <div className="ai-chat-messages" ref={chatMessagesRef}>
          {chatMessages.length === 0 ? null : (
            chatMessages.map((msg, idx) => {
              const isEditing = editingMessageIndex === idx;
              const plainContent = msg.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
              
              return (
              <div key={idx} className={`chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                <div className="chat-message-avatar">
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className="chat-message-content-wrapper">
                    {isEditing && msg.role === 'user' ? (
                      <div className="chat-edit-container">
                        <textarea
                          className="chat-edit-input"
                          value={editingMessageContent}
                          onChange={(e) => setEditingMessageContent(e.target.value)}
                          rows={3}
                          autoFocus
                        />
                        <div className="chat-edit-actions">
                          <button 
                            className="chat-edit-save-btn"
                            onClick={saveEditedMessage}
                            title="Save and regenerate response"
                          >
                            ✓ Save
                          </button>
                          <button 
                            className="chat-edit-cancel-btn"
                            onClick={cancelEditing}
                            title="Cancel editing"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Display attachments if present */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="chat-message-attachments">
                            {msg.attachments.map((att, attIdx) => (
                              <div key={attIdx} className="chat-attachment-preview">
                                {att.type === 'image' ? (
                                  <div className="chat-attachment-image">
                                    <img 
                                      src={att.data.startsWith('data:') ? att.data : `data:${att.mimeType || 'image/jpeg'};base64,${att.data}`}
                                      alt={att.name}
                                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                                    />
                                    <span className="chat-attachment-name">{att.name}</span>
                                  </div>
                                ) : (
                                  <div className="chat-attachment-file">
                                    <span className="chat-attachment-icon">📄</span>
                                    <span className="chat-attachment-name">{att.name}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                  <div 
                    className="chat-message-content"
                    dangerouslySetInnerHTML={formatMessageContent(msg.content)}
                  />
                        <div className="chat-message-actions">
                          {msg.role === 'user' ? (
                            <>
                              <button 
                                className="chat-copy-button"
                                onClick={(e) => copyMessage(msg.content, e.target)}
                                title="Copy message"
                              >
                                📋 Copy
                              </button>
                              <button 
                                className="chat-edit-button"
                                onClick={() => startEditingMessage(idx, msg.content)}
                                title="Edit message"
                              >
                                ✏️ Edit
                              </button>
                            </>
                          ) : (
                            <>
                              {!msg.isError && (
                                <button 
                                  className="chat-copy-button"
                                  onClick={(e) => copyMessage(msg.content, e.target)}
                                  title="Copy message"
                                >
                                  📋 Copy
                                </button>
                              )}
                              {!msg.isError && (
                    <button 
                      className="chat-add-button"
                      onClick={() => addToScriptEditor(msg.content)}
                      title="Add to Content Editor"
                    >
                      ➕ Add to Editor
                    </button>
                              )}
                            </>
                  )}
                </div>
                      </>
                    )}
              </div>
                </div>
              );
            })
          )}
          {isChatGenerating && (
            <div className="chat-message assistant">
              <div className="chat-message-avatar">🤖</div>
              <div className="chat-message-content-wrapper">
                <div className="chat-message-loading">
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* File Previews */}
        {selectedFiles.length > 0 && (
          <div className="chat-file-previews">
            {selectedFiles.map((file, index) => (
              <div key={index} className="chat-file-preview">
                {file.type === 'image' ? (
                  <div className="chat-file-preview-image">🖼️</div>
                ) : (
                  <div className="chat-file-preview-file">📄</div>
                )}
                <span className="chat-file-preview-name">{file.name}</span>
                <button
                  className="chat-file-preview-remove"
                  onClick={() => removeFile(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Chat Input Section */}
        <div className="ai-chat-input-section">
          <div className="ai-chat-input-wrapper">
            <input
              type="file"
              id="chat-file-input"
              multiple
              accept="image/*,.pdf,.txt,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <input
              type="file"
              id="chat-image-input"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <textarea
              ref={chatInputRef}
              className="ai-chat-input-large"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyPress}
              placeholder="Whatever you need, just ask Fiddy AI"
              rows={4}
              disabled={isChatGenerating}
            />
            <div className="ai-chat-input-footer">
              <div className="ai-chat-char-counter">
                {chatInput.length}/10,000
              </div>
              <div className="ai-chat-action-buttons">
                {availableChatModels && availableChatModels.length > 0 && (
                  <div className="ai-chat-model-selector-action-wrapper">
                    <SearchableSelect
                      id="ai-chat-model-selector-action"
                      value={selectedChatModel}
                      onChange={setSelectedChatModel}
                      options={availableChatModels.map(model => {
                        const isUnavailable = model.isAvailable === false;
                        return {
                          id: model.id,
                          value: model.id,
                          label: `${model.name} ${model.isFree ? '(FREE)' : '(PAID)'} ${isUnavailable ? '[Unavailable]' : ''}`,
                          disabled: isUnavailable,
                        };
                      })}
                      placeholder="Select model..."
                      className="ai-chat-model-selector-action"
                    />
                  </div>
                )}
                <label htmlFor="chat-file-input" className="ai-chat-action-btn" title="Attach File">
                  📎 Attach File
                </label>
                <label htmlFor="chat-image-input" className="ai-chat-action-btn" title="Upload Image">
                  🖼️ Upload Image
                </label>
                <button 
                  className="ai-chat-action-btn" 
                  title="Voice Message"
                  onClick={() => alert('Voice message feature coming soon!')}
                >
                  🎤 Voice Message
                </button>
              </div>
              <button
                className="ai-chat-send-button-large"
                onClick={sendChatMessage}
                disabled={(!chatInput.trim() && selectedFiles.length === 0) || isChatGenerating}
                title="Send message"
              >
                {isChatGenerating ? '⏳' : '✈️'}
              </button>
            </div>
          </div>
          
          {/* Explore by ready prompt section - only show when no messages */}
          {chatMessages.length === 0 && (
            <div className="ai-chat-explore-section">
              <h3 className="ai-chat-explore-title">Explore by ready prompt</h3>
              <div className="ai-chat-prompt-cards">
                <div 
                  className="ai-chat-prompt-card"
                  onClick={() => {
                    setChatInput('Help me write and improve my content. I need assistance with: ');
                    chatInputRef.current?.focus();
                  }}
                >
                  <div className="ai-chat-prompt-icon">✏️</div>
                  <h4 className="ai-chat-prompt-card-title">Writing</h4>
                  <p className="ai-chat-prompt-card-desc">Elevate your writing with tools designed for seamless creation, and sophisticated style refinement.</p>
                </div>
                <div 
                  className="ai-chat-prompt-card"
                  onClick={() => {
                    setChatInput('Help me research and analyze information about: ');
                    chatInputRef.current?.focus();
                  }}
                >
                  <div className="ai-chat-prompt-icon">💡</div>
                  <h4 className="ai-chat-prompt-card-title">Research & Analysis</h4>
                  <p className="ai-chat-prompt-card-desc">Discover, analyze, interpret, and present information with clarity and impact.</p>
                </div>
                <div 
                  className="ai-chat-prompt-card"
                  onClick={() => {
                    setChatInput('Help me with programming. I need assistance with: ');
                    chatInputRef.current?.focus();
                  }}
                >
                  <div className="ai-chat-prompt-icon">💻</div>
                  <h4 className="ai-chat-prompt-card-title">Programming</h4>
                  <p className="ai-chat-prompt-card-desc">Develop robust code, debug effectively, test thoroughly, and expand your expertise.</p>
                </div>
                <div 
                  className="ai-chat-prompt-card"
                  onClick={() => {
                    setChatInput('Help me learn new skills and explore ideas about: ');
                    chatInputRef.current?.focus();
                  }}
                >
                  <div className="ai-chat-prompt-icon">🎓</div>
                  <h4 className="ai-chat-prompt-card-title">Learning Skills</h4>
                  <p className="ai-chat-prompt-card-desc">Embark on a journey of innovation, exploring new ideas and evolving your existing skills.</p>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
        
        {/* Rename Modal */}
        {editingConversationId && (
          <div className="rename-modal-overlay" onClick={() => {
            setEditingConversationId(null);
            setEditingConversationTitle('');
          }}>
            <div className="rename-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Rename Conversation</h3>
              <input
                type="text"
                className="rename-modal-input"
                value={editingConversationTitle}
                onChange={(e) => setEditingConversationTitle(e.target.value)}
                placeholder="Enter conversation title"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editingConversationTitle.trim()) {
                    handleRenameConversation(editingConversationId, editingConversationTitle);
                  } else if (e.key === 'Escape') {
                    setEditingConversationId(null);
                    setEditingConversationTitle('');
                  }
                }}
              />
              <div className="rename-modal-actions">
                <button
                  className="rename-modal-cancel"
                  onClick={() => {
                    setEditingConversationId(null);
                    setEditingConversationTitle('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="rename-modal-save"
                  onClick={() => {
                    if (editingConversationTitle.trim()) {
                      handleRenameConversation(editingConversationId, editingConversationTitle);
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSidebarTool = (tool) => {
    switch (tool) {
      case 'imageLibrary':
        openModal('Image Library', renderImageLibraryModal());
        break;
      case 'editImage':
        console.log('Edit Image clicked, selectedEditorImage:', selectedEditorImage);
        if (selectedEditorImage) {
          console.log('Opening Edit Image modal...');
          openModal('Edit Image', renderImageEditorModal());
        } else {
          alert('Please click on an image in the editor first to select it for editing.');
        }
        break;
      case 'insertImage':
        openModal('Insert Image', renderInsertImageModal());
        break;
      case 'deleteToTop':
        deleteToTop();
        break;
      case 'deleteToBottom':
        deleteToBottom();
        break;
      case 'removeLinks':
        removeLinks();
        break;
      case 'scrapeURL':
        openModal('Scrape URL', renderScrapeURLModal());
        break;
      case 'fileToText':
        openModal('File to Text', renderFileToTextModal());
        break;
      case 'youtubeToText':
        openModal('YouTube to Text', renderYouTubeToTextModal());
        break;
      case 'exportPDF':
        handlePDFExport();
        break;
      case 'toWordPress':
        console.log('Opening WordPress modal...');
        openModal('Post to WordPress', renderWordPressPostModal());
        break;
      case 'humanize':
        openModal('Humanize', null);
        break;
      default:
        console.log('Tool clicked:', tool);
        break;
    }
  };

  return (
    <div className="script-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="editor-title">Content Editor</div>
        <div className="window-controls">
          <button className="window-control minimize">─</button>
          <button className="window-control maximize">□</button>
          <button className="window-control close">×</button>
        </div>
      </div>

      <div className="editor-main">
        {/* Left Sidebar */}
        <div className="editor-sidebar">
          <div className="sidebar-section">
            <button className="sidebar-btn ai-prompts" onClick={handleAIPromptsClick}>
              <span className="icon">🧠</span>
              Fiddy AI Chat
            </button>
          </div>

          <div className="sidebar-section">
            <button className="sidebar-btn" onClick={() => handleSidebarTool('imageLibrary')}>
              <span className="icon">🖼️</span>
              Image Library
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('editImage')}>
              <span className="icon">✏️</span>
              Edit Image
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('insertImage')}>
              <span className="icon">📁</span>
              Insert Image
            </button>
          </div>

          <div className="sidebar-section">
            <button className="sidebar-btn" onClick={() => handleSidebarTool('deleteToTop')}>
              <span className="icon">🗑️↑</span>
              Delete to Top
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('deleteToBottom')}>
              <span className="icon">🗑️↓</span>
              Delete to Bottom
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('removeLinks')}>
              <span className="icon">🔗❌</span>
              Remove Links
            </button>

          </div>

          <div className="sidebar-section">
            <button className="sidebar-btn" onClick={() => handleSidebarTool('scrapeURL')}>
              <span className="icon">🌐</span>
              Scrape URL
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('fileToText')}>
              <span className="icon">📄</span>
              File to Text
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('youtubeToText')}>
              <span className="icon">▶️</span>
              YouTube to Text
            </button>
          </div>

          <div className="sidebar-section">
            <button className="sidebar-btn" onClick={() => handleSidebarTool('exportPDF')}>
              <span className="icon">📄</span>
              Export to PDF
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('toWordPress')}>
              <span className="icon">W</span>
              To WordPress
            </button>
            <button className="sidebar-btn" onClick={() => handleSidebarTool('humanize')}>
              <span className="icon">👤</span>
              Humanize
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="editor-content">













          {/* Toolbar */}
          <div className="toolbar">
            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={() => formatText('undo')} title="Undo (Ctrl+Z)">↶</button>
              <button className="toolbar-btn" onClick={() => formatText('redo')} title="Redo (Ctrl+Y)">↷</button>
              <button className="toolbar-btn" onClick={handlePasteFromToolbar} title="Paste (Ctrl+V)">📋</button>
            </div>

            <div className="toolbar-group">
              <select 
                className="toolbar-select" 
                onChange={(e) => {
                  if (e.target.value === 'p') {
                    formatText('formatBlock', '<p>');
                  } else {
                    formatText('formatBlock', `<${e.target.value}>`);
                  }
                }}
              >
                <option value="p">Paragraph</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="h4">Heading 4</option>
                <option value="h5">Heading 5</option>
                <option value="h6">Heading 6</option>
                <option value="blockquote">Quote</option>
                <option value="pre">Code Block</option>
              </select>
              <select 
                className="toolbar-select"
                value={formatState.fontName}
                onChange={(e) => formatText('fontName', e.target.value)}
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Courier New">Courier New</option>
                <option value="Tahoma">Tahoma</option>
                <option value="Trebuchet MS">Trebuchet MS</option>
                <option value="Impact">Impact</option>
                <option value="Comic Sans MS">Comic Sans MS</option>
              </select>
              <select 
                className="toolbar-select"
                value={formatState.fontSize}
                onChange={(e) => formatText('fontSize', e.target.value)}
              >
                <option value="1">8pt</option>
                <option value="2">10pt</option>
                <option value="3">12pt</option>
                <option value="4">14pt</option>
                <option value="5">16pt</option>
                <option value="6">18pt</option>
                <option value="7">20pt</option>
                <option value="8">24pt</option>
                <option value="9">28pt</option>
                <option value="10">32pt</option>
                <option value="11">36pt</option>
                <option value="12">48pt</option>
                <option value="13">60pt</option>
                <option value="14">72pt</option>
              </select>
            </div>

            <div className="toolbar-group">
              <button 
                className={`toolbar-btn ${formatState.bold ? 'active' : ''}`} 
                onClick={() => formatText('bold')} 
                title="Bold (Ctrl+B)"
              >
                B
              </button>
              <button 
                className={`toolbar-btn ${formatState.italic ? 'active' : ''}`} 
                onClick={() => formatText('italic')} 
                title="Italic (Ctrl+I)"
              >
                I
              </button>
              <button 
                className={`toolbar-btn ${formatState.underline ? 'active' : ''}`} 
                onClick={() => formatText('underline')} 
                title="Underline (Ctrl+U)"
              >
                U
              </button>
              <button 
                className={`toolbar-btn ${formatState.strikethrough ? 'active' : ''}`} 
                onClick={() => formatText('strikeThrough')} 
                title="Strikethrough"
              >
                S
              </button>
              <button className="toolbar-btn" onClick={() => formatText('superscript')} title="Superscript">X²</button>
              <button className="toolbar-btn" onClick={() => formatText('subscript')} title="Subscript">X₂</button>
              <button className="toolbar-btn" onClick={() => wrapSelection('code')} title="Code">&lt;&gt;</button>
              <button className="toolbar-btn" onClick={() => wrapSelection('mark')} title="Highlight">H</button>
            </div>

            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={() => insertContent('link')}>🔗</button>
              <button className="toolbar-btn" onClick={() => insertContent('image')}>🖼️</button>
              <button className="toolbar-btn" onClick={() => insertContent('video')}>▶️</button>
              <button className="toolbar-btn" onClick={() => insertContent('table')}>⊞</button>
            </div>

            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={() => formatText('justifyLeft')}>⫷</button>
              <button className="toolbar-btn" onClick={() => formatText('justifyCenter')}>⫸</button>
              <button className="toolbar-btn" onClick={() => formatText('justifyRight')}>⫹</button>
              <button className="toolbar-btn" onClick={() => formatText('justifyFull')}>⫺</button>
            </div>

            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={() => formatText('insertUnorderedList')} title="Bullet List">•</button>
              <button className="toolbar-btn" onClick={() => formatText('insertOrderedList')} title="Numbered List">1.</button>
              <button className="toolbar-btn" onClick={() => formatText('outdent')} title="Decrease Indent">←</button>
              <button className="toolbar-btn" onClick={() => formatText('indent')} title="Increase Indent">→</button>
            </div>

            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={() => wrapSelection('h1')} title="Heading 1">H1</button>
              <button className="toolbar-btn" onClick={() => wrapSelection('h2')} title="Heading 2">H2</button>
              <button className="toolbar-btn" onClick={() => wrapSelection('h3')} title="Heading 3">H3</button>
              <button className="toolbar-btn" onClick={() => wrapSelection('blockquote')} title="Quote">"</button>
            </div>

            <div className="toolbar-group">
              <input 
                type="color" 
                className="toolbar-color-picker"
                onChange={(e) => formatText('foreColor', e.target.value)}
                title="Text Color"
              />
              <input 
                type="color" 
                className="toolbar-color-picker"
                onChange={(e) => formatText('hiliteColor', e.target.value)}
                title="Background Color"
              />
            </div>

            <div className="toolbar-group">
              <button className="toolbar-btn" onClick={toggleContentMode} title={`Switch to ${contentMode === 'html' ? 'Text' : 'HTML'} Mode`}>
                {contentMode === 'html' ? 'TXT' : 'HTML'}
              </button>
              <button className="toolbar-btn" onClick={() => setShowSourceCode(!showSourceCode)} title="Toggle Source Code View">
                &lt;&gt;
              </button>
              <button className="toolbar-btn" onClick={() => openModal('Insert Emoji', null)} title="Insert Emoji">😊</button>
              <button className="toolbar-btn" onClick={() => openModal('Insert Special Character', null)} title="Insert Special Character">Ω</button>
            </div>
          </div>

          {/* Editor Area */}
          <div className="editor-area">
            <div className="content-mode-indicator">
              {showSourceCode ? 'SOURCE' : contentMode === 'html' ? 'HTML' : 'TEXT'}
            </div>
            {showSourceCode ? (
              <textarea
                ref={editorRef}
                className="source-editor"
                value={content}
                onChange={handleContentChange}
                placeholder="Enter your content here..."
              />
            ) : (
              <div
                ref={editorRef}
                className="rich-editor"
                contentEditable
                onInput={handleRichEditorChange}
                onSelect={handleEditorSelect}
                onKeyUp={handleKeyUp}
                onMouseUp={handleEditorSelect}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onContextMenu={handleContextMenu}
                onBlur={handleEditorBlur}
                placeholder="Enter your content here..."
              />
            )}
          </div>

          {/* Status Bar */}
          <div className="status-bar">
            <div className="status-left">
              <button className="status-btn" onClick={handleNewDocument}>File</button>
              <button className="status-btn" onClick={handleSave}>Save</button>
              <label className="autosave-checkbox">
                <input
                  type="checkbox"
                  checked={autosave}
                  onChange={(e) => setAutosave(e.target.checked)}
                />
                Autosave
              </label>
            </div>
            <div className="status-right">
              <span className="word-count">{wordCount} words</span>
              <button className="status-btn clear-all" onClick={handleClearAll}>Clear All</button>
              <button className="status-btn close" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenuPosition.y,
            left: contextMenuPosition.x,
            zIndex: 10001
          }}
        >
          <div className="context-menu-item" onClick={() => { formatText('cut'); setShowContextMenu(false); }}>
            <span className="icon">✂️</span> Cut
          </div>
          <div className="context-menu-item" onClick={() => { formatText('copy'); setShowContextMenu(false); }}>
            <span className="icon">📋</span> Copy
          </div>
          <div className="context-menu-item" onClick={() => { handlePasteFromToolbar(); setShowContextMenu(false); }}>
            <span className="icon">📋</span> Paste
          </div>
          <div className="context-menu-item" onClick={() => { formatText('selectAll'); setShowContextMenu(false); }}>
            <span className="icon">📄</span> Select All
          </div>
        </div>
      )}

      {/* Modal System */}
      {showModal && modalTitle !== 'Post to WordPress' && modalTitle !== 'Insert Image' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {modalTitle === 'Insert Link' && renderInsertLinkModal()}
            {modalTitle === 'Insert Video' && renderInsertVideoModal()}
            {modalTitle === 'Insert Table' && renderInsertTableModal()}
            {modalTitle === 'Insert Emoji' && renderInsertEmojiModal()}
            {modalTitle === 'Insert Special Character' && renderInsertSpecialCharModal()}
            {modalTitle === 'Image Library' && renderImageLibraryModal()}
            {modalTitle === 'Scrape URL' && renderScrapeURLModal()}
            {modalTitle === 'File to Text' && renderFileToTextModal()}
            {modalTitle === 'YouTube to Text' && renderYouTubeToTextModal()}
            {modalTitle === 'Image Editor' && renderImageEditorModal()}
            {modalTitle === 'Humanize' && renderHumanizerModal()}
            {modalTitle !== 'Insert Link' && 
             modalTitle !== 'Insert Video' && 
             modalTitle !== 'Insert Table' && 
             modalTitle !== 'Insert Emoji' && 
             modalTitle !== 'Insert Special Character' && 
             modalTitle !== 'Image Library' && 
             modalTitle !== 'Scrape URL' && 
             modalTitle !== 'File to Text' && 
             modalTitle !== 'YouTube to Text' && 
             modalTitle !== 'Image Editor' && 
             modalContent}
            </div>
            </div>
      )}
      
      {/* Insert Image Modal - Direct Rendering */}
      {showModal && modalTitle === 'Insert Image' && (
        <div className="modal-overlay" onClick={closeModal}>
          {renderInsertImageModal()}
        </div>
      )}
      
      {/* WordPress Modal - Direct Rendering */}
      {showModal && modalTitle === 'Post to WordPress' && (
        <div className="wordpress-modal-overlay" onClick={closeModal}>
          <div onClick={(e) => e.stopPropagation()}>
          {renderWordPressPostModal()}
          </div>
        </div>
      )}

      {/* New Category Modal */}
      {showNewCategoryModal && (
        <div className="wordpress-modal-overlay" onClick={() => setShowNewCategoryModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            {renderNewCategoryModal()}
          </div>
        </div>
      )}

      {/* New Tag Modal */}
      {showNewTagModal && (
        <div className="wordpress-modal-overlay" onClick={() => setShowNewTagModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            {renderNewTagModal()}
          </div>
        </div>
      )}
      
      {/* AI Chat Modal */}
      {showChatModal && (
        <div className="ai-chat-modal-overlay" onClick={() => setShowChatModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            {renderAIChatModal()}
          </div>
        </div>
      )}
    </div>
  );
});

export default ScriptEditor; 
